<p-toast position="top-right"></p-toast>

<div class="settings-page">

  <!-- Tabs are rendered by the global submenu panel; this page now only shows the active tab's content. -->

  <!-- ── Content area ────────────────────────────────── -->
  <div class="settings-content">

    <!-- Back to the hub from any opened setting (admin-only — the component hides itself otherwise). -->
    <app-settings-backlink *ngIf="activeTab !== 'hub'"></app-settings-backlink>

    <!-- ══════════════ SETTINGS HUB (landing page) ══════════════ -->
    <!-- Rendered when /settings is opened WITHOUT a ?tab= — categorised cards, one per
         setting, driven by SETTINGS_CATALOG. Admin-only (non-admins are hard-routed to
         ?tab=profile before this can show). -->
    <div *ngIf="activeTab === 'hub'" class="settings-panel fadein shub">
      <div class="shub__header">
        <div class="shub__badge"><i class="pi pi-cog"></i></div>
        <div>
          <h1 class="shub__title">Settings</h1>
          <p class="shub__subtitle">Configure your CRM to match your workflow</p>
        </div>
      </div>

      <div class="shub__search">
        <i class="pi pi-search"></i>
        <input type="text" [(ngModel)]="hubSearch" [ngModelOptions]="{ standalone: true }"
               placeholder="Search settings and modules..." />
        <button type="button" class="shub__search-clear" *ngIf="hubSearch" (click)="hubSearch = ''">
          <i class="pi pi-times"></i>
        </button>
      </div>

      <div class="shub__section" *ngFor="let cat of hubCatalog">
        <div class="shub__section-label">
          <i [class]="cat.icon"></i><span>{{ cat.label }}</span>
        </div>
        <div class="shub__grid">
          <button type="button" class="shub__card" *ngFor="let card of cat.cards" (click)="openHubCard(card)">
            <span class="shub__card-icon"><i [class]="card.icon"></i></span>
            <span class="shub__card-text">
              <span class="shub__card-title">{{ card.title }}</span>
              <span class="shub__card-desc">{{ card.description }}</span>
            </span>
            <i class="pi pi-angle-right shub__card-chev"></i>
          </button>
        </div>
      </div>

      <div class="shub__empty" *ngIf="!hubCatalog.length">
        <i class="pi pi-search"></i>
        <p>No settings match “{{ hubSearch }}”</p>
      </div>
    </div>

    <!-- ══════════════ COMPANY PROFILE ══════════════ -->
    <!-- Admin only — non-admins are hard-routed to ?tab=profile (My profile). The company's
         brand + configuration: the logo uploaded here shows top-left for EVERY user. -->
    <div *ngIf="activeTab === 'company'" class="settings-panel fadein">
      <div class="panel-header">
        <div>
          <h2 class="panel-title">Company profile</h2>
          <p class="panel-sub">Your company's brand and configuration, shared by everyone in the workspace.</p>
        </div>
      </div>

      <div *ngIf="loadingCompany" class="settings-loading">
        <p-progressSpinner strokeWidth="4" styleClass="settings-spinner"></p-progressSpinner>
        <span>Loading company profile...</span>
      </div>

      <div class="settings-card" *ngIf="!loadingCompany">

        <!-- Brand hero — company logo (click the badge to upload) + name + plan/status -->
        <div class="cp-hero">
          <div class="cp-logo-wrap">
            <div class="cp-logo">
              <img *ngIf="companyLogoSrc" [src]="companyLogoSrc" alt="Company logo" />
              <span *ngIf="!companyLogoSrc" class="cp-logo__initials">{{ getCompanyInitials() }}</span>
            </div>
            <label class="cp-logo-btn" pTooltip="Upload a new logo" tooltipPosition="bottom">
              <i class="pi" [ngClass]="uploadingLogo ? 'pi-spin pi-spinner' : 'pi-camera'"></i>
              <input type="file" accept="image/*" (change)="onCompanyLogo($event)" [disabled]="uploadingLogo" hidden />
            </label>
          </div>
          <div class="cp-hero__main">
            <div class="cp-hero__kicker">Company</div>
            <div class="cp-hero__name">{{ companyProfile?.name || 'Company' }}</div>
            <div class="cp-hero__hint">
              <i class="pi pi-image"></i>
              <span>This logo appears in the top-left corner for every user in your company.</span>
              <a *ngIf="companyLogoSrc" class="cp-hero__remove" (click)="removeCompanyLogo()">Remove logo</a>
            </div>
          </div>
          <div class="cp-hero__side">
            <div class="profile-tags">
              <p-tag [value]="subscriptionProfile?.plan || 'N/A'" severity="info"></p-tag>
              <p-tag [value]="(companyProfile?.status || subscriptionProfile?.status) || 'N/A'" severity="success"></p-tag>
            </div>
            <button *ngIf="isFreePlan" pButton label="Upgrade Plan" icon="pi pi-crown"
              class="p-button-warning p-button-sm" (click)="openUpgradeDialog()"></button>
          </div>
        </div>

        <!-- Company configuration — same form styling as the settings modals -->
        <div class="cp-section">
          <div class="cp-section__head">
            <div class="cp-section__title">Company configuration</div>
            <div class="cp-section__sub">These settings apply to every member of the workspace.</div>
          </div>
          <div class="cp-form">
            <div class="invite-field">
              <label class="invite-label">Company name</label>
              <div class="invite-input-row">
                <i class="pi pi-building"></i>
                <input pInputText [(ngModel)]="companyNameDraft" placeholder="Your company name" />
              </div>
              <small class="cp-help">Shown across the app, on quotations and in customer messages.</small>
            </div>
            <div class="invite-field">
              <label class="invite-label">Display timezone</label>
              <p-select appendTo="body" [options]="timezoneOptions" [(ngModel)]="timezoneDraft"
                        optionLabel="label" optionValue="value" [filter]="true"
                        styleClass="w-full"></p-select>
              <small class="cp-help">All dates &amp; times (web + mobile) show in this timezone. Stored data stays in UTC.</small>
            </div>
            <div class="invite-field">
              <label class="invite-label">Default country</label>
              <p-select appendTo="body" [options]="countryOptions" [(ngModel)]="countryDraft"
                        optionLabel="label" optionValue="value" [filter]="true"
                        styleClass="w-full"></p-select>
              <small class="cp-help">Phone numbers entered without a country code are treated as this country (e.g. a bare 10-digit number becomes +91…). Numbers that already include a country code (+44…, +1…) are kept as-is.</small>
            </div>
          </div>
          <div class="cp-actions">
            <button pButton label="Save changes" icon="pi pi-check"
                    [loading]="savingCompany" (click)="saveCompanyConfig()"></button>
          </div>

          <!-- Phone-format cleanup: re-normalize existing lead numbers to E.164 for the default country. -->
          <div class="cp-phonefix">
            <div class="cp-phonefix__head">
              <i class="pi pi-phone"></i>
              <div>
                <div class="cp-phonefix__title">Fix existing phone formats</div>
                <div class="cp-phonefix__sub">Re-normalizes stored lead numbers using the default country above. Preview first — a number that was already truncated can't recover its original country, so it takes the default.</div>
              </div>
            </div>
            <div class="cp-phonefix__actions">
              <button pButton label="Preview" icon="pi pi-search" class="p-button-outlined p-button-sm"
                      [loading]="phoneFixBusy" (click)="previewPhoneFix()"></button>
              <button pButton [label]="'Apply' + (phoneFixPreview?.changed ? ' (' + phoneFixPreview?.changed + ')' : '')"
                      icon="pi pi-check" class="p-button-sm" [loading]="phoneFixBusy"
                      [disabled]="phoneFixBusy || !phoneFixPreview?.changed" (click)="applyPhoneFix()"></button>
            </div>
            <div class="cp-phonefix__preview" *ngIf="phoneFixPreview">
              <div class="cp-phonefix__count">
                <b>{{ phoneFixPreview.changed }}</b> of {{ phoneFixPreview.scanned }} numbers will change.
              </div>
              <table class="cp-phonefix__tbl gh-table" *ngIf="phoneFixPreview.samples?.length">
                <tr><th>Stored now</th><th></th><th>After</th></tr>
                <tr *ngFor="let s of phoneFixPreview.samples">
                  <td>{{ s.old }}</td><td><i class="pi pi-arrow-right"></i></td><td>{{ s.new }}</td>
                </tr>
              </table>
            </div>
          </div>
        </div>

        <!-- Workspace summary — read-only account + subscription facts -->
        <div class="cp-section">
          <div class="cp-section__head">
            <div class="cp-section__title">Workspace</div>
            <div class="cp-section__sub">A read-only summary of your account and subscription.</div>
          </div>
          <div class="cp-facts">
            <div class="cp-fact"><div class="cp-fact__label"><i class="pi pi-check-circle"></i>Status</div><div class="cp-fact__value">{{ companyProfile?.status || '—' }}</div></div>
            <div class="cp-fact"><div class="cp-fact__label"><i class="pi pi-calendar"></i>Created</div><div class="cp-fact__value">{{ companyProfile?.createdAt ? (companyProfile?.createdAt | appDate:'date') : '—' }}</div></div>
            <div class="cp-fact"><div class="cp-fact__label"><i class="pi pi-id-card"></i>Plan</div><div class="cp-fact__value">{{ subscriptionProfile?.plan || '—' }}</div></div>
            <div class="cp-fact"><div class="cp-fact__label"><i class="pi pi-bolt"></i>Plan status</div><div class="cp-fact__value">{{ subscriptionProfile?.status || '—' }}</div></div>
            <div class="cp-fact"><div class="cp-fact__label"><i class="pi pi-refresh"></i>Renewal</div><div class="cp-fact__value">{{ subscriptionProfile?.renewalDate ? (subscriptionProfile?.renewalDate | appDate:'date') : '—' }}</div></div>
            <div class="cp-fact"><div class="cp-fact__label"><i class="pi pi-clock"></i>Trial</div><div class="cp-fact__value">{{ subscriptionProfile?.trial ? 'Yes' : 'No' }}</div></div>
          </div>
          <div class="cp-signed">
            <i class="pi pi-user"></i>
            <span>Signed in as <strong>{{ myProfile?.name || '—' }}</strong><span *ngIf="myProfile?.email"> · {{ myProfile?.email }}</span> — your own photo and details live in <a (click)="goToMyProfile()">My profile</a>.</span>
          </div>
        </div>
      </div>
    </div>

    <!-- ══════════════ MY PROFILE ══════════════ -->
    <!-- Self-service account page (any role). For agents/managers this is the ONLY Settings
         tab; admins reach it alongside Company Profile. Reuses the standalone My Profile UI. -->
    <div *ngIf="activeTab === 'profile'" class="settings-panel fadein">
      <app-my-profile></app-my-profile>
    </div>

    <!-- ══════════════ WHATSAPP & CHATBOT ══════════════ -->
    <div *ngIf="activeTab === 'channels'" class="settings-panel fadein">
      <div class="panel-header">
        <div>
          <h2 class="panel-title">
            <ng-container *ngIf="channelTab === 'whatsapp'">WhatsApp Configuration</ng-container>
            <ng-container *ngIf="channelTab === 'chatbot'">Chatbot Configuration</ng-container>
          </h2>
          <p class="panel-sub">
            <ng-container *ngIf="channelTab === 'whatsapp'">Connect and manage your WhatsApp Business integration.</ng-container>
            <ng-container *ngIf="channelTab === 'chatbot'">Configure the chatbot widget and conversation rules.</ng-container>
          </p>
        </div>
      </div>

      <!-- WhatsApp sub-tab -->
      <ng-container *ngIf="channelTab === 'whatsapp'">
        <div *ngIf="loadingWhatsApp" class="settings-loading">
          <p-progressSpinner strokeWidth="4" styleClass="settings-spinner"></p-progressSpinner>
          <span>Loading WhatsApp integration...</span>
        </div>

        <div class="settings-card settings-card--compact mb-4 whatsapp-guide-card">
          <div>
            <div class="text-xs text-500 font-semibold uppercase tracking-widest">Meta Onboarding</div>
            <div class="text-900 font-semibold mt-1">Connect in 4 steps:</div>
            <ol class="integration-steps mt-2 mb-0">
              <li><strong>The client shares their WhatsApp Account with us.</strong>
                  In their Meta Business Settings → <em>Partners</em> → GrowHigh → share the
                  <em>WhatsApp Account</em>.</li>
              <li><strong>Assign it to our system user.</strong> Our Business Settings → Users →
                  System Users → Add Assets → WhatsApp Accounts. Sharing alone is not enough —
                  the token cannot act on the account until it is assigned.</li>
              <li><strong>Add the number below</strong> — Phone Number, Phone Number ID and WABA ID,
                  all from Meta's WhatsApp Manager.</li>
              <li><strong>Press Subscribe</strong> on the number. That points Meta's webhook at us and
                  inbound messages start arriving.</li>
            </ol>
          </div>
          <!-- The steps above are the real flow. This link goes to Meta's own Cloud API docs, which
               describe the direct (own-app) setup, so it is labelled for what it is rather than
               presented as "how to connect". -->
          <button pButton type="button" label="Meta Cloud API Docs" icon="pi pi-external-link"
            class="p-button-sm p-button-outlined card-header__action" (click)="openMetaGuide()"></button>
        </div>

        <div *ngIf="!metaConnected" class="settings-card settings-card--center">
          <div class="border-circle bg-green-50 inline-flex align-items-center justify-content-center mb-4" style="width:80px;height:80px">
            <i class="pi pi-whatsapp text-green-500 text-4xl"></i>
          </div>
          <h3 class="text-900 font-bold text-2xl mb-2">WhatsApp Business API</h3>
          <p class="text-600 mb-5 max-w-30rem mx-auto">Connect your Meta Business account to automate customer communication via WhatsApp.</p>
          <button pButton label="Add WhatsApp Account" icon="pi pi-plus" class="p-button-success p-button-lg px-5" (click)="showAccountDialog = true"></button>
        </div>

        <div *ngIf="metaConnected" class="settings-card settings-card--compact mb-4">
          <div class="flex align-items-center">
            <i class="pi pi-whatsapp text-green-500 text-3xl mr-3"></i>
            <div>
              <div class="text-sm font-bold text-500 uppercase">Linked Account</div>
              <div class="text-900 font-bold text-xl">{{ channelAccount?.accountName }}
                <span class="text-400 font-normal text-sm ml-2">{{ channelAccount?.externalAccountId }}</span>
              </div>
            </div>
          </div>
          <div class="flex align-items-center gap-2">
            <p-tag [severity]="getStatusSeverity(channelAccount?.status)" [value]="channelAccount?.status" styleClass="px-3"></p-tag>
            <button pButton type="button" icon="pi pi-pencil" class="p-button-text p-button-secondary"
                    pTooltip="Rename this account" tooltipPosition="top"
                    (click)="openTokenEditor()"></button>
            <!-- Removing the account cascades to every number mapped to it, so this opens a
                 confirm dialog that names them first — see openRemoveAccountDialog(). -->
            <button pButton type="button" icon="pi pi-trash" class="p-button-text p-button-danger"
                    pTooltip="Remove this account and its WhatsApp numbers from GrowHigh"
                    tooltipPosition="top" (click)="openRemoveAccountDialog()"></button>
          </div>
        </div>

        <div *ngIf="metaConnected" class="settings-card">
          <div class="card-header">
            <h5 class="m-0 font-bold text-900">Registered Phone Numbers</h5>
            <button pButton label="Add Number" icon="pi pi-plus" class="p-button-sm p-button-outlined card-header__action" (click)="editingConfig = null; numberForm = { senderIdentifier: '', displayName: '', isDefault: false, phoneNumber: '', channelType: 'CUSTOMER', wabaId: '', operationId: null }; showNumberDialog = true"></button>
          </div>
          <p-table [value]="channelConfigs" styleClass="p-datatable-sm saas-table">
            <ng-template pTemplate="header">
              <tr>
                <th>Number</th><th>Phone Number ID</th><th>WABA ID</th>
                <th>Label</th><th>Status</th><th>Quality</th><th class="text-center">Sync</th><th class="text-right">Actions</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td class="font-bold">{{ row.phoneNumber }}</td>
                <td>{{ row.senderIdentifier }}</td>
                <td>{{ row.wabaId || '—' }}</td>
                <td>{{ row.displayName }}</td>
                <td><p-tag [severity]="getStatusSeverity(row.status)" [value]="row.status"></p-tag></td>
                <td>
                  <p-tag *ngIf="(waNumberQuality[row.phoneNumber] || waNumberQuality[row.senderIdentifier]) as q"
                         [severity]="waQualitySeverity(q)" [value]="waQualityLabel(q)"
                         pTooltip="WhatsApp number quality, live from Meta" tooltipPosition="top"></p-tag>
                  <span *ngIf="!(waNumberQuality[row.phoneNumber] || waNumberQuality[row.senderIdentifier])" class="text-400">—</span>
                </td>
                <td class="text-center">
                  <i class="pi pi-check-circle text-green-500" *ngIf="row.status === 'VERIFIED'"></i>
                  <i class="pi pi-clock text-400" *ngIf="row.status !== 'VERIFIED'"></i>
                </td>
                <td class="text-right">
                  <button pButton icon="pi pi-link" class="p-button-text p-button-secondary" [loading]="row.subscribing"
                          pTooltip="Subscribe this number to receive WhatsApp messages" tooltipPosition="top"
                          (click)="subscribeNumber(row)"></button>
                  <button pButton icon="pi pi-pencil" class="p-button-text p-button-secondary" (click)="openEditNumber(row)"></button>
                  <button pButton icon="pi pi-refresh" class="p-button-text p-button-secondary" [loading]="row.testing" (click)="testSender(row)"></button>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>
      </ng-container>

      <!-- Chatbot sub-tab -->
      <ng-container *ngIf="channelTab === 'chatbot'">
        <div class="settings-card mb-4">
          <div class="card-header">
            <h5 class="m-0 font-bold text-900">Webhook Secret Key</h5>
            <button pButton type="button" icon="pi pi-key" label="Create Secret Key" class="p-button-sm"
              *ngIf="!widgetSecretKey" [loading]="creatingWidgetSecret" [disabled]="creatingWidgetSecret"
              (click)="createWebhookSecretKey()"></button>
          </div>
          <div class="secret-key-box">
            <label class="block font-medium mb-2">Generated Secret Key</label>
            <div class="secret-key-input-wrap">
              <input pInputText class="w-full" [type]="widgetSecretVisible ? 'text' : 'password'"
                [value]="widgetSecretKey || ''" readonly placeholder="No secret key created yet" />
              <button pButton type="button" class="p-button-text p-button-sm"
                [icon]="widgetSecretVisible ? 'pi pi-eye-slash' : 'pi pi-eye'"
                (click)="widgetSecretVisible = !widgetSecretVisible"
                [disabled]="!widgetSecretKey"></button>
            </div>
            <small class="text-500">Format: <code>IR-bot-XXXX-XXXX</code>.</small>
          </div>
        </div>

        <div class="settings-card mb-4">
          <div class="card-header">
            <h5 class="m-0 font-bold text-900">Chatbot Integration Code</h5>
          </div>
          <div class="integration-grid">
            <div class="integration-item">
              <div class="integration-label">Website Embed Script</div>
              <pre class="code-block">{{ websiteEmbedCode }}</pre>
              <button pButton type="button" icon="pi pi-copy" label="Copy Website Code" class="p-button-sm" (click)="copyToClipboard(websiteEmbedCode, 'Website code copied')"></button>
            </div>
            <div class="integration-item">
              <div class="integration-label">Mobile App (WebView)</div>
              <pre class="code-block">{{ mobileWebViewCode }}</pre>
              <button pButton type="button" icon="pi pi-copy" label="Copy Mobile Code" class="p-button-sm p-button-outlined" (click)="copyToClipboard(mobileWebViewCode, 'Mobile code copied')"></button>
            </div>
            <div class="integration-item">
              <div class="integration-label">Centered Webchat (Always Open)</div>
              <pre class="code-block">{{ centeredChatbotCode }}</pre>
              <button pButton type="button" icon="pi pi-copy" label="Copy Centered Code" class="p-button-sm p-button-outlined" (click)="copyToClipboard(centeredChatbotCode, 'Centered chatbot code copied')"></button>
            </div>
          </div>
        </div>

        <div class="settings-card">
          <div class="card-header">
            <h5 class="m-0 font-bold text-900">Customize Chatbot Styling</h5>
            <button pButton type="button" icon="pi pi-save" label="Save Customization" class="p-button-sm"
              [loading]="savingChatbotSettings" [disabled]="savingChatbotSettings" (click)="saveChatbotSettings()"></button>
          </div>
          <div class="chatbot-customize-layout">
            <div class="chatbot-config-panel">
              <div class="chatbot-style-grid">
                <div>
                  <label class="block font-medium mb-1">Bot Name</label>
                  <input pInputText class="w-full" [(ngModel)]="chatbotStyle.botName" />
                </div>
                <div class="chatbot-field-wide">
                  <label class="block font-medium mb-1">Initial Message</label>
                  <textarea pTextarea rows="4" class="w-full chatbot-message-input" [(ngModel)]="chatbotStyle.initialMessage" placeholder="Enter what user should see first..."></textarea>
                </div>
                <div class="chatbot-field-wide">
                  <div class="quick-buttons-head">
                    <label class="block font-medium mb-1">Quick Reply Buttons</label>
                    <button pButton type="button" icon="pi pi-plus" label="Add Button" class="p-button-sm p-button-outlined" (click)="addQuickButton()"></button>
                  </div>
                  <div class="quick-buttons-list">
                    <div class="quick-button-row" *ngFor="let btn of chatbotStyle.quickButtons; let i = index">
                      <input pInputText class="w-full" [(ngModel)]="chatbotStyle.quickButtons[i]" placeholder="Button label" />
                      <button pButton type="button" icon="pi pi-trash" class="p-button-text p-button-danger" (click)="removeQuickButton(i)"></button>
                    </div>
                  </div>
                </div>
                <div>
                  <label class="block font-medium mb-1">Position</label>
                  <p-select appendTo="body" [filter]="true" filterPlaceholder="Search" [options]="[{label:'Right',value:'right'},{label:'Left',value:'left'}]" optionLabel="label" optionValue="value" [(ngModel)]="chatbotStyle.position"></p-select>
                </div>
                <div>
                  <label class="block font-medium mb-1">Primary Color</label>
                  <input pInputText type="color" class="w-full color-input" [(ngModel)]="chatbotStyle.primaryColor" />
                </div>
                <div>
                  <label class="block font-medium mb-1">Text Color</label>
                  <input pInputText type="color" class="w-full color-input" [(ngModel)]="chatbotStyle.textColor" />
                </div>
                <div>
                  <label class="block font-medium mb-1">Border Radius (px)</label>
                  <input pInputText type="number" class="w-full" [(ngModel)]="chatbotStyle.borderRadius" />
                </div>
              </div>
            </div>
            <div class="chatbot-preview-panel">
              <div class="chatbot-preview-box">
                <div class="chatbot-preview-header">Live Preview</div>
                <div class="preview-launcher"
                  [ngStyle]="{'background-color': chatbotStyle.primaryColor,'color': chatbotStyle.textColor,'border-radius.px': chatbotStyle.borderRadius,'margin-left': chatbotStyle.position === 'left' ? '0' : 'auto','margin-right': chatbotStyle.position === 'right' ? '0' : 'auto'}">
                  {{ chatbotStyle.botName }}
                </div>
                <div class="chatbot-mini-window mt-3">
                  <div class="chatbot-mini-header" [ngStyle]="{'background-color': chatbotStyle.primaryColor,'color': chatbotStyle.textColor}">{{ chatbotStyle.botName }}</div>
                  <div class="chatbot-mini-body">
                    <div class="mini-msg">{{ chatbotStyle.initialMessage || 'Hello! How can I help you today?' }}</div>
                    <button class="mini-quick-btn" type="button" *ngFor="let btn of chatbotStyle.quickButtons">{{ btn }}</button>
                  </div>
                  <div class="chatbot-mini-input">
                    <input type="text" placeholder="Type your message..." />
                    <button type="button" [ngStyle]="{'background-color': chatbotStyle.primaryColor,'color': chatbotStyle.textColor,'border-radius.px': chatbotStyle.borderRadius}">Send</button>
                  </div>
                  <div class="chatbot-mini-powered">Powered by InstaRepli.com</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ng-container>
    </div>

    <!-- ══════════════ JOURNEY PHASES ══════════════ -->
    <div *ngIf="activeTab === 'config'" class="settings-panel fadein">
      <div class="panel-header journey-header">
        <div>
          <h2 class="panel-title">
            Journey Phases
            <i class="pi pi-info-circle journey-info"
               pTooltip="Phase = a top-level chapter of the journey (Lead, KYC, Sale, Onboarding).&#10;Sub-phases are optional — split a phase into smaller steps.&#10;Stages are what a case sits on INSIDE a phase (New → Qualified → Complete), configured per phase.&#10;SOPs are the checklist — each has an SLA target and a Required flag."
               tooltipPosition="right"></i>
          </h2>
          <p class="panel-sub">
            Define how a lead flows through your business. Each stage can hold sub-stages or SOPs directly.
          </p>
        </div>
        <div class="journey-header__actions">
          <!-- Search — matches stage AND sub-stage names. -->
          <span class="settings-search">
            <i class="pi pi-search"></i>
            <input type="text" pInputText [(ngModel)]="stageSearch"
                   placeholder="Search stages or sub-stages…" aria-label="Search stages" />
            <button type="button" class="settings-search__clear" *ngIf="stageSearch"
                    (click)="clearStageSearch()" aria-label="Clear search"><i class="pi pi-times"></i></button>
          </span>
          <button pButton icon="pi pi-plus" label="New Stage"
                  class="p-button-sm"
                  (click)="openAddStageDialog()"></button>
        </div>
      </div>

      <!-- Loading -->
      <div *ngIf="loadingStatuses" class="js-loading">
        <p-progressSpinner strokeWidth="4" styleClass="settings-spinner"></p-progressSpinner>
        <span>Loading journey…</span>
      </div>

      <!-- Empty state — no stages anywhere -->
      <div *ngIf="!loadingStatuses && !hasJourneyStages && unfiledStages.length === 0"
           class="js-empty">
        <div class="js-empty__icon"><i class="pi pi-sitemap"></i></div>
        <div class="js-empty__title">No journey stages yet</div>
        <div class="js-empty__sub">
          Map out how a lead moves through your business — for example
          <strong>Lead → KYC → Sale → Onboarding</strong>.
        </div>
        <button pButton icon="pi pi-plus" label="Create your first stage"
                class="p-button-sm" (click)="openAddStageDialog()"></button>
      </div>

      <!-- Search active but nothing matched. -->
      <div *ngIf="!loadingStatuses && noStageSearchResults" class="js-empty">
        <div class="js-empty__icon"><i class="pi pi-search"></i></div>
        <div class="js-empty__title">No stages match “{{ stageSearch }}”</div>
        <div class="js-empty__sub">Try a different word, or clear the search to see the full journey.</div>
        <button pButton icon="pi pi-times" label="Clear search" class="p-button-sm p-button-outlined"
                (click)="clearStageSearch()"></button>
      </div>

      <!-- Journey stages — accordion -->
      <div *ngIf="!loadingStatuses && hasJourneyStages && !noStageSearchResults" class="js-list">
        <div *ngFor="let stage of visibleJourneyStages; let i = index"
             class="js-stage"
             [class.js-stage--open]="isStageExpanded(stage.id)">

          <!-- Stage header — clickable row that toggles open/closed.
               The "map workflow" link icon lives in the right cluster
               and stops propagation so clicking it doesn't also toggle
               the accordion. -->
          <div class="js-stage__head" (click)="toggleStage(stage.id)">
            <div class="js-stage__left">
              <span class="js-stage__num">{{ i + 1 }}</span>
              <div class="js-stage__title">
                <div class="js-stage__name">{{ stage.name }}</div>
                <div class="js-stage__sub">
                  <span *ngIf="hasSubstages(stage.id)">
                    {{ substagesOf(stage.id).length }} sub-stage{{ substagesOf(stage.id).length === 1 ? '' : 's' }}
                    · {{ sopCountFor(stage.id) }} SOPs
                  </span>
                  <span *ngIf="!hasSubstages(stage.id)">
                    Direct SOPs · {{ sopCountFor(stage.id) }}
                  </span>
                </div>
              </div>
            </div>
            <div class="js-stage__right">

              <!-- Department chip — shown when a team owns this stage -->
              <span *ngIf="hasMappedDepartment(stage)"
                    class="js-stage__dept-chip"
                    pTooltip="Team owns this stage — click to change"
                    tooltipPosition="top">
                <i class="pi pi-users"></i>
                {{ mappedDepartmentLabel(stage) || 'Team attached' }}
              </span>

              <!-- Workflow chip — shown when something is mapped -->
              <span *ngIf="hasMappedWorkflow(stage)"
                    class="js-stage__wf-chip"
                    pTooltip="Workflow attached — click to change"
                    tooltipPosition="top">
                <i class="pi pi-bolt"></i>
                {{ mappedWorkflowLabel(stage) || 'Workflow attached' }}
              </span>

              <!-- V017 channel + opening-mode badges (read-only on the card).
                   Channel only shows for auto phases — a manual phase has no
                   auto reach-out, so there's no sending channel to surface. -->
              <span class="js-stage__chip js-stage__chip--channel"
                    *ngIf="stage.channel && stage.openingMode !== 'manual'">
                <i class="pi"
                   [class.pi-whatsapp]="stage.channel === 'whatsapp'"
                   [class.pi-mobile]="stage.channel === 'sms'"
                   [class.pi-phone]="stage.channel === 'voice'"
                   [class.pi-envelope]="stage.channel === 'email'"
                   [class.pi-globe]="stage.channel === 'web'"></i>
                {{ channelLabel(stage.channel) }}
              </span>
              <span class="js-stage__chip js-stage__chip--mode"
                    *ngIf="stage.openingMode"
                    [class.js-stage__chip--manual]="stage.openingMode === 'manual'">
                {{ stage.openingMode === 'manual' ? 'Manual' : 'Auto' }}
              </span>

              <!-- V017+ unified Configure button — opens the tabbed dialog
                   (General / Opening / Statuses / SOPs / Sub-phases / Audit).
                   The old Map-workflow + Map-department inline buttons are
                   subsumed into the General tab. -->
              <button type="button"
                      class="js-stage__cfg-btn"
                      (click)="$event.stopPropagation(); openPhaseConfigDialog(stage)"
                      pTooltip="Configure phase (channel, opening, statuses, SOPs, audit)"
                      tooltipPosition="top">
                <i class="pi pi-cog"></i>
              </button>

              <!-- Delete-stage icon button — separate from the workflow icon
                   so accidental clicks are unlikely. Server enforces safety
                   (blocks delete when sub-stages or active cases exist). -->
              <button type="button"
                      class="js-stage__del-btn"
                      (click)="$event.stopPropagation(); deleteJourneyStage(stage, 'stage')"
                      pTooltip="Delete this journey stage"
                      tooltipPosition="top">
                <i class="pi pi-trash"></i>
              </button>

              <span class="js-stage__chev">
                <i class="pi" [class.pi-chevron-down]="isStageExpanded(stage.id)"
                              [class.pi-chevron-right]="!isStageExpanded(stage.id)"></i>
              </span>
            </div>
          </div>

          <!-- Stage body (collapsed by default) -->
          <div *ngIf="isStageExpanded(stage.id)" class="js-stage__body">

            <!-- Inline-add toolbar removed by design — SOPs and sub-phases
                 are added from inside the Phase Config dialog (⚙) which
                 surfaces all phase authoring in one focused place.
                 A short read-only summary stays here for context. -->
            <div class="js-stage__toolbar js-stage__toolbar--readonly">
              <span class="js-stage__hint" *ngIf="!hasSubstages(stage.id) && sopCountFor(stage.id) === 0">
                <i class="pi pi-info-circle"></i>
                Click <strong>⚙ Configure</strong> on this phase to add SOPs or sub-phases.
              </span>
              <span class="js-stage__hint" *ngIf="hasSubstages(stage.id)">
                <i class="pi pi-info-circle"></i>
                SOPs live inside each sub-phase. Open a sub-phase to view; manage from <strong>⚙ Configure</strong>.
              </span>
            </div>

            <!-- ── Path A: stage has sub-stages → nested list ────── -->
            <div *ngIf="hasSubstages(stage.id)" class="js-sub-list">
              <div *ngFor="let sub of visibleSubstagesOf(stage.id); let j = index"
                   class="js-sub"
                   [class.js-sub--open]="isSubstageExpanded(sub.id)">

                <div class="js-sub__head" (click)="toggleSubstage(sub.id)">
                  <div class="js-sub__left">
                    <span class="js-sub__order">{{ j + 1 }}</span>
                    <div class="js-sub__title">
                      <div class="js-sub__name">{{ sub.name }}</div>
                      <div class="js-sub__sub">
                        <span *ngIf="loadingSopsFor[sub.id]">Loading…</span>
                        <span *ngIf="!loadingSopsFor[sub.id]">
                          {{ sopsFor(sub.id).length }} SOP{{ sopsFor(sub.id).length === 1 ? '' : 's' }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="js-sub__right">
                    <!-- Delete sub-stage -->
                    <button type="button"
                            class="js-sub__del-btn"
                            (click)="$event.stopPropagation(); deleteJourneyStage(sub, 'substage')"
                            pTooltip="Delete this sub-stage"
                            tooltipPosition="top">
                      <i class="pi pi-trash"></i>
                    </button>
                    <span class="js-sub__chev">
                      <i class="pi" [class.pi-chevron-down]="isSubstageExpanded(sub.id)"
                                    [class.pi-chevron-right]="!isSubstageExpanded(sub.id)"></i>
                    </span>
                  </div>
                </div>

                <div *ngIf="isSubstageExpanded(sub.id)" class="js-sub__body">
                  <!-- SOP list for this sub-stage -->
                  <ul class="js-sop-list" *ngIf="sopsFor(sub.id).length > 0">
                    <li *ngFor="let sop of sopsFor(sub.id)" class="js-sop">
                      <div class="js-sop__left">
                        <span class="js-sop__order">{{ sop.orderNo }}</span>
                        <div class="js-sop__main">
                          <div class="js-sop__name">{{ sop.label }}</div>
                          <div *ngIf="sop.description" class="js-sop__desc">{{ sop.description }}</div>
                        </div>
                      </div>
                      <div class="js-sop__meta">
                        <span class="js-sop__sla"
                              [class.js-sop__sla--none]="!sop.slaMinutes">
                          <i class="pi pi-clock"></i>
                          SLA: {{ formatSla(sop.slaMinutes) }}
                        </span>
                        <span class="js-sop__req"
                              [class.js-sop__req--yes]="sop.isMandatory"
                              [class.js-sop__req--no]="!sop.isMandatory">
                          {{ sop.isMandatory ? 'Required' : 'Optional' }}
                        </span>
                        <button pButton icon="pi pi-trash" [text]="true" [rounded]="true"
                                size="small" severity="danger"
                                pTooltip="Delete SOP" tooltipPosition="left"
                                (click)="deleteSop(sub.id, sop)"></button>
                      </div>
                    </li>
                  </ul>
                  <div *ngIf="sopsFor(sub.id).length === 0 && !loadingSopsFor[sub.id]"
                       class="js-sop-empty">
                    <i class="pi pi-list-check"></i>
                    No SOPs in this sub-stage yet — add them from
                    <strong>⚙ Configure</strong> on the parent phase.
                  </div>
                </div>
              </div>
            </div>

            <!-- ── Path B: no sub-stages → direct SOP list ───────── -->
            <ul *ngIf="!hasSubstages(stage.id) && sopsFor(stage.id).length > 0"
                class="js-sop-list">
              <li *ngFor="let sop of sopsFor(stage.id)" class="js-sop">
                <div class="js-sop__left">
                  <span class="js-sop__order">{{ sop.orderNo }}</span>
                  <div class="js-sop__main">
                    <div class="js-sop__name">{{ sop.label }}</div>
                    <div *ngIf="sop.description" class="js-sop__desc">{{ sop.description }}</div>
                  </div>
                </div>
                <div class="js-sop__meta">
                  <span class="js-sop__sla"
                        [class.js-sop__sla--none]="!sop.slaMinutes">
                    <i class="pi pi-clock"></i>
                    SLA: {{ formatSla(sop.slaMinutes) }}
                  </span>
                  <span class="js-sop__req"
                        [class.js-sop__req--yes]="sop.isMandatory"
                        [class.js-sop__req--no]="!sop.isMandatory">
                    {{ sop.isMandatory ? 'Required' : 'Optional' }}
                  </span>
                  <button pButton icon="pi pi-trash" [text]="true" [rounded]="true"
                          size="small" severity="danger"
                          pTooltip="Delete SOP" tooltipPosition="left"
                          (click)="deleteSop(stage.id, sop)"></button>
                </div>
              </li>
            </ul>

          </div>
        </div>

        <!-- Legacy Unfiled rows — hidden by design. Pre-V014 rows that
             don't belong to a phase are not surfaced on the Status Master
             screen anymore. They remain in the DB and the Operations
             screen migration path will reattach them or terminal-close
             them. -->
        <ng-container *ngIf="false && unfiledStages.length > 0"></ng-container>
      </div>
    </div>

    <!-- ══════ NEW STAGE DIALOG ═══════════════════════════════ -->
    <p-dialog [(visible)]="showStageDialog" header="New Phase" [modal]="true"
              [style]="{ width: '460px' }" [closable]="!savingStage">
      <div class="js-dialog">
        <p class="js-dialog__hint">
          A phase is a chapter of work — e.g. <strong>Lead</strong> (handled by Sales),
          <strong>KYC</strong> (handled by Compliance), <strong>Sale</strong> (handled by Closing).
          You'll pick which phases go into which operation — and in what order —
          on the <strong>Operations</strong> screen.
        </p>

        <div class="field">
          <label>Phase name <span class="req">*</span></label>
          <input pInputText [(ngModel)]="stageForm.name" placeholder="e.g. Lead"
                 class="w-full" [disabled]="savingStage" maxlength="100"
                 (keydown.enter)="!savingStage && stageForm.name && createStage()"/>
        </div>
      </div>

      <ng-template pTemplate="footer">
        <button pButton label="Cancel" class="p-button-text p-button-secondary"
                (click)="showStageDialog = false" [disabled]="savingStage"></button>
        <button pButton icon="pi pi-check" label="Create Stage"
                [loading]="savingStage"
                [disabled]="savingStage || !stageForm.name"
                (click)="createStage()"></button>
      </ng-template>
    </p-dialog>

    <!-- ══════ NEW SUB-STAGE DIALOG ═══════════════════════════ -->
    <!-- baseZIndex above the Phase Config dialog so this one stacks on top
         when launched from inside it. draggable=false matches the parent. -->
    <p-dialog [(visible)]="showSubstageDialog" [modal]="true"
              [style]="{ width: '440px' }" [closable]="!savingSubstage"
              [draggable]="false" [resizable]="false" [baseZIndex]="11000"
              appendTo="body"
              styleClass="p-fluid settings-dialog">
      <ng-template pTemplate="header">
        <div>
          <div class="text-500 text-xs uppercase tracking-wide">Add sub-stage under</div>
          <div class="font-bold text-900">{{ substageParentName }}</div>
        </div>
      </ng-template>

      <div class="js-dialog">
        <div class="field">
          <label>Sub-stage name <span class="req">*</span></label>
          <input pInputText [(ngModel)]="substageForm.name"
                 placeholder="e.g. Qualified" class="w-full"
                 [disabled]="savingSubstage" maxlength="100"
                 (keydown.enter)="!savingSubstage && substageForm.name && createSubstage()"/>
        </div>

        <div class="field">
          <label>Display order within this stage</label>
          <input pInputText type="number" [(ngModel)]="substageForm.order"
                 class="w-full" min="1" [disabled]="savingSubstage"/>
        </div>
      </div>

      <ng-template pTemplate="footer">
        <button pButton label="Cancel" class="p-button-text p-button-secondary"
                (click)="showSubstageDialog = false" [disabled]="savingSubstage"></button>
        <button pButton icon="pi pi-check" label="Add Sub-stage"
                [loading]="savingSubstage"
                [disabled]="savingSubstage || !substageForm.name"
                (click)="createSubstage()"></button>
      </ng-template>
    </p-dialog>

    <!-- ══════ NEW SOP DIALOG ═════════════════════════════════ -->
    <!-- Same stacking rules as the sub-stage dialog — both launched from
         inside the Phase Config modal so they need higher z-index. -->
    <p-dialog [(visible)]="showJourneySopDialog" [modal]="true"
              [style]="{ width: '520px' }" [closable]="!savingJourneySop"
              [draggable]="false" [resizable]="false" [baseZIndex]="11000"
              appendTo="body"
              styleClass="p-fluid settings-dialog">
      <ng-template pTemplate="header">
        <div>
          <div class="text-500 text-xs uppercase tracking-wide">{{ journeySopForm.id ? 'Edit SOP in' : 'Add SOP to' }}</div>
          <div class="font-bold text-900">
            {{ sopForOwnerName }}
            <span class="js-dialog__kind">
              · {{ sopForOwnerKind === 'stage' ? 'Stage' : 'Sub-stage' }}
            </span>
          </div>
        </div>
      </ng-template>

      <div class="js-dialog">
        <div class="field">
          <label>SOP name <span class="req">*</span></label>
          <input pInputText [(ngModel)]="journeySopForm.label"
                 placeholder="e.g. Send welcome message"
                 class="w-full" [disabled]="savingJourneySop" maxlength="200"/>
        </div>

        <div class="field">
          <label>Description (optional)</label>
          <textarea pTextarea [(ngModel)]="journeySopForm.description"
                    placeholder="What does this SOP cover?"
                    [disabled]="savingJourneySop" rows="2"
                    class="w-full js-sop-textarea"></textarea>
        </div>

        <div class="field">
          <label>SLA target <small class="text-500">— when must this be done?</small></label>
          <div class="js-sla-row">
            <input pInputText type="number" min="1"
                   [(ngModel)]="journeySopForm.slaValue"
                   class="js-sla-num"
                   placeholder="e.g. 60"
                   [disabled]="savingJourneySop"/>
            <p-select [filter]="true" filterPlaceholder="Search" appendTo="body" [options]="[
                        { label: 'Minutes', value: 'minutes' },
                        { label: 'Hours',   value: 'hours'   },
                        { label: 'Days',    value: 'days'    }
                      ]"
                      [(ngModel)]="journeySopForm.slaUnit"
                      optionLabel="label" optionValue="value"
                      styleClass="js-sla-unit"></p-select>
            <span class="js-sla-preview" *ngIf="journeySopForm.slaValue">
              = {{ formatSla(toSlaMinutesPublic(journeySopForm.slaValue, journeySopForm.slaUnit)) }}
            </span>
          </div>
          <small class="text-500">Leave blank for no SLA — operator just ticks it when done.</small>
        </div>

        <div class="js-required-row">
          <p-checkbox [(ngModel)]="journeySopForm.isMandatory" [binary]="true"
                      inputId="sop-req"></p-checkbox>
          <label for="sop-req" class="js-required-label">
            <div class="js-required-title">Required to advance</div>
            <div class="js-required-sub">
              When ticked, this SOP must be completed before the lead can move to the next stage.
            </div>
          </label>
        </div>
      </div>

      <ng-template pTemplate="footer">
        <button pButton label="Cancel" class="p-button-text p-button-secondary"
                (click)="showJourneySopDialog = false" [disabled]="savingJourneySop"></button>
        <button pButton icon="pi pi-check" [label]="journeySopForm.id ? 'Save SOP' : 'Add SOP'"
                [loading]="savingJourneySop"
                [disabled]="savingJourneySop || !journeySopForm.label"
                (click)="saveSop()"></button>
      </ng-template>
    </p-dialog>

    <!-- ════════════════════════════════════════════════════════ -->
    <!--  V017/V018/V019/V020 — UNIFIED PHASE CONFIG DIALOG      -->
    <!--  Tabs: General | Opening | Statuses | SOPs | Sub-phases  -->
    <!--        | Audit                                            -->
    <!-- ════════════════════════════════════════════════════════ -->
    <p-dialog [(visible)]="showPhaseConfigDialog" [modal]="true"
              [style]="{ width: '900px', maxWidth: '95vw' }"
              [closable]="true"
              [draggable]="false"
              [resizable]="false"
              [baseZIndex]="10000"
              appendTo="body"
              [header]="phaseConfigStage?.name ? 'Configure phase — ' + phaseConfigStage.name : 'Configure phase'"
              styleClass="p-fluid settings-dialog js-phase-config-dialog">

      <div class="pc-shell">
        <!-- Tab strip -->
        <div class="pc-tabs">
          <button type="button" class="pc-tab"
                  [class.pc-tab--active]="phaseConfigActiveTab === 'general'"
                  (click)="switchPhaseConfigTab('general')">
            <i class="pi pi-cog"></i> General
          </button>
          <button type="button" class="pc-tab" *ngIf="isWhatsAppChannel"
                  [class.pc-tab--active]="phaseConfigActiveTab === 'opening'"
                  (click)="switchPhaseConfigTab('opening')">
            <i class="pi pi-send"></i> Opening
          </button>
          <button type="button" class="pc-tab" *ngIf="isVoiceChannel"
                  [class.pc-tab--active]="phaseConfigActiveTab === 'voice'"
                  (click)="switchPhaseConfigTab('voice')">
            <i class="pi pi-phone"></i> Voice config
          </button>
          <button type="button" class="pc-tab" *ngIf="isEmailChannel"
                  [class.pc-tab--active]="phaseConfigActiveTab === 'email'"
                  (click)="switchPhaseConfigTab('email')">
            <i class="pi pi-envelope"></i> Email config
          </button>
          <button type="button" class="pc-tab"
                  [class.pc-tab--active]="phaseConfigActiveTab === 'statuses'"
                  (click)="switchPhaseConfigTab('statuses')">
            <i class="pi pi-tag"></i> Statuses
          </button>
          <button type="button" class="pc-tab"
                  [class.pc-tab--active]="phaseConfigActiveTab === 'sops'"
                  (click)="switchPhaseConfigTab('sops')">
            <i class="pi pi-check-square"></i> SOPs
          </button>
          <button type="button" class="pc-tab"
                  [class.pc-tab--active]="phaseConfigActiveTab === 'subphases'"
                  (click)="switchPhaseConfigTab('subphases')">
            <i class="pi pi-sitemap"></i> Sub-phases
          </button>
          <button type="button" class="pc-tab"
                  [class.pc-tab--active]="phaseConfigActiveTab === 'audit'"
                  (click)="switchPhaseConfigTab('audit')">
            <i class="pi pi-history"></i> Audit
          </button>
        </div>

        <!-- ═══════ GENERAL TAB ═══════ -->
        <div class="pc-pane" *ngIf="phaseConfigActiveTab === 'general'">
          <div class="js-dialog">
            <div class="pc-pane__title">
              <span>General</span>
              <i class="pi pi-info-circle pc-info"
                 pTooltip="Phase identity, owner and how it reaches the customer. Workflow + sequence are set on the Operations screen."
                 tooltipPosition="right"></i>
            </div>

            <div class="formgrid grid">
              <div class="field col-12">
                <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest">
                  Name <span class="req">*</span>
                </label>
                <input pInputText [(ngModel)]="phaseGeneralForm.name" class="w-full"
                       placeholder="e.g. Lead Capture" />
              </div>

              <!-- Opening mode comes first because it gates Channel
                   visibility — keeps the cause-and-effect read order. -->
              <div class="field col-12 md:col-6">
                <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest">
                  Opening mode
                  <i class="pi pi-info-circle pc-info"
                     [pTooltip]="phaseGeneralForm.openingMode === 'manual'
                        ? 'Manual — case sits in the agent inbox; no auto reach-out, so no channel to configure.'
                        : 'Auto — system sends the opening template or starts the workflow per the 24h rule.'"
                     tooltipPosition="top"></i>
                </label>
                <p-select [filter]="true" filterPlaceholder="Search" appendTo="body" [options]="openingModeOptions"
                          [(ngModel)]="phaseGeneralForm.openingMode"
                          optionLabel="label" optionValue="value" styleClass="w-full"></p-select>
              </div>

              <!-- Channel only meaningful when opening mode is 'auto' —
                   manual phases don't proactively reach out, so there's
                   no outbound channel to configure. The persisted column
                   stays at its existing value so re-toggling back to auto
                   restores the previous selection. -->
              <div class="field col-12 md:col-6"
                   *ngIf="phaseGeneralForm.openingMode === 'auto'">
                <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest">
                  Channels
                  <i class="pi pi-info-circle pc-info"
                     pTooltip="How the phase reaches the customer. Pick one or more — each enabled channel gets its own config tab (WhatsApp → Opening, Voice → Voice config, Email → Email config). The first one picked is the primary channel."
                     tooltipPosition="top"></i>
                </label>
                <p-multiSelect filterPlaceHolder="Search" appendTo="body" [options]="channelOptions"
                          [(ngModel)]="phaseGeneralForm.channels"
                          (onChange)="onPhaseChannelChange()"
                          optionLabel="label" optionValue="value" display="chip"
                          placeholder="Pick one or more channels" styleClass="w-full"></p-multiSelect>
              </div>

              <div class="field col-12 md:col-6">
                <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest">
                  Department
                  <i class="pi pi-info-circle pc-info"
                     pTooltip="Team that owns cases in this phase. Round-robin assigns a member when a case enters."
                     tooltipPosition="top"></i>
                </label>
                <p-select [filter]="true" filterPlaceholder="Search" appendTo="body" [options]="availableDepartments"
                          [(ngModel)]="phaseGeneralForm.departmentId"
                          optionLabel="name" optionValue="id"
                          [showClear]="true" placeholder="(unassigned)"
                          styleClass="w-full"></p-select>
              </div>

              <!-- Co-owning departments — several teams share ONE phase, each seeing only its
                   own leads. Hidden until a primary is chosen: a phase cannot have co-owners
                   without an owner. -->
              <div class="field col-12 md:col-6"
                   *ngIf="phaseGeneralForm.departmentId && !phaseGeneralForm.restricted">
                <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest">
                  Also handled by
                  <i class="pi pi-info-circle pc-info"
                     pTooltip="Extra teams that also WORK this phase — use it for a shared stage like Distributors. Each lead is routed to ONE of these teams and stays private to it; the other teams cannot see it. Which team gets a lead is decided by this phase's Assignment rules."
                     tooltipPosition="top"></i>
                </label>
                <p-multiSelect filterPlaceHolder="Search" appendTo="body"
                          [options]="phaseCoOwnerOptions()"
                          [(ngModel)]="phaseGeneralForm.departmentIds"
                          optionLabel="name" optionValue="id" display="chip"
                          placeholder="No other team — this phase is owned by one team"
                          [emptyMessage]="'No other teams available'"
                          styleClass="w-full"></p-multiSelect>
              </div>

              <div class="field col-12 md:col-6">
                <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest">
                  Notice template
                  <i class="pi pi-info-circle pc-info"
                     pTooltip="Optional courtesy message sent on phase activation. Different from the opening template in the Opening tab — Notice is purely informational, no conversation follow-up. Create one in WhatsApp Templates with type 'Notice'."
                     tooltipPosition="top"></i>
                </label>
                <p-select appendTo="body" [options]="noticeTemplateOptions"
                          [(ngModel)]="phaseGeneralForm.noticeTemplateId"
                          optionLabel="displayLabel" optionValue="id"
                          [showClear]="true"
                          [filter]="true" filterBy="displayLabel,metaTemplateName"
                          placeholder="(none) — phase will open silently"
                          [emptyMessage]="loadingTemplatesForPhase
                            ? 'Loading templates…'
                            : 'No NOTICE-type templates yet. Create one in WhatsApp Templates first.'"
                          styleClass="w-full"></p-select>
              </div>

              <div class="field col-12 md:col-6">
                <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest">
                  Phase SLA
                  <i class="pi pi-info-circle pc-info"
                     pTooltip="Target time a case should spend in this phase before it's flagged as breached — the whole-phase clock. Separate from the per-SOP SLAs on the SOPs tab. Leave blank for no phase SLA."
                     tooltipPosition="top"></i>
                </label>
                <div class="pc-sla-row">
                  <p-inputNumber [(ngModel)]="phaseGeneralForm.slaValue" [min]="0"
                                 placeholder="0" styleClass="pc-sla-num"></p-inputNumber>
                  <p-select [filter]="true" filterPlaceholder="Search" appendTo="body" [options]="[
                              {label:'minutes',value:'minutes'},
                              {label:'hours',value:'hours'},
                              {label:'days',value:'days'}]"
                            [(ngModel)]="phaseGeneralForm.slaUnit"
                            optionLabel="label" optionValue="value" styleClass="pc-sla-unit"></p-select>
                  <span class="pc-sla-hint"
                        *ngIf="toSlaMinutesPublic(phaseGeneralForm.slaValue, phaseGeneralForm.slaUnit)">
                    = {{ formatSla(toSlaMinutesPublic(phaseGeneralForm.slaValue, phaseGeneralForm.slaUnit)) }}
                  </span>
                </div>
              </div>
            </div>

            <!-- V024 — Phase position flags + entry metadata picker.
                 Position flags live in their own subsection so they're
                 not buried in the field grid. -->
            <div class="pc-section-divider"></div>

            <div class="pc-pane__title pc-pane__title--sub">
              <span>Phase position</span>
              <i class="pi pi-info-circle pc-info"
                 pTooltip="Mark this phase as the entry point (where new cases start) or the last phase (where cases close). Operations sequence builds on these flags."
                 tooltipPosition="right"></i>
            </div>

            <div class="pc-flag-grid">
              <label class="pc-flag-card"
                     [class.pc-flag-card--on]="phaseGeneralForm.isEntryPhase">
                <p-checkbox [(ngModel)]="phaseGeneralForm.isEntryPhase"
                            [binary]="true"
                            (onChange)="onEntryPhaseToggle()"></p-checkbox>
                <div class="pc-flag-card__text">
                  <div class="pc-flag-card__title">
                    <i class="pi pi-sign-in"></i> Entry phase
                  </div>
                  <div class="pc-flag-card__desc">
                    First phase — receives leads from intake sources.
                  </div>
                </div>
              </label>

              <label class="pc-flag-card"
                     [class.pc-flag-card--on]="phaseGeneralForm.isLastPhase">
                <p-checkbox [(ngModel)]="phaseGeneralForm.isLastPhase" [binary]="true"></p-checkbox>
                <div class="pc-flag-card__text">
                  <div class="pc-flag-card__title">
                    <i class="pi pi-flag-fill"></i> Last phase
                  </div>
                  <div class="pc-flag-card__desc">
                    Terminal phase — case closes when SOPs complete here.
                  </div>
                </div>
              </label>

              <label class="pc-flag-card"
                     [class.pc-flag-card--on]="phaseGeneralForm.allowManualCreate">
                <p-checkbox [(ngModel)]="phaseGeneralForm.allowManualCreate" [binary]="true"></p-checkbox>
                <div class="pc-flag-card__text">
                  <div class="pc-flag-card__title">
                    <i class="pi pi-user-plus"></i> Allow creating leads here
                  </div>
                  <div class="pc-flag-card__desc">
                    This phase's department can add a lead that starts here (e.g. a Site Visit walk-in),
                    skipping the entry phase.
                  </div>
                </div>
              </label>
            </div>

            <!-- Entry-phase intake config — visible only when the
                 Entry phase toggle is on. Admin first chooses HOW a case
                 enters (data pushed vs customer-initiated), and the
                 metadata picker reveals only for the data-pushed mode. -->
            <div *ngIf="phaseGeneralForm.isEntryPhase" class="pc-entry-meta">
              <div class="pc-pane__title pc-pane__title--sub">
                <span>How does a case enter this phase?</span>
                <i class="pi pi-info-circle pc-info"
                   pTooltip="Entry phases fire from two patterns. (1) A lead source pushes structured data (Meta lead form, web form, manual entry) — define what fields must arrive. (2) The customer initiates by messaging us first (claim, support, report retrieval) — no pre-collected data; AI handles intake."
                   tooltipPosition="right"></i>
              </div>

              <div class="pc-intake-modes">
                <label class="pc-intake-mode"
                       [class.pc-intake-mode--on]="phaseGeneralForm.intakePattern === 'customer_initiated'">
                  <input type="radio" name="intakePattern" value="customer_initiated"
                         [(ngModel)]="phaseGeneralForm.intakePattern"
                         (change)="onIntakePatternChange()" />
                  <div class="pc-intake-mode__text">
                    <div class="pc-intake-mode__title">
                      <i class="pi pi-comments"></i> Customer initiates
                    </div>
                    <div class="pc-intake-mode__desc">
                      Customer messages us first (inbound WhatsApp, voice call, web chat).
                      No pre-collected data. AI workflow handles intake.
                    </div>
                    <div class="pc-intake-mode__examples">
                      <i class="pi pi-tags"></i>
                      Claim settlement · Support ticket · Lab report request · Hospital appointment
                    </div>
                  </div>
                </label>

                <label class="pc-intake-mode"
                       [class.pc-intake-mode--on]="phaseGeneralForm.intakePattern === 'lead_pushed'">
                  <input type="radio" name="intakePattern" value="lead_pushed"
                         [(ngModel)]="phaseGeneralForm.intakePattern"
                         (change)="onIntakePatternChange()" />
                  <div class="pc-intake-mode__text">
                    <div class="pc-intake-mode__title">
                      <i class="pi pi-database"></i> Lead source pushes data
                    </div>
                    <div class="pc-intake-mode__desc">
                      Meta lead form, web form, or manual entry sends structured fields
                      (name, phone, etc.) before the conversation starts.
                    </div>
                    <div class="pc-intake-mode__examples">
                      <i class="pi pi-tags"></i>
                      Sales lead capture · Loan application · Solar quote request · Real estate inquiry
                    </div>
                  </div>
                </label>
              </div>

              <!-- Metadata field picker — visible only for lead_pushed -->
              <div *ngIf="phaseGeneralForm.intakePattern === 'lead_pushed'"
                   class="pc-entry-meta__contract">
                <div class="pc-pane__title pc-pane__title--sub">
                  <span>Expected fields from lead source</span>
                  <i class="pi pi-info-circle pc-info"
                     pTooltip="What the source must include for a case to enter cleanly. Missing required fields route the lead to manual correction instead of starting the case."
                     tooltipPosition="right"></i>
                </div>

                <div *ngIf="loadingSystemFields" class="pc-inline-loading">
                  <p-progressSpinner strokeWidth="4" styleClass="settings-spinner"></p-progressSpinner>
                  <span>Loading system fields…</span>
                </div>

                <div *ngIf="!loadingSystemFields">
                  <div class="pc-entry-meta__add">
                    <p-select appendTo="body" [options]="availableSystemFieldOptions"
                              optionLabel="label" optionValue="value"
                              placeholder="Pick a field to add (name, phone, email, …)"
                              [filter]="true" filterBy="label,value"
                              #fieldPicker
                              (onChange)="addEntryMetadataField($event.value); fieldPicker.writeValue(null)"
                              styleClass="w-full"></p-select>
                    <small class="text-500">
                      Manage the master list in <strong>Settings → Master Data → System Fields</strong>.
                    </small>
                  </div>

                  <div *ngIf="!phaseGeneralForm.entryMetadataFields.length"
                       class="pc-empty pc-entry-meta__empty">
                    No fields selected yet. Pick what the lead source should send.
                  </div>

                  <ul *ngIf="phaseGeneralForm.entryMetadataFields.length"
                      class="pc-entry-meta__list">
                    <li *ngFor="let f of phaseGeneralForm.entryMetadataFields"
                        class="pc-entry-meta__row"
                        [class.pc-entry-meta__row--required]="f.required">
                      <div class="pc-entry-meta__name">
                        <i class="pi pi-tag"></i>
                        <span class="pc-entry-meta__label">{{ fieldLabelFor(f.fieldCode) }}</span>
                        <span class="pc-entry-meta__code">{{ f.fieldCode }}</span>
                      </div>
                      <div class="pc-entry-meta__actions">
                        <label class="pc-entry-meta__required">
                          <p-checkbox [(ngModel)]="f.required" [binary]="true"></p-checkbox>
                          <span>Required</span>
                        </label>
                        <button pButton icon="pi pi-times"
                                class="p-button-text p-button-sm p-button-danger"
                                (click)="removeEntryMetadataField(f.fieldCode)"
                                pTooltip="Remove field"></button>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>

              <!-- Customer-initiated explainer — visible only for customer_initiated -->
              <div *ngIf="phaseGeneralForm.intakePattern === 'customer_initiated'"
                   class="pc-entry-meta__note">
                <i class="pi pi-info-circle"></i>
                <span>
                  No fields to configure here. When a customer sends a message, the AI
                  workflow on this phase handles identification + intake through the
                  conversation itself.
                </span>
              </div>
            </div>

            <div class="pc-pane__actions">
              <button pButton label="Save General" icon="pi pi-check"
                      [loading]="savingPhaseGeneral"
                      [disabled]="savingPhaseGeneral || !phaseGeneralForm.name?.trim()"
                      (click)="savePhaseGeneral()"></button>
            </div>
          </div>
        </div>

        <!-- ═══════ OPENING TAB ═══════ -->
        <div class="pc-pane" *ngIf="phaseConfigActiveTab === 'opening'">
          <div class="js-dialog">
            <div class="pc-pane__title">
              <span>Opening</span>
              <i class="pi pi-info-circle pc-info"
                 pTooltip="What to send when the phase activates and the customer's 24h window is closed. Reminder slots fire on cadence after."
                 tooltipPosition="right"></i>
            </div>

            <div *ngIf="loadingPhaseWaConfig" class="pc-inline-loading">
              <p-progressSpinner strokeWidth="4" styleClass="settings-spinner"></p-progressSpinner>
              <span>Loading opening config…</span>
            </div>

            <div *ngIf="!loadingPhaseWaConfig" class="formgrid grid">
              <div class="field col-12 md:col-6">
                <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest">
                  Opening template
                  <i class="pi pi-info-circle pc-info"
                     pTooltip="Pre-approved WhatsApp template sent when the customer's 24h window is closed."
                     tooltipPosition="top"></i>
                </label>
                <p-select appendTo="body" [options]="openingTemplateOptions"
                          [(ngModel)]="phaseWaConfigForm.templateId"
                          optionLabel="displayLabel" optionValue="id"
                          [showClear]="true"
                          [filter]="true" filterBy="displayLabel,metaTemplateName"
                          placeholder="Pick an ENTRY template"
                          (onChange)="onOpeningTemplateChange($event.value)"
                          (onClear)="onOpeningTemplateChange(null)"
                          [emptyMessage]="loadingTemplatesForPhase
                            ? 'Loading templates…'
                            : 'No ENTRY-type templates yet — create one first.'"
                          styleClass="w-full"></p-select>
              </div>
              <div class="field col-12 md:col-6">
                <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest">
                  Sender
                  <i class="pi pi-info-circle pc-info"
                     pTooltip="The WhatsApp number that sends this phase's messages — taken automatically from the template you picked. Each template is bound to one sender."
                     tooltipPosition="top"></i>
                </label>
                <!-- Read-only: derived from the chosen template's channel
                     config, the LeadWhatsAppConfig convention. No manual
                     UUID entry. -->
                <div class="pc-sender-display"
                     [class.pc-sender-display--empty]="!openingTemplatePhone">
                  <i class="pi" [class.pi-whatsapp]="openingTemplatePhone"
                                [class.pi-question-circle]="!openingTemplatePhone"></i>
                  <span>{{ openingTemplatePhone || 'Pick a template — its sender appears here' }}</span>
                </div>
              </div>

              <!-- Live WhatsApp preview — mirrors the WA Lead Config phone
                   mockup. Reflects the chosen template + variable bindings. -->
              <div class="field col-12">
                <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest">
                  Message preview
                  <i class="pi pi-info-circle pc-info"
                     pTooltip="How the opening message looks on the customer's phone. Bound variables show as [path]."
                     tooltipPosition="top"></i>
                </label>
                <div class="pc-phone-scene">
                  <div class="pc-phone-frame" *ngIf="pcOpeningTemplate; else pcNoPreview">
                    <div class="pc-wa-header">
                      <i class="pi pi-arrow-left"></i>
                      <div class="pc-wa-avatar">{{ (openingTemplatePhone || 'B').charAt(0) }}</div>
                      <div class="pc-wa-contact">
                        <span class="pc-wa-name">{{ openingTemplatePhone || 'Business' }}</span>
                        <span class="pc-wa-sub">online</span>
                      </div>
                      <i class="pi pi-ellipsis-v"></i>
                    </div>
                    <div class="pc-wa-body-area">
                      <div class="pc-wa-day">Today</div>
                      <div class="pc-wa-bubble">
                        <div class="pc-wa-img" *ngIf="pcHasImageHeader">
                          <i class="pi pi-image"></i><span>Image header</span>
                        </div>
                        <div class="pc-wa-hdr" *ngIf="pcTemplateHeaderText">{{ pcTemplateHeaderText }}</div>
                        <div class="pc-wa-text">{{ pcPreviewBody || pcTemplateBody }}</div>
                        <div class="pc-wa-foot" *ngIf="pcTemplateFooter">{{ pcTemplateFooter }}</div>
                        <div class="pc-wa-time">9:41 <i class="pi pi-check-circle"></i></div>
                      </div>
                      <div class="pc-wa-btns" *ngIf="pcTemplateButtons.length">
                        <button class="pc-wa-btn" *ngFor="let b of pcTemplateButtons">
                          <i class="pi"
                             [class.pi-reply]="b.type === 'QUICK_REPLY'"
                             [class.pi-external-link]="b.type === 'URL'"
                             [class.pi-phone]="b.type === 'PHONE_NUMBER'"></i>
                          {{ b.text }}
                        </button>
                      </div>
                    </div>
                  </div>
                  <ng-template #pcNoPreview>
                    <div class="pc-phone-empty">
                      <i class="pi pi-mobile"></i>
                      <p>Pick a template to see<br>the live preview</p>
                    </div>
                  </ng-template>
                </div>
              </div>

              <div class="field col-12">
                <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest">
                  Header image URL
                  <i class="pi pi-info-circle pc-info"
                     pTooltip="Optional. Type a static link, or pick a lead-intake field to use its URL — resolved per lead at send time. Supports lead.*, source.*, system.* placeholders."
                     tooltipPosition="top"></i>
                </label>
                <input pInputText [(ngModel)]="phaseWaConfigForm.headerImageUrl" class="w-full"
                       placeholder="https://cdn.example.com/banner.jpg" />

                <!-- Entry phase with intake fields → let the admin pull the image
                     straight from an intake URL instead of pasting a static link.
                     Picking one writes a {{lead.<code>}} token into the field above. -->
                <div class="pc-img-intake mt-2" *ngIf="pcImageSourceOptions.length && pcHasImageHeader">
                  <span class="pc-img-intake__lbl">
                    <i class="pi pi-link"></i> Use a URL from the lead intake
                  </span>
                  <p-select [filter]="true" filterPlaceholder="Search" appendTo="body" [options]="pcImageSourceOptions"
                            optionLabel="label" optionValue="value"
                            [ngModel]="pcSelectedImageIntake"
                            (onChange)="phaseWaConfigForm.headerImageUrl = $event.value"
                            [showClear]="true"
                            (onClear)="phaseWaConfigForm.headerImageUrl = ''"
                            placeholder="Pick an intake field (e.g. an image link)…"
                            styleClass="w-full"></p-select>
                  <small class="text-500">
                    Pulls the image URL from this intake field for each lead when the opening is sent.
                  </small>
                </div>
              </div>

              <!-- V025 — variable binding editor maps each numbered
                   template parameter (the body slots inside your approved
                   template) to a runtime data path. -->
              <div class="field col-12">
                <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest">
                  Variable bindings
                  <i class="pi pi-info-circle pc-info"
                     pTooltip="Bind each numbered template body parameter to a runtime data path. The resolver substitutes the value at send time — e.g. position 1 might map to lead.name, position 2 to lead.phone."
                     tooltipPosition="top"></i>
                </label>

                <div class="pc-varmap" *ngIf="variableBindingKeys.length; else noBindings">
                  <div *ngFor="let k of variableBindingKeys" class="pc-varmap__row">
                    <span class="pc-varmap__token">{{ tokenFor(k) }}</span>
                    <span class="pc-varmap__arrow">→</span>
                    <p-select appendTo="body" [options]="variableSourceOptions"
                              [editable]="true"
                              optionLabel="label" optionValue="value"
                              [filter]="true" filterBy="label,value"
                              [ngModel]="phaseWaConfigForm.variableMapping[k]"
                              (onChange)="setVariableBinding(k, $event.value)"
                              (onClear)="setVariableBinding(k, '')"
                              placeholder="Pick or type a path (lead.name, case.id, …)"
                              styleClass="pc-varmap__select w-full"></p-select>
                    <button pButton icon="pi pi-times"
                            class="p-button-text p-button-sm p-button-danger"
                            (click)="removeVariableBinding(k)"
                            pTooltip="Remove binding"></button>
                  </div>
                </div>

                <ng-template #noBindings>
                  <div class="pc-empty pc-varmap__empty">
                    No variable bindings yet. Add one for each parameter your opening template carries.
                  </div>
                </ng-template>

                <button pButton icon="pi pi-plus" label="Add binding"
                        class="p-button-sm p-button-text pc-varmap__add"
                        (click)="addVariableBinding()"></button>

                <small class="text-500 pc-varmap__hint">
                  <ng-container *ngIf="phaseGeneralForm.isEntryPhase && phaseGeneralForm.intakePattern === 'lead_pushed'">
                    Tip — this is an entry phase with intake fields configured.
                    The dropdown lists those fields first so picking <code>lead.name</code> or
                    <code>lead.phone</code> is one click.
                  </ng-container>
                  <ng-container *ngIf="!(phaseGeneralForm.isEntryPhase &amp;&amp; phaseGeneralForm.intakePattern === 'lead_pushed')">
                    Tip — paths resolve at send time. Use <code>lead.*</code> for lead-row
                    fields, <code>case.*</code> for journey-captured data,
                    <code>system.*</code> for date / company name.
                  </ng-container>
                </small>
              </div>

              <div class="field col-12">
                <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest pc-slots__title">
                  Reminder slots
                  <button pButton icon="pi pi-plus" label="Add slot"
                          class="p-button-sm p-button-text pc-slots__add"
                          (click)="addReminderSlot()"></button>
                </label>

                <div class="pc-slots">
                  <div *ngIf="!phaseWaConfigForm.reminderSlots?.length" class="pc-slots__empty">
                    No reminder slots yet. Add one to follow up when the customer doesn't reply.
                  </div>

                  <div *ngFor="let slot of phaseWaConfigForm.reminderSlots; let i = index"
                       class="pc-slot">
                    <div class="pc-slot__order">#{{ i + 1 }}</div>
                    <div class="pc-slot__fields">
                      <div class="pc-slot__row">
                        <label>Delay</label>
                        <p-inputNumber [(ngModel)]="slot.delayValue" [min]="1"
                                       styleClass="pc-slot__num"></p-inputNumber>
                        <p-select [filter]="true" filterPlaceholder="Search" appendTo="body" [options]="[
                                    {label:'minutes',value:'MINUTES'},
                                    {label:'hours',value:'HOURS'},
                                    {label:'days',value:'DAYS'}]"
                                  [(ngModel)]="slot.delayUnit"
                                  optionLabel="label" optionValue="value" styleClass="pc-slot__unit"></p-select>
                      </div>
                      <div class="pc-slot__row">
                        <label>Tone</label>
                        <p-select [filter]="true" filterPlaceholder="Search" appendTo="body" [options]="[
                                    {label:'Friendly',value:'FRIENDLY'},
                                    {label:'Urgent',value:'URGENT'},
                                    {label:'Final notice',value:'FINAL_NOTICE'}]"
                                  [(ngModel)]="slot.tone"
                                  optionLabel="label" optionValue="value" styleClass="pc-slot__tone"></p-select>
                      </div>
                      <div class="pc-slot__row pc-slot__row--full">
                        <label>Template</label>
                        <p-select appendTo="body" [options]="slotTemplateOptions"
                                  [ngModel]="slot.templateId"
                                  (onChange)="onSlotTemplateChange(slot, $event.value)"
                                  optionLabel="displayLabel" optionValue="id"
                                  [filter]="true" filterBy="displayLabel"
                                  placeholder="Pick a follow-up template"
                                  styleClass="pc-slot__tpl w-full"></p-select>
                      </div>

                      <!-- Sender — derived from the chosen template, the
                           LeadWhatsAppConfig convention (no manual entry). -->
                      <div class="pc-slot__row pc-slot__row--full" *ngIf="slot.templateId">
                        <label>Sender</label>
                        <div class="pc-sender-display pc-sender-display--inline"
                             [class.pc-sender-display--empty]="!slotPhone(slot)">
                          <i class="pi" [class.pi-whatsapp]="slotPhone(slot)"
                                        [class.pi-question-circle]="!slotPhone(slot)"></i>
                          <span>{{ slotPhone(slot) || 'No sender bound to this template' }}</span>
                        </div>
                      </div>

                      <!-- Per-slot variable bindings — same editor as the
                           opening template, seeded from the template body's
                           numbered parameters. -->
                      <div class="pc-slot__row pc-slot__row--vars"
                           *ngIf="slot.templateId && slotVarKeys(slot).length">
                        <label>Variables</label>
                        <div class="pc-slot__varmap">
                          <div *ngFor="let k of slotVarKeys(slot)" class="pc-varmap__row">
                            <span class="pc-varmap__token">{{ tokenFor(k) }}</span>
                            <span class="pc-varmap__arrow">→</span>
                            <p-select appendTo="body" [options]="variableSourceOptions"
                                      [editable]="true"
                                      optionLabel="label" optionValue="value"
                                      [filter]="true" filterBy="label,value"
                                      [ngModel]="slot.variableMapping[k]"
                                      (onChange)="setSlotVarBinding(slot, k, $event.value)"
                                      (onClear)="setSlotVarBinding(slot, k, '')"
                                      placeholder="Pick or type a path"
                                      styleClass="pc-varmap__select w-full"></p-select>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="pc-slot__ctrl">
                      <button pButton icon="pi pi-arrow-up" class="p-button-text p-button-sm"
                              (click)="moveReminderSlot(i, -1)" [disabled]="i === 0"
                              pTooltip="Move up"></button>
                      <button pButton icon="pi pi-arrow-down" class="p-button-text p-button-sm"
                              (click)="moveReminderSlot(i, 1)"
                              [disabled]="i === (phaseWaConfigForm.reminderSlots.length - 1)"
                              pTooltip="Move down"></button>
                      <button pButton icon="pi pi-trash" class="p-button-text p-button-sm p-button-danger"
                              (click)="removeReminderSlot(i)" pTooltip="Remove"></button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- How a lead-pushed reply starts the workflow -->
            <div class="field col-12">
              <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest">
                What starts the workflow?
              </label>
              <p-select [filter]="true" filterPlaceholder="Search" appendTo="body" [options]="triggerModeOptions"
                        [(ngModel)]="phaseWaConfigForm.triggerMode"
                        optionLabel="label" optionValue="value" styleClass="w-full"></p-select>
              <small class="text-500 block mt-2">
                After the opening message on a lead-pushed conversation:
                <strong>Any reply</strong> starts the phase on a "yes/ok" or any button/list tap;
                <strong>A specific button</strong> starts only when that button is tapped —
                other replies are answered by the operation AI persona.
              </small>

              <div *ngIf="phaseWaConfigForm.triggerMode === 'ON_BUTTON'" class="mt-3">
                <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest">
                  Trigger button label
                </label>
                <input pInputText class="w-full"
                       [(ngModel)]="phaseWaConfigForm.triggerButtonText"
                       placeholder="e.g. Get Started" />
                <small class="text-500 block mt-2">
                  Must match a button title on your opening template exactly.
                </small>
              </div>
            </div>

            <!-- Webchat greeting — plain opening line for non-WhatsApp channels -->
            <div class="field col-12">
              <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest">
                Webchat greeting
              </label>
              <textarea rows="2" class="w-full"
                        style="padding:8px 10px;border:1px solid var(--surface-border);border-radius:8px;min-height:64px;font:inherit;"
                        [(ngModel)]="phaseWaConfigForm.greetingText"
                        placeholder="e.g. Thanks for reaching out! An agent will be with you shortly."></textarea>
              <small class="text-500 block mt-2">
                Plain-text opening line sent on phase entry for webchat / in-window channels —
                used instead of a WhatsApp template (e.g. on a manual webchat phase). Leave blank to send nothing.
              </small>
            </div>

            <div class="pc-pane__actions" *ngIf="!loadingPhaseWaConfig">
              <button pButton label="Save Opening" icon="pi pi-check"
                      [loading]="savingPhaseWaConfig"
                      [disabled]="savingPhaseWaConfig || !phaseWaConfigForm.channelConfigId"
                      (click)="savePhaseWaConfig()"></button>
            </div>
          </div>
        </div>

        <!-- ═══════ VOICE CONFIG TAB ═══════ -->
        <div class="pc-pane" *ngIf="phaseConfigActiveTab === 'voice'">
          <div class="js-dialog">
            <div class="pc-pane__title">
              <span>Voice config</span>
              <i class="pi pi-info-circle pc-info"
                 pTooltip="The outbound voice line and call cadence used when this phase activates. The AI voice agent dials the customer using the configured STT/TTS stack."
                 tooltipPosition="right"></i>
            </div>

            <div *ngIf="loadingPhaseWaConfig" class="pc-inline-loading">
              <p-progressSpinner strokeWidth="4" styleClass="settings-spinner"></p-progressSpinner>
              <span>Loading voice config…</span>
            </div>

            <div *ngIf="!loadingPhaseWaConfig" class="formgrid grid">
              <div class="field col-12 md:col-6">
                <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest">
                  Voice line
                  <i class="pi pi-info-circle pc-info"
                     pTooltip="The outbound voice number (CompanyChannelConfig of channel VOICE) the AI agent calls from."
                     tooltipPosition="top"></i>
                </label>
                <p-select [filter]="true" filterPlaceholder="Search" appendTo="body" [options]="voiceChannelConfigOptions"
                          [(ngModel)]="phaseWaConfigForm.channelConfigId"
                          optionLabel="displayLabel" optionValue="id"
                          [showClear]="true"
                          placeholder="Pick a voice line"
                          [emptyMessage]="'No voice lines configured yet.'"
                          styleClass="w-full"></p-select>
              </div>

              <div class="field col-12 md:col-6 flex align-items-end">
                <div class="pc-toggle-row">
                  <p-checkbox [(ngModel)]="phaseWaConfigForm.isActive" [binary]="true" inputId="voiceActive"></p-checkbox>
                  <label for="voiceActive" class="ml-2">Active</label>
                </div>
              </div>

              <div class="field col-12">
                <div class="pc-voice-note">
                  <i class="pi pi-phone"></i>
                  <div>
                    <strong>AI voice agent</strong>
                    <p>Calls use the streaming STT (Deepgram) + TTS (ElevenLabs) stack configured
                       on the server. Call-retry cadence reuses the reminder-slot model and is
                       managed by the voice dispatcher.</p>
                  </div>
                </div>
              </div>
            </div>

            <div class="pc-pane__actions" *ngIf="!loadingPhaseWaConfig">
              <button pButton label="Save Voice config" icon="pi pi-check"
                      [loading]="savingPhaseWaConfig"
                      [disabled]="savingPhaseWaConfig || !phaseWaConfigForm.channelConfigId"
                      (click)="savePhaseWaConfig()"></button>
            </div>
          </div>
        </div>

        <!-- ═══════ EMAIL CONFIG TAB ═══════ -->
        <div class="pc-pane" *ngIf="phaseConfigActiveTab === 'email'">
          <div class="js-dialog">
            <div class="pc-pane__title">
              <span>Email config</span>
              <i class="pi pi-info-circle pc-info"
                 pTooltip="Pick an existing email template and when it sends. Templates and SMTP live under Settings → Email; automations follow the same trigger + delay model."
                 tooltipPosition="right"></i>
            </div>

            <div *ngIf="loadingEmailTemplates || loadingPhaseWaConfig" class="pc-inline-loading">
              <p-progressSpinner strokeWidth="4" styleClass="settings-spinner"></p-progressSpinner>
              <span>Loading email config…</span>
            </div>

            <div *ngIf="!loadingEmailTemplates && !loadingPhaseWaConfig" class="formgrid grid">
              <div class="field col-12 md:col-6">
                <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest">
                  Email template
                  <i class="pi pi-info-circle pc-info"
                     pTooltip="An existing email template (Settings → Email templates). Its subject + blocks are sent when this phase fires."
                     tooltipPosition="top"></i>
                </label>
                <p-select appendTo="body" [options]="emailTemplateOptions"
                          [(ngModel)]="phaseEmailForm.emailTemplateId"
                          optionLabel="displayLabel" optionValue="id"
                          [showClear]="true" [filter]="true" filterBy="displayLabel,name,subject"
                          placeholder="Pick an email template"
                          [emptyMessage]="'No email templates yet — create one under Settings → Email.'"
                          styleClass="w-full"></p-select>
              </div>

              <div class="field col-12 md:col-6 flex align-items-end">
                <div class="pc-toggle-row">
                  <p-checkbox [(ngModel)]="phaseEmailForm.active" [binary]="true" inputId="emailActive"></p-checkbox>
                  <label for="emailActive" class="ml-2">Active</label>
                </div>
              </div>

              <div class="field col-12" *ngIf="selectedEmailTemplate">
                <div class="pc-email-preview">
                  <div class="pc-email-preview__row">
                    <span class="pc-email-preview__k">Subject</span>
                    <span class="pc-email-preview__v">{{ selectedEmailTemplate.subject || '(no subject)' }}</span>
                  </div>
                  <div class="pc-email-preview__row" *ngIf="selectedEmailTemplate.description">
                    <span class="pc-email-preview__k">About</span>
                    <span class="pc-email-preview__v">{{ selectedEmailTemplate.description }}</span>
                  </div>
                  <div class="pc-email-preview__row">
                    <span class="pc-email-preview__k">Status</span>
                    <span class="pc-email-preview__v">{{ selectedEmailTemplate.status }}</span>
                  </div>
                </div>
              </div>

              <div class="field col-12 md:col-6">
                <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest">
                  Send when
                  <i class="pi pi-info-circle pc-info"
                     pTooltip="Which phase event fires the email — same trigger model as lead email automations."
                     tooltipPosition="top"></i>
                </label>
                <p-select [filter]="true" filterPlaceholder="Search" appendTo="body" [options]="emailTriggerOptions"
                          [(ngModel)]="phaseEmailForm.trigger"
                          optionLabel="label" optionValue="value" styleClass="w-full"></p-select>
              </div>

              <div class="field col-12 md:col-6">
                <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest">
                  Delay
                  <i class="pi pi-info-circle pc-info"
                     pTooltip="Wait this long after the trigger before sending. 0 = immediately."
                     tooltipPosition="top"></i>
                </label>
                <div class="pc-delay-row">
                  <p-inputNumber [(ngModel)]="phaseEmailForm.delayValue" [min]="0"
                                 styleClass="pc-delay-num"></p-inputNumber>
                  <p-select [filter]="true" filterPlaceholder="Search" appendTo="body" [options]="emailDelayUnitOptions"
                            [(ngModel)]="phaseEmailForm.delayUnit"
                            optionLabel="label" optionValue="value" styleClass="pc-delay-unit"></p-select>
                </div>
              </div>
            </div>

            <!-- Email sequence (drip) — multi-step email automation attached to this phase.
                 Build sequences under Channels → Email Sequences; here you just pick which one runs. -->
            <div *ngIf="!loadingEmailTemplates && !loadingPhaseWaConfig"
                 style="border-top:1px solid var(--surface-border, #e2e8f0); padding-top:14px; margin-top:10px;">
              <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest">
                Email sequence (drip)
                <i class="pi pi-info-circle pc-info"
                   pTooltip="Optionally run a multi-step email drip when a case enters this phase. Build sequences under Channels → Email Sequences."
                   tooltipPosition="top"></i>
              </label>
              <p-select [filter]="true" filterPlaceholder="Search" appendTo="body" [options]="emailSequenceOptions"
                        [(ngModel)]="phaseEmailSequenceId"
                        optionLabel="label" optionValue="value"
                        [showClear]="true"
                        (onChange)="onPhaseEmailSequenceChange()" (onClear)="onPhaseEmailSequenceChange()"
                        placeholder="No drip — single email only"
                        [emptyMessage]="'No email sequences yet — create one under Channels → Email Sequences.'"
                        styleClass="w-full"></p-select>
              <small class="block mt-2 text-sm text-500">When set, the whole drip fires (each email at its delay) as a case enters this phase. Saved instantly. Requires an email sender under <strong>Channels → Email Config</strong> to actually deliver.</small>
            </div>

            <div class="pc-pane__actions" *ngIf="!loadingEmailTemplates && !loadingPhaseWaConfig">
              <button pButton label="Save Email config" icon="pi pi-check"
                      [loading]="savingPhaseWaConfig"
                      [disabled]="savingPhaseWaConfig || !phaseEmailForm.emailTemplateId"
                      (click)="savePhaseEmailConfig()"></button>
            </div>
          </div>
        </div>

        <!-- ═══════ STATUSES TAB ═══════ -->
        <div class="pc-pane" *ngIf="phaseConfigActiveTab === 'statuses'">
          <div class="js-dialog">
            <div class="pc-pane__title">
              <span>Stages</span>
              <i class="pi pi-info-circle pc-info"
                 pTooltip="Dropdown values an agent can pick for the case's current stage — e.g. Negotiating, Quote Sent, Closed Won. What a stage DOES (auto-advance, agent win/loss, deal win/loss, win %) is set per journey in Operations → Phase flow → Outcomes."
                 tooltipPosition="right"></i>
            </div>

            <div *ngIf="loadingPhaseStatuses" class="pc-inline-loading">
              <p-progressSpinner strokeWidth="4" styleClass="settings-spinner"></p-progressSpinner>
              <span>Loading statuses…</span>
            </div>

            <div *ngIf="!loadingPhaseStatuses">
              <div *ngIf="!phaseStatuses.length" class="pc-empty">
                No stages yet. Add the first one below.
              </div>

              <div *ngIf="phaseStatuses.length" class="pc-statuses">
              <div *ngFor="let s of phaseStatuses" class="pc-status" [style.borderLeftColor]="s.color">
                <div class="pc-status__main">
                  <span class="pc-status__dot" [style.background]="s.color"></span>
                  <span class="pc-status__name">{{ s.name }}</span>
                  <span class="pc-status__flags">
                    <span *ngIf="s.isDefault" class="pc-status__flag pc-status__flag--default"
                          pTooltip="Default status for new cases in this phase">
                      <i class="pi pi-star-fill"></i> Default
                    </span>
                    <!-- Read-only mirrors of what the journey configured. Editing lives in
                         Operations → Phase flow → Outcomes, because the same stage can mean
                         different things in two journeys. Shown here so an admin renaming a
                         stage can see what it currently drives. -->
                    <span *ngIf="s.isTerminal" class="pc-status__flag pc-status__flag--terminal"
                          pTooltip="Setting this stage advances the case to the next phase. Configured in the journey.">
                      <i class="pi pi-forward"></i> Auto-advance
                    </span>
                    <span *ngIf="s.agentOutcome" class="pc-status__flag pc-status__flag--step"
                          pTooltip="Credits the agent for their step. Configured in the journey.">
                      <i class="pi pi-user"></i> Step {{ s.agentOutcome === 'WON' ? 'won' : 'lost' }}
                    </span>
                    <span *ngIf="s.caseOutcome" class="pc-status__flag"
                          [class.pc-status__flag--won]="s.caseOutcome === 'WON'"
                          [class.pc-status__flag--lost]="s.caseOutcome === 'LOST'"
                          pTooltip="Closes the deal — drives forecast and pipeline. Configured in the journey.">
                      <i class="pi" [class.pi-trophy]="s.caseOutcome === 'WON'"
                         [class.pi-times-circle]="s.caseOutcome === 'LOST'"></i>
                      Deal {{ s.caseOutcome === 'WON' ? 'won' : 'lost' }}
                    </span>
                  </span>
                </div>
                <div class="pc-status__actions">
                  <button pButton icon="pi pi-pencil" class="p-button-text p-button-sm"
                          (click)="editPhaseStatus(s)" pTooltip="Edit"></button>
                  <button pButton icon="pi pi-trash" class="p-button-text p-button-sm p-button-danger"
                          [loading]="deletingPhaseStatusId === s.id"
                          [disabled]="deletingPhaseStatusId === s.id"
                          (click)="deletePhaseStatus(s)" pTooltip="Delete"></button>
                </div>
              </div>
            </div>

              <!-- Inline add/edit form -->
              <div class="pc-status-form">
                <div class="pc-status-form__title">
                  {{ phaseStatusForm.id ? 'Edit stage' : 'Add stage' }}
                  <button *ngIf="phaseStatusForm.id" pButton icon="pi pi-times"
                          label="New" class="p-button-text p-button-sm"
                          (click)="resetPhaseStatusForm()"></button>
                </div>
                <div class="formgrid grid">
                  <div class="field col-12 md:col-5">
                    <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest">Name <span class="req">*</span></label>
                    <input pInputText [(ngModel)]="phaseStatusForm.name" class="w-full"
                           placeholder="e.g. Negotiating" />
                  </div>
                  <div class="field col-6 md:col-3">
                    <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest">Color</label>
                    <input type="color" [(ngModel)]="phaseStatusForm.color" class="pc-color-picker" />
                  </div>
                  <div class="field col-6 md:col-2">
                    <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest">Order</label>
                    <p-inputNumber [(ngModel)]="phaseStatusForm.displayOrder"
                                   [showButtons]="true" [min]="1"
                                   styleClass="w-full"></p-inputNumber>
                  </div>
                  <div class="field col-12 md:col-2 pc-status-form__flag-col">
                    <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest">&nbsp;</label>
                    <button pButton label="Save"
                            icon="pi pi-check"
                            class="w-full"
                            [loading]="savingPhaseStatus"
                            [disabled]="savingPhaseStatus || !phaseStatusForm.name?.trim()"
                            (click)="submitPhaseStatusForm()"></button>
                  </div>

                  <div class="field col-12 pc-status-form__flags">
                    <label class="pc-status-form__check">
                      <p-checkbox [(ngModel)]="phaseStatusForm.isDefault" [binary]="true"></p-checkbox>
                      <span>Default for new cases in this phase</span>
                    </label>
                    <span class="pc-status-form__hint">
                      <i class="pi pi-info-circle"></i>
                      What a stage <em>does</em> — auto-advance, agent win/loss, deal win/loss, win % —
                      is set per journey in <strong>Operations → Phase flow → Outcomes</strong>.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ═══════ SOPs TAB ═══════ -->
        <div class="pc-pane" *ngIf="phaseConfigActiveTab === 'sops'">
          <div class="js-dialog">
            <div class="pc-pane__title">
              <span>Direct SOPs</span>
              <i class="pi pi-info-circle pc-info"
                 pTooltip="Tasks that must complete inside this phase. Each SOP can have an SLA. Mandatory SOPs gate phase advancement. Sub-phase SOPs live on the next tab."
                 tooltipPosition="right"></i>
            </div>

            <ng-container *ngIf="phaseConfigStage">
              <div *ngIf="sopsFor(phaseConfigStage.id).length === 0" class="pc-empty">
                No direct SOPs on this phase yet.
              </div>
              <ul class="pc-sop-list">
                <li *ngFor="let sop of sopsFor(phaseConfigStage.id)" class="pc-sop">
                  <div class="pc-sop__main">
                    <span class="pc-sop__name">{{ sop.label }}</span>
                    <span class="pc-sop__mandatory" *ngIf="sop.isMandatory" pTooltip="Required">
                      <i class="pi pi-star-fill"></i> Required
                    </span>
                    <span class="pc-sop__sla" *ngIf="sop.slaMinutes">
                      <i class="pi pi-clock"></i> {{ formatSla(sop.slaMinutes) }}
                    </span>
                  </div>
                  <div class="pc-sop__actions">
                    <button pButton icon="pi pi-pencil" class="p-button-text p-button-sm"
                            (click)="openEditSopDialog(phaseConfigStage, sop, 'stage')" pTooltip="Edit"></button>
                    <button pButton icon="pi pi-trash" class="p-button-text p-button-sm p-button-danger"
                            (click)="deleteSop(phaseConfigStage.id, sop)" pTooltip="Delete"></button>
                  </div>
                </li>
              </ul>
              <div class="pc-pane__actions">
                <button pButton label="Add SOP"
                        icon="pi pi-plus"
                        class="p-button-outlined"
                        (click)="openAddSopDialog(phaseConfigStage, 'stage')"></button>
              </div>
            </ng-container>
          </div>
        </div>

        <!-- ═══════ SUB-PHASES TAB ═══════ -->
        <div class="pc-pane" *ngIf="phaseConfigActiveTab === 'subphases'">
          <div class="js-dialog">
            <div class="pc-pane__title">
              <span>Sub-phases</span>
              <i class="pi pi-info-circle pc-info"
                 pTooltip="Sequential checkpoints within this phase (e.g. New → Active → Qualified → Won). Each can have its own SOPs. The system advances through sub-phases automatically."
                 tooltipPosition="right"></i>
            </div>

            <ng-container *ngIf="phaseConfigStage">
              <div *ngIf="substagesOf(phaseConfigStage.id).length === 0" class="pc-empty">
                No sub-phases yet.
              </div>

              <div *ngIf="substagesOf(phaseConfigStage.id).length > 0" class="pc-subphases">
                <div *ngFor="let sub of substagesOf(phaseConfigStage.id)" class="pc-subphase">
                  <div class="pc-subphase__head">
                    <span class="pc-subphase__name">{{ sub.name }}</span>
                    <span class="pc-subphase__meta">
                      <i class="pi pi-check-square"></i> {{ sopCountFor(sub.id) }} SOPs
                    </span>
                    <div class="pc-subphase__actions">
                      <button pButton icon="pi pi-plus" label="Add SOP"
                              class="p-button-text p-button-sm"
                              (click)="openAddSopDialog(sub, 'substage')"></button>
                      <button pButton icon="pi pi-trash"
                              class="p-button-text p-button-sm p-button-danger"
                              (click)="deleteJourneyStage(sub, 'substage')"
                              pTooltip="Delete sub-phase"></button>
                    </div>
                  </div>
                  <ul class="pc-sop-list pc-sop-list--nested"
                      *ngIf="sopsFor(sub.id).length > 0">
                    <li *ngFor="let sop of sopsFor(sub.id)" class="pc-sop">
                      <div class="pc-sop__main">
                        <span class="pc-sop__name">{{ sop.label }}</span>
                        <span class="pc-sop__mandatory" *ngIf="sop.isMandatory">
                          <i class="pi pi-star-fill"></i> Required
                        </span>
                        <span class="pc-sop__sla" *ngIf="sop.slaMinutes">
                          <i class="pi pi-clock"></i> {{ formatSla(sop.slaMinutes) }}
                        </span>
                      </div>
                      <div class="pc-sop__actions">
                        <button pButton icon="pi pi-pencil"
                                class="p-button-text p-button-sm"
                                (click)="openEditSopDialog(sub, sop, 'substage')" pTooltip="Edit"></button>
                        <button pButton icon="pi pi-trash"
                                class="p-button-text p-button-sm p-button-danger"
                                (click)="deleteSop(sub.id, sop)" pTooltip="Delete"></button>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>

              <div class="pc-pane__actions">
                <button pButton label="Add sub-phase"
                        icon="pi pi-plus"
                        class="p-button-outlined"
                        (click)="openAddSubstageDialog(phaseConfigStage)"></button>
              </div>
            </ng-container>
          </div>
        </div>

        <!-- ═══════ AUDIT TAB ═══════ -->
        <div class="pc-pane" *ngIf="phaseConfigActiveTab === 'audit'">
          <div class="js-dialog">
            <div class="pc-pane__title">
              <span>Audit timeline</span>
              <i class="pi pi-info-circle pc-info"
                 pTooltip="Every change to this phase, its statuses, opening config and SOPs — logged with who, when and what changed."
                 tooltipPosition="right"></i>
            </div>

            <div *ngIf="loadingPhaseAudit" class="pc-inline-loading">
              <p-progressSpinner strokeWidth="4" styleClass="settings-spinner"></p-progressSpinner>
              <span>Loading audit timeline…</span>
            </div>

            <div *ngIf="!loadingPhaseAudit">
              <div *ngIf="!phaseAuditLog.length" class="pc-empty">
                No changes recorded yet.
              </div>
              <ul *ngIf="phaseAuditLog.length" class="pc-audit">
                <li *ngFor="let entry of phaseAuditLog" class="pc-audit__row"
                    [attr.data-action]="entry.action">
                  <div class="pc-audit__when">{{ entry.createdAt | appDate:'datetime' }}</div>
                  <div class="pc-audit__what">
                    <span class="pc-audit__action">{{ entry.action }}</span>
                    <span class="pc-audit__entity">{{ entry.entityType }}</span>
                    <span class="pc-audit__desc">{{ entry.description }}</span>
                  </div>
                  <div class="pc-audit__diff" *ngIf="entry.displayChanges?.length">
                    <div *ngFor="let c of entry.displayChanges" class="pc-audit__change">
                      <span class="pc-audit__field">{{ c.field }}</span>
                      <span class="pc-audit__val pc-audit__val--old" [title]="c.oldFull">{{ c.oldShort }}</span>
                      <i class="pi pi-arrow-right pc-audit__arrow"></i>
                      <span class="pc-audit__val pc-audit__val--new" [title]="c.newFull">{{ c.newShort }}</span>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <ng-template pTemplate="footer">
        <button pButton label="Close" class="p-button-text"
                (click)="showPhaseConfigDialog = false"></button>
      </ng-template>
    </p-dialog>

    <!-- ══════ MAP WORKFLOW DIALOG (legacy — kept for backwards compat,
            no longer surfaced from the stage card) ═════════════════ -->
    <p-dialog [(visible)]="showMapWorkflowDialog" [modal]="true"
              [style]="{ width: '500px' }" [closable]="!savingMapWorkflow">
      <ng-template pTemplate="header">
        <div>
          <div class="text-500 text-xs uppercase tracking-wide">Map workflow to stage</div>
          <div class="font-bold text-900">{{ mappingStageName }}</div>
        </div>
      </ng-template>

      <div class="js-dialog">
        <p class="js-dialog__hint">
          Pick an AI workflow that should run whenever a lead is active in this stage.
          When the lead enters a different stage, its workflow takes over and this one stops.
        </p>

        <div class="field">
          <label>Workflow</label>
          <p-select appendTo="body" [options]="availableWorkflows"
                    [(ngModel)]="mappingSelectedWorkflowId"
                    [filter]="true"
                    optionLabel="flowName"
                    optionValue="id"
                    placeholder="Pick a workflow…"
                    [disabled]="savingMapWorkflow || loadingMapWorkflows"
                    [showClear]="true"
                    styleClass="w-full js-map-select"></p-select>
          <small *ngIf="loadingMapWorkflows" class="text-500">Loading workflows…</small>
          <small *ngIf="!loadingMapWorkflows && !availableWorkflows.length" class="text-500">
            No workflows found. Create one under <strong>Workflows</strong> in the side menu first.
          </small>
        </div>

        <!-- Visual reassurance — shows what the user will save -->
        <div class="js-map-preview" *ngIf="mappingSelectedWorkflowId">
          <i class="pi pi-check-circle"></i>
          <div>
            <div class="js-map-preview__title">Ready to attach</div>
            <div class="js-map-preview__sub">
              Leads entering <strong>{{ mappingStageName }}</strong> will start this workflow.
            </div>
          </div>
        </div>
        <div class="js-map-preview js-map-preview--clear" *ngIf="!mappingSelectedWorkflowId">
          <i class="pi pi-info-circle"></i>
          <div>
            <div class="js-map-preview__title">No workflow</div>
            <div class="js-map-preview__sub">
              Saving without a selection will <strong>unmap</strong> any workflow currently attached.
            </div>
          </div>
        </div>
      </div>

      <ng-template pTemplate="footer">
        <button pButton label="Cancel" class="p-button-text p-button-secondary"
                (click)="showMapWorkflowDialog = false" [disabled]="savingMapWorkflow"></button>
        <button pButton icon="pi pi-check"
                [label]="mappingSelectedWorkflowId ? 'Attach Workflow' : 'Save (Unmapped)'"
                [loading]="savingMapWorkflow"
                [disabled]="savingMapWorkflow"
                (click)="saveMapWorkflow()"></button>
      </ng-template>
    </p-dialog>

    <!-- ══════ MAP DEPARTMENT DIALOG ══════════════════════════ -->
    <p-dialog [(visible)]="showMapDepartmentDialog" [modal]="true"
              [style]="{ width: '500px' }" [closable]="!savingMapDepartment">
      <ng-template pTemplate="header">
        <div>
          <div class="text-500 text-xs uppercase tracking-wide">Map team to stage</div>
          <div class="font-bold text-900">{{ mappingDeptStageName }}</div>
        </div>
      </ng-template>

      <div class="js-dialog">
        <!-- Restricted-stage toggle: worked by MULTIPLE restricted (business-partner /
             distributor) teams with NO automatic assignment. -->
        <div class="field-checkbox flex align-items-center gap-2 mb-3">
          <p-checkbox [(ngModel)]="mappingRestricted" [binary]="true" inputId="mapRestricted"
                      [disabled]="savingMapDepartment"></p-checkbox>
          <label for="mapRestricted" class="m-0 cursor-pointer">
            Restricted stage — business partners create their own leads (no auto-assignment)
          </label>
        </div>

        <!-- NORMAL stage — single owning team (unchanged). -->
        <!-- Normal stages are owned via Configure phase -> General ("Department" +
             "Also handled by"). Keeping a second editor here meant two screens writing the same
             two columns, where saving one could clear what the other had just set. -->
        <ng-container *ngIf="!mappingRestricted">
          <div class="js-map-preview">
            <i class="pi pi-info-circle"></i>
            <div>
              <div class="js-map-preview__title">Set the department in Configure phase</div>
              <div class="js-map-preview__sub">
                Open <strong>{{ mappingDeptStageName }}</strong> from the phase list (the gear
                icon) and use <strong>General &rarr; Department</strong>. Add
                <strong>Also handled by</strong> there to let several teams share the stage &mdash;
                each lead goes to ONE of them and stays private to that team.
              </div>
            </div>
          </div>
        </ng-container>

        <!-- RESTRICTED stage — multiple restricted teams only. -->
        <ng-container *ngIf="mappingRestricted">
          <p class="js-dialog__hint">
            Attach one or more <strong>restricted</strong> (business-partner / distributor) teams.
            Their members create leads here; cases are never auto-assigned or round-robin'd.
          </p>
          <div class="field">
            <label>Restricted teams</label>
            <p-multiSelect [options]="restrictedDepartments"
                           [(ngModel)]="mappingSelectedDepartmentIds"
                           optionLabel="name"
                           optionValue="id"
                           [filter]="true"
                           display="chip"
                           placeholder="Pick restricted teams…"
                           appendTo="body"
                           [disabled]="savingMapDepartment || loadingMapDepartments"
                           styleClass="w-full js-map-select"></p-multiSelect>
            <small *ngIf="loadingMapDepartments" class="text-500">Loading teams…</small>
            <small *ngIf="!loadingMapDepartments && !restrictedDepartments.length" class="text-500">
              No restricted teams yet. Turn on <strong>Restricted</strong> for a team under
              <strong>Teams</strong> first.
            </small>
          </div>
          <div class="js-map-preview" *ngIf="mappingSelectedDepartmentIds.length">
            <i class="pi pi-check-circle"></i>
            <div>
              <div class="js-map-preview__title">
                {{ mappingSelectedDepartmentIds.length }} team{{ mappingSelectedDepartmentIds.length === 1 ? '' : 's' }} attached
              </div>
              <div class="js-map-preview__sub">
                Members of these teams can create leads in <strong>{{ mappingDeptStageName }}</strong>.
                Cases stay with the partner who creates them — no automatic assignment.
              </div>
            </div>
          </div>
        </ng-container>
      </div>

      <ng-template pTemplate="footer">
        <button pButton label="Cancel" class="p-button-text p-button-secondary"
                (click)="showMapDepartmentDialog = false" [disabled]="savingMapDepartment"></button>
        <button pButton icon="pi pi-check"
                [label]="mappingRestricted ? 'Save restricted teams' : 'Save'"
                [loading]="savingMapDepartment"
                [disabled]="savingMapDepartment"
                (click)="saveMapDepartment()"></button>
      </ng-template>
    </p-dialog>

    <!-- ══════════════ USERS ══════════════ -->
    <div *ngIf="activeTab === 'userslist'" class="settings-panel fadein">
      <div class="panel-header">
        <div>
          <h2 class="panel-title">Users</h2>
          <p class="panel-sub">View and manage your workspace users and agents.</p>
        </div>
      </div>

      <!-- Info banner -->
      <div class="info-banner mb-4">
        <div class="info-banner__icon"><i class="pi pi-user"></i></div>
        <div class="info-banner__body">
          <div class="info-banner__title">Workspace Users</div>
          <div class="info-banner__text">
            Users are the agents and admins who have access to this workspace.
            Add users here and then assign them to <strong>Teams</strong> to control which journey phases they handle.
          </div>
          <div class="info-banner__steps">
            <span class="info-step"><i class="pi pi-plus-circle"></i> Add a user</span>
            <i class="pi pi-arrow-right info-step-arrow"></i>
            <span class="info-step"><i class="pi pi-users"></i> Assign to a team</span>
          </div>
        </div>
      </div>

      <!-- Users list -->
      <div class="settings-card">
        <div class="card-header">
          <h5 class="m-0 font-bold text-900">All Users</h5>
          <div class="flex gap-2 align-items-center">
            <button pButton label="CSV Template" icon="pi pi-download"
              class="p-button-sm p-button-outlined p-button-secondary"
              pTooltip="Download a CSV template to fill in" tooltipPosition="bottom"
              (click)="downloadUserTemplate()"></button>
            <button pButton label="Import CSV" icon="pi pi-upload"
              class="p-button-sm p-button-outlined" [loading]="uploadingUsers"
              pTooltip="Bulk-add users from a filled-in CSV" tooltipPosition="bottom"
              (click)="userCsvInput.click()"></button>
            <input #userCsvInput type="file" accept=".csv,text/csv" hidden
              (change)="onUserCsvSelected($event)" />
            <button pButton label="Add User" icon="pi pi-plus" class="p-button-sm"
              (click)="openAddUserDialog()"></button>
          </div>
        </div>
        <p class="text-sm text-500 mx-3 mt-0 mb-3">
          Add users one by one, or <strong>Import CSV</strong> to add many at once — download the template, fill it, and upload.
          Users listed here can be added as members to any <strong>Team</strong>.
        </p>
        <!-- Search — matches name, email, phone, employee code, role and access template. -->
        <div class="settings-search-row">
          <span class="settings-search">
            <i class="pi pi-search"></i>
            <input type="text" pInputText [(ngModel)]="userSearch"
                   placeholder="Search name, email, phone or code…" aria-label="Search users" />
            <button type="button" class="settings-search__clear" *ngIf="userSearch"
                    (click)="clearUserSearch()" aria-label="Clear search"><i class="pi pi-times"></i></button>
          </span>
          <!-- Filters mirror the two non-text columns: Roles and Access Template. -->
          <p-select appendTo="body" [options]="userRoleFilterOptions" [(ngModel)]="userRoleFilter"
                    optionLabel="label" optionValue="value" placeholder="Any role"
                    [showClear]="true" styleClass="settings-filter" aria-label="Filter by role"></p-select>
          <p-select appendTo="body" [options]="userTemplateFilterOptions" [(ngModel)]="userTemplateFilter"
                    optionLabel="label" optionValue="value" placeholder="Any access template"
                    [showClear]="true" [filter]="true" filterBy="label"
                    styleClass="settings-filter" aria-label="Filter by access template"></p-select>
          <button pButton type="button" label="Clear" icon="pi pi-filter-slash"
                  class="p-button-text p-button-sm" *ngIf="userFiltersActive"
                  (click)="clearUserFilters()"></button>
          <span class="settings-search-row__count" *ngIf="!loadingEmployees">
            {{ filteredEmployees.length }} of {{ employees.length }} user{{ employees.length === 1 ? '' : 's' }}
          </span>
        </div>
        <p-table [value]="filteredEmployees" [loading]="loadingEmployees" styleClass="p-datatable-sm">
          <ng-template pTemplate="header">
            <tr>
              <th style="width:140px;">Employee Code</th>
              <th>Name</th>
              <th>Email</th>
              <th style="width:160px;">Phone</th>
              <th style="width:180px;">Roles</th>
              <th style="width:200px;">Access Template</th>
              <th style="width:140px;">Actions</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-u>
            <tr>
              <td><code class="md-code">{{ u.employeeCode || '—' }}</code></td>
              <td><strong>{{ u.name || u.firstName + ' ' + u.lastName }}</strong></td>
              <td>{{ u.email }}</td>
              <td>{{ u.phone || '—' }}</td>
              <td>
                <div class="flex gap-1 flex-wrap">
                  <span *ngFor="let r of userRoleBadges(u)" class="role-badge role-badge--{{ r.tone }}">{{ r.label }}</span>
                  <span *ngIf="!userRoleBadges(u).length" class="text-sm text-500">—</span>
                </div>
              </td>
              <td>
                <span *ngIf="isAdminUser(u)" class="text-sm text-500"
                      pTooltip="Company Admins always have full access" tooltipPosition="top">Full access (admin)</span>
                <span *ngIf="!isAdminUser(u)" class="text-sm"
                      pTooltip="Set the access template from the edit dialog" tooltipPosition="top">
                  {{ userTemplateLabel(u) }}
                </span>
              </td>
              <td>
                <div class="user-actions">
                  <button pButton icon="pi pi-pencil" class="p-button-text p-button-sm"
                          pTooltip="Edit user" tooltipPosition="top" (click)="openEditUserDialog(u)"></button>
                  <button pButton icon="pi pi-eye" class="p-button-text p-button-sm"
                          pTooltip="View / reset password — the stored password is hashed and can't be shown; generate a new one and reveal it"
                          tooltipPosition="top" (click)="openPasswordDialog(u)"></button>
                  <button pButton icon="pi pi-trash" class="p-button-text p-button-sm p-button-danger"
                          pTooltip="Remove user" tooltipPosition="top"
                          [loading]="removingUserId === u.id" [disabled]="removingUserId === u.id"
                          (click)="removeUser(u)"></button>
                </div>
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="7">
              <div class="empty-config">
                <i class="pi" [ngClass]="userFiltersActive ? 'pi-filter-slash' : 'pi-user'"></i>
                <span *ngIf="!userFiltersActive">No users found.</span>
                <span *ngIf="userFiltersActive && userSearch">No users match “{{ userSearch }}”{{ userRoleFilter || userTemplateFilter !== null ? ' with these filters' : '' }}.</span>
                <span *ngIf="userFiltersActive && !userSearch">No users match these filters.</span>
                <button pButton *ngIf="userFiltersActive" icon="pi pi-filter-slash" label="Clear filters"
                        class="p-button-sm p-button-text" (click)="clearUserFilters()"></button>
              </div>
            </td></tr>
          </ng-template>
        </p-table>
      </div>

      <!-- CSV import result -->
      <p-dialog header="Import results" [(visible)]="showUserImportResult" [modal]="true"
                [style]="{ width: '560px' }" styleClass="p-fluid settings-dialog">
        <div *ngIf="userImportResult" class="import-result">
          <div class="import-result__counts">
            <span class="import-chip import-chip--ok"><i class="pi pi-check-circle"></i> {{ userImportResult.created }} added</span>
            <span class="import-chip import-chip--fail" *ngIf="userImportResult.failed">
              <i class="pi pi-times-circle"></i> {{ userImportResult.failed }} failed
            </span>
            <span class="import-chip import-chip--warn" *ngIf="userImportResult.warnings.length">
              <i class="pi pi-exclamation-triangle"></i> {{ userImportResult.warnings.length }} warning(s)
            </span>
          </div>

          <div *ngIf="userImportResult.errors.length" class="import-result__section">
            <div class="import-result__title">Errors</div>
            <ul class="import-result__list import-result__list--err">
              <li *ngFor="let e of userImportResult.errors">{{ e }}</li>
            </ul>
          </div>

          <div *ngIf="userImportResult.warnings.length" class="import-result__section">
            <div class="import-result__title">Warnings</div>
            <ul class="import-result__list import-result__list--warn">
              <li *ngFor="let w of userImportResult.warnings">{{ w }}</li>
            </ul>
          </div>

          <p *ngIf="!userImportResult.errors.length && !userImportResult.warnings.length" class="text-sm text-500 m-0">
            All {{ userImportResult.created }} row(s) imported cleanly.
          </p>
        </div>
        <ng-template pTemplate="footer">
          <button pButton label="Done" icon="pi pi-check" (click)="showUserImportResult = false"></button>
        </ng-template>
      </p-dialog>

      <!-- Change a user's password (admin sets it directly) -->
      <p-dialog header="Change password" [(visible)]="showPasswordDialog" [modal]="true"
                [style]="{ width: '460px' }" styleClass="p-fluid settings-dialog">
        <div class="field" *ngIf="passwordUser">
          <label>New password for <strong>{{ passwordUser.name || passwordUser.email }}</strong></label>
          <div class="pwd-reveal-row">
            <input pInputText [type]="resetPasswordVisible ? 'text' : 'password'" [(ngModel)]="newPassword"
                   placeholder="Enter a new password" autocomplete="new-password" />
            <button pButton type="button" class="p-button-text p-button-sm"
                    [icon]="resetPasswordVisible ? 'pi pi-eye-slash' : 'pi pi-eye'"
                    [pTooltip]="resetPasswordVisible ? 'Hide password' : 'Show password'" tooltipPosition="top"
                    (click)="resetPasswordVisible = !resetPasswordVisible"></button>
            <button pButton type="button" label="Generate" icon="pi pi-refresh"
                    class="p-button-outlined p-button-sm"
                    pTooltip="Generate a strong password" tooltipPosition="top"
                    (click)="generateResetPassword()"></button>
          </div>
          <small class="text-500">
            At least 6 characters. Click the eye to reveal what you typed so you can share it with the user.
            Stored passwords are hashed — the current one can't be shown, only replaced.
          </small>
        </div>
        <ng-template pTemplate="footer">
          <button pButton label="Cancel" class="p-button-text" (click)="showPasswordDialog = false"></button>
          <button pButton label="Update password" icon="pi pi-check" [loading]="savingPassword"
                  [disabled]="savingPassword || !newPassword || newPassword.length < 6" (click)="savePassword()"></button>
        </ng-template>
      </p-dialog>
    </div>

    <!-- ══════════════ TEAMS ══════════════ -->
    <div *ngIf="activeTab === 'users'" class="settings-panel fadein">
      <div class="panel-header">
        <div>
          <h2 class="panel-title">Teams</h2>
          <p class="panel-sub">Create teams, add agents, and assign teams to journey phases.</p>
        </div>
      </div>

      <!-- Info banner -->
      <div class="info-banner mb-4">
        <div class="info-banner__icon"><i class="pi pi-users"></i></div>
        <div class="info-banner__body">
          <div class="info-banner__title">How Teams work</div>
          <div class="info-banner__text">
            Teams group your agents so that the right people handle cases at the right stage of a workflow.
            Each team can be assigned to one or more <strong>Journey Phases</strong>, ensuring cases are automatically
            routed to the correct team as they advance.
          </div>
          <div class="info-banner__steps">
            <span class="info-step"><i class="pi pi-plus-circle"></i> Create a team</span>
            <i class="pi pi-arrow-right info-step-arrow"></i>
            <span class="info-step"><i class="pi pi-user-plus"></i> Add members</span>
            <i class="pi pi-arrow-right info-step-arrow"></i>
            <span class="info-step"><i class="pi pi-tag"></i> Map team to stages</span>
          </div>
        </div>
      </div>

      <!-- Create team form -->
      <div class="settings-card mb-4">
        <div class="card-header">
          <h5 class="m-0 font-bold text-900">Create Team</h5>
          <span class="card-header-hint">Teams can be assigned to one or more journey phases.</span>
        </div>
        <div class="config-form-row">
          <div class="field flex-1">
            <label>Team Name</label>
            <input pInputText [(ngModel)]="teamForm.name" placeholder="e.g. Credit Team" class="w-full" />
          </div>
          <div class="field align-self-end">
            <button pButton label="Create Team" icon="pi pi-plus" class="p-button-sm"
              [loading]="savingTeam" [disabled]="savingTeam || !teamForm.name"
              (click)="createTeam()"></button>
          </div>
        </div>
      </div>

      <!-- Teams list -->
      <div class="settings-card">
        <div class="card-header">
          <h5 class="m-0 font-bold text-900">All Teams</h5>
          <p-progressSpinner *ngIf="loadingTeams" strokeWidth="4" styleClass="settings-spinner"></p-progressSpinner>
        </div>
        <p class="text-sm text-500 mx-3 mt-0 mb-3">Use <strong>Members</strong> to view &amp; add agents to a team.</p>
        <!-- Search by name, plus filters for the two toggle columns below. -->
        <div class="settings-search-row">
          <span class="settings-search">
            <i class="pi pi-search"></i>
            <input type="text" pInputText [(ngModel)]="teamSearch"
                   placeholder="Search team name…" aria-label="Search teams" />
            <button type="button" class="settings-search__clear" *ngIf="teamSearch"
                    (click)="teamSearch = ''" aria-label="Clear search"><i class="pi pi-times"></i></button>
          </span>
          <p-select appendTo="body" [options]="teamCheckInFilterOptions" [(ngModel)]="teamCheckInFilter"
                    optionLabel="label" optionValue="value" placeholder="Any check-in"
                    [showClear]="true" styleClass="settings-filter" aria-label="Filter by check-in"></p-select>
          <p-select appendTo="body" [options]="teamModeFilterOptions" [(ngModel)]="teamModeFilter"
                    optionLabel="label" optionValue="value" placeholder="Any mode"
                    [showClear]="true" styleClass="settings-filter" aria-label="Filter by team mode"></p-select>
          <button pButton type="button" label="Clear" icon="pi pi-filter-slash"
                  class="p-button-text p-button-sm" *ngIf="teamFiltersActive"
                  (click)="clearTeamFilters()"></button>
          <span class="settings-search-row__count" *ngIf="!loadingTeams">
            {{ filteredTeams.length }} of {{ teams.length }} team{{ teams.length === 1 ? '' : 's' }}
          </span>
        </div>
        <p-table [value]="filteredTeams" [loading]="loadingTeams" styleClass="p-datatable-sm">
          <ng-template pTemplate="header">
            <tr>
              <th>Team Name</th>
              <th>Created At</th>
              <th pTooltip="Members of this department can check in / out" tooltipPosition="top">Check-in</th>
              <th pTooltip="Also let this department's managers check in" tooltipPosition="top">Mgr check-in</th>
              <th pTooltip="Business-partner mode: members work cases fully but can't assign; advancing hands the lead on unassigned" tooltipPosition="top">Restricted</th>
              <th>Actions</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-t>
            <tr>
              <td><strong>{{ t.name }}</strong></td>
              <td>{{ t.createdAt | appDate:'date' }}</td>
              <td>
                <p-checkbox [(ngModel)]="t.attendanceEnabled" [binary]="true" (onChange)="saveTeamAttendance(t)"
                            pTooltip="Members of this department can check in / out" tooltipPosition="top"></p-checkbox>
              </td>
              <td>
                <p-checkbox [(ngModel)]="t.managerCheckInEnabled" [binary]="true" (onChange)="saveTeamAttendance(t)"
                            [disabled]="!t.attendanceEnabled"
                            pTooltip="Also let this department's managers check in (needs check-in enabled)" tooltipPosition="top"></p-checkbox>
              </td>
              <td>
                <p-checkbox [(ngModel)]="t.restrictedMode" [binary]="true" (onChange)="saveTeamRestricted(t)"
                            pTooltip="Business partners / distributors: members work cases fully but can't assign, and advancing leaves the next phase unassigned" tooltipPosition="top"></p-checkbox>
              </td>
              <td>
                <div class="flex gap-2">
                  <button pButton icon="pi pi-user-plus" label="Members" class="p-button-sm p-button-outlined"
                    pTooltip="View &amp; add agents to this team" tooltipPosition="top"
                    (click)="openViewMembers(t)"></button>
                  <button pButton icon="pi pi-pencil" class="p-button-sm p-button-text"
                    pTooltip="Rename this team" tooltipPosition="top"
                    (click)="openEditTeam(t)"></button>
                  <button pButton icon="pi pi-trash" class="p-button-sm p-button-text p-button-danger"
                    pTooltip="Delete this team" tooltipPosition="top"
                    [loading]="deletingTeamId === t.id" [disabled]="deletingTeamId === t.id"
                    (click)="deleteTeamConfirm(t)"></button>
                </div>
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="6">
              <div class="empty-config">
                <i class="pi" [ngClass]="teamFiltersActive ? 'pi-filter-slash' : 'pi-users'"></i>
                <span *ngIf="!teamFiltersActive">No teams yet. Create your first team above.</span>
                <span *ngIf="teamFiltersActive">No teams match these filters.</span>
                <button pButton *ngIf="teamFiltersActive" icon="pi pi-filter-slash" label="Clear filters"
                        class="p-button-text p-button-sm" (click)="clearTeamFilters()"></button>
              </div>
            </td></tr>
          </ng-template>
        </p-table>
      </div>

      <!-- Rename a team / department. The toggles stay inline in the table; this covers the
           two fields that had no editor at all, so a typo in a team name previously meant
           deleting the team (and its members + groups + phase mappings) and rebuilding it. -->
      <p-dialog [(visible)]="showEditTeam" [modal]="true" [draggable]="false" [resizable]="false"
                [style]="{ width: '420px', maxWidth: '95vw' }" header="Edit team">
        <div class="p-fluid" *ngIf="editTeamRow">
          <div class="field">
            <label for="editTeamName">Name<span class="text-red-500"> *</span></label>
            <input id="editTeamName" pInputText [(ngModel)]="editTeamName" placeholder="Team name"
                   (keydown.enter)="saveEditTeam()" />
          </div>
          <div class="field">
            <label for="editTeamDesc">Description</label>
            <input id="editTeamDesc" pInputText [(ngModel)]="editTeamDesc" placeholder="Optional" />
          </div>
        </div>
        <ng-template pTemplate="footer">
          <button pButton label="Cancel" class="p-button-text" [disabled]="savingEditTeam"
                  (click)="showEditTeam = false"></button>
          <button pButton label="Save" icon="pi pi-check" [loading]="savingEditTeam"
                  [disabled]="!editTeamName.trim() || savingEditTeam" (click)="saveEditTeam()"></button>
        </ng-template>
      </p-dialog>
    </div>

    <!-- ══════════════ BILLING ══════════════ -->
    <div *ngIf="activeTab === 'billing'" class="settings-panel fadein">
      <div class="panel-header">
        <div>
          <h2 class="panel-title">Billing</h2>
          <p class="panel-sub">Manage your subscription and payment details.</p>
        </div>
      </div>
      <div class="settings-card">
        <div class="card-header">
          <h5 class="m-0 font-bold text-900">Current Plan</h5>
          <button *ngIf="isFreePlan" pButton label="Upgrade Plan" icon="pi pi-crown" class="p-button-warning p-button-sm" (click)="openUpgradeDialog()"></button>
        </div>
        <div class="profile-grid">
          <div class="profile-field"><div class="profile-label">Plan</div><div class="profile-value">{{ subscriptionProfile?.plan || '-' }}</div></div>
          <div class="profile-field"><div class="profile-label">Status</div><div class="profile-value">{{ subscriptionProfile?.status || '-' }}</div></div>
          <div class="profile-field">
            <div class="profile-label">Renewal Date</div>
            <div class="profile-value">{{ subscriptionProfile?.renewalDate ? (subscriptionProfile?.renewalDate | appDate:'datetime') : '-' }}</div>
          </div>
          <div class="profile-field"><div class="profile-label">Trial</div><div class="profile-value">{{ subscriptionProfile?.trial ? 'Yes' : 'No' }}</div></div>
        </div>
      </div>

      <div class="mt-4">
        <app-billing-overview></app-billing-overview>
      </div>
    </div>

    <!-- ══════════════ API KEYS ══════════════ -->
    <div *ngIf="activeTab === 'api'" class="settings-panel fadein">
      <div class="panel-header">
        <div>
          <h2 class="panel-title">API Keys</h2>
          <p class="panel-sub">Manage API credentials for programmatic access.</p>
        </div>
      </div>
      <div class="settings-card">
        <div class="card-header">
          <h5 class="m-0 font-bold text-900">Secret Key</h5>
        </div>
        <div class="secret-key-box">
          <label class="block font-medium mb-2">Webhook Secret Key</label>
          <div class="secret-key-input-wrap">
            <input pInputText class="w-full" [type]="widgetSecretVisible ? 'text' : 'password'"
              [value]="widgetSecretKey || ''" readonly placeholder="No secret key created yet" />
            <button pButton type="button" class="p-button-text p-button-sm"
              [icon]="widgetSecretVisible ? 'pi pi-eye-slash' : 'pi pi-eye'"
              (click)="widgetSecretVisible = !widgetSecretVisible"
              [disabled]="!widgetSecretKey"></button>
          </div>
          <small class="text-500">Format: <code>IR-bot-XXXX-XXXX</code>.</small>
        </div>
      </div>

      <!-- Saved API keys — reused in journey API steps as the X-API-Key header -->
      <div class="settings-card">
        <div class="card-header">
          <h5 class="m-0 font-bold text-900">Your API Keys</h5>
        </div>
        <p class="text-500" style="padding:0 4px 6px;">Save external API keys here (e.g. your ERP key). Pick one from the dropdown when configuring an API step in a journey — it is sent as the <code>X-API-Key</code> header.</p>

        <div style="display:flex; gap:10px; flex-wrap:wrap; align-items:flex-end; padding:0 4px 14px;">
          <div class="form-field" style="flex:1; min-width:180px;">
            <label class="block font-medium mb-2">Name</label>
            <input pInputText class="w-full" [(ngModel)]="newApiKeyName" placeholder="e.g. Milan Auto ERP" />
          </div>
          <div class="form-field" style="flex:2; min-width:220px;">
            <label class="block font-medium mb-2">API Key</label>
            <input pInputText type="password" autocomplete="off" class="w-full" [(ngModel)]="newApiKeyValue" placeholder="Paste the key" />
          </div>
          <button pButton label="Add Key" icon="pi pi-plus" class="p-button-sm"
            [loading]="savingApiKey" [disabled]="savingApiKey" (click)="createApiKey()"></button>
        </div>

        <p-table [value]="apiKeys" [loading]="loadingApiKeys" styleClass="p-datatable-sm">
          <ng-template pTemplate="header">
            <tr><th>Name</th><th>Key</th><th style="width:110px;">Actions</th></tr>
          </ng-template>
          <ng-template pTemplate="body" let-k>
            <tr>
              <td><strong>{{ k.name }}</strong></td>
              <td><code>{{ revealedApiKeyIds.has(k.id) ? k.apiKey : maskApiKey(k.apiKey) }}</code></td>
              <td style="white-space:nowrap;">
                <button pButton [icon]="revealedApiKeyIds.has(k.id) ? 'pi pi-eye-slash' : 'pi pi-eye'"
                        size="small" severity="secondary" [text]="true" pTooltip="Show / hide" tooltipPosition="top"
                        (click)="toggleApiKeyReveal(k.id)"></button>
                <button pButton [icon]="deletingApiKeyId === k.id ? 'pi pi-spin pi-spinner' : 'pi pi-trash'"
                        size="small" severity="danger" [text]="true" pTooltip="Delete" tooltipPosition="top"
                        [disabled]="deletingApiKeyId === k.id" (click)="deleteApiKey(k)"></button>
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="3">
              <div class="empty-config"><i class="pi pi-key"></i>
                <span>No API keys yet. Add one above to use it in journey API steps.</span></div>
            </td></tr>
          </ng-template>
        </p-table>
      </div>
    </div>

    <!-- ══════════════ LEAD SOURCES ══════════════ -->
    <div *ngIf="activeTab === 'sources'" class="settings-panel fadein">

      <div class="panel-header">
        <div>
          <h2 class="panel-title">{{ sourceTypeFilter ? sourceTypeLabels[sourceTypeFilter] : 'Lead Sources' }}</h2>
          <p class="panel-sub">{{ sourceTypeFilter ? 'Sources of this type — pick another type below.'
                                                  : 'Configure integrations that send leads into the platform.' }}</p>
        </div>
      </div>

      <!-- Type chips — replaces the retired per-type Settings submenu deep-links. Each chip
           navigates (?source=) so the deep-link handling (Meta/Google panels) still applies. -->
      <div class="src-typechips">
        <button type="button" class="src-typechip" [class.is-active]="!sourceTypeFilter"
                (click)="setSourceType(null)">All</button>
        <button type="button" class="src-typechip" *ngFor="let t of sourceTypeKeys"
                [class.is-active]="sourceTypeFilter === t"
                (click)="setSourceType(t)">{{ sourceTypeLabels[t] }}</button>
      </div>

      <!-- ══ META PANEL — one tab per connected Facebook Page ══════════════════ -->
      <ng-container *ngIf="sourceTypeFilter === 'meta_ads'; else notMeta">

        <!-- Guided setup. This panel still works exactly as before (and remains the way to edit an
             existing page or paste a token by hand) — but a NEW connection should go through the
             wizard, which shares assets instead of asking for a Page token and also configures
             conversion routing and go-live in the same pass. -->
        <div class="settings-card settings-card--compact mb-3"
             style="display:flex;align-items:center;gap:var(--gh-space-3);flex-wrap:wrap;
                    background:var(--gh-primary-wash);box-shadow:none;">
          <i class="pi pi-sparkles" style="color:var(--gh-primary);font-size:var(--gh-fs-16);"></i>
          <div style="min-width:0;flex:1 1 320px;">
            <div style="font-weight:800;color:var(--gh-text);">Set up Meta the guided way</div>
            <div style="font-size:var(--gh-fs-12);color:var(--gh-text-2);">
              Share your Page, ad account and dataset with GrowHigh — no Page token to paste — then
              map forms, route conversions and go live in four steps.
            </div>
          </div>
          <button pButton type="button" label="Open guided setup" icon="pi pi-arrow-right"
                  iconPos="right" class="p-button-sm" (click)="openMetaSetupWizard()"></button>
        </div>

        <!-- Page tabs -->
        <div class="flex align-items-center gap-2 mb-3" style="flex-wrap:wrap;">
          <button *ngFor="let s of metaSources" pButton type="button" class="p-button-sm"
                  [outlined]="metaPanelSource?.id !== s.id"
                  [label]="getCampaignName(s) || s.sourceId"
                  icon="pi pi-facebook"
                  (click)="selectMetaSource(s)"></button>
          <button pButton type="button" class="p-button-sm" severity="secondary"
                  [outlined]="metaPanelSource !== null"
                  label="Add Page" icon="pi pi-plus"
                  (click)="selectMetaSource(null)"></button>
        </div>

        <!-- ADD PAGE: connection card -->
        <div *ngIf="!metaPanelSource" class="settings-card" style="max-width:560px;">
          <h5 class="m-0 font-bold text-900 mb-1">Connect a Facebook Page</h5>
          <p class="text-sm text-600 mt-0 mb-3">Paste the Page's id and its access token — we verify the pair
            against Facebook and subscribe the Page's leads automatically.</p>
          <div style="display:grid;gap:0.75rem;">
            <div>
              <label class="source-field-label">Name</label>
              <input pInputText [(ngModel)]="metaConn.name" class="w-full source-input"
                     placeholder="e.g. Veda Meta Leads — shown as the SOURCE across the app" />
            </div>
            <div>
              <label class="source-field-label">Facebook Page ID</label>
              <input pInputText [(ngModel)]="metaConn.page_id" class="w-full source-input"
                     placeholder="e.g. 102938475610293" />
            </div>
            <div>
              <label class="source-field-label">Page Access Token</label>
              <div class="flex gap-2">
                <input pInputText type="password" [(ngModel)]="metaConn.token" class="w-full source-input"
                       autocomplete="new-password" placeholder="Enter the Page ID above, then Generate — or paste a token" />
                <button pButton type="button" label="Generate" icon="pi pi-key" size="small"
                        [loading]="metaGeneratingToken"
                        pTooltip="Mint the token from the Page ID using our system user — no curl needed"
                        tooltipPosition="top" (click)="generateMetaPageToken()"></button>
              </div>
              <small class="text-500">Assign the Page to the system user in Business Manager first, then Generate.</small>
            </div>
            <div class="field-checkbox flex align-items-start gap-2">
              <p-checkbox [(ngModel)]="metaConn.test_mode" [binary]="true" inputId="metaConnTest"></p-checkbox>
              <label for="metaConnTest" class="m-0 cursor-pointer">
                Test mode — save leads but don't message anyone
                <small class="block text-500">Leave on while setting up; turn off on the source to go live.</small>
              </label>
            </div>
            <div *ngIf="metaConnError" class="source-field-error"><i class="pi pi-times-circle"></i> {{ metaConnError }}</div>
            <div>
              <button pButton label="Save & Verify" icon="pi pi-check"
                      [loading]="metaConnSaving" (click)="saveMetaConnection()"></button>
            </div>
          </div>
        </div>

        <!-- SELECTED PAGE: verified banner + forms manager -->
        <ng-container *ngIf="metaPanelSource as sel">
          <div class="settings-card settings-card--compact mb-3">
            <div class="flex align-items-center gap-2" style="flex-wrap:wrap;">
              <i class="pi pi-facebook" style="font-size:1.2rem;color:#1877f2;"></i>
              <strong>{{ getCampaignName(sel) || sel.sourceId }}</strong>
              <p-tag *ngIf="metaVerifiedName[sel.id]" severity="success" [rounded]="true"
                     [value]="'Connected: ' + metaVerifiedName[sel.id]"></p-tag>
              <p-tag *ngIf="metaVerifiedName[sel.id] === ''" severity="danger" [rounded]="true"
                     value="Verification failed"></p-tag>
              <span class="sp" style="flex:1"></span>
              <button pButton type="button" label="Re-verify & subscribe" icon="pi pi-sync" size="small"
                      severity="secondary" [outlined]="true" (click)="verifyMetaPanelSource(sel, false)"></button>
              <button pButton type="button" label="Reprocess leads → cases" icon="pi pi-arrow-right-arrow-left"
                      size="small" severity="secondary" [outlined]="true" [loading]="reprocessingCases"
                      pTooltip="Leads stored while in test mode show in Lead Analysis but not on the board — this creates their cases."
                      tooltipPosition="bottom" (click)="reprocessToCases()"></button>
              <button pButton type="button" label="Edit connection" icon="pi pi-pencil" size="small"
                      severity="secondary" [text]="true" (click)="openEditSource(sel)"></button>
            </div>
          </div>

          <div class="settings-card">
            <div class="card-header">
              <h5 class="m-0 font-bold text-900">Lead Forms</h5>
              <div class="flex gap-2">
                <button pButton [label]="metaForms.length ? 'Refresh' : 'Load forms from Meta'"
                        icon="pi pi-refresh" class="p-button-sm" severity="secondary" [outlined]="true"
                        [loading]="metaFormsLoading" (click)="loadMetaForms()"></button>
                <button *ngIf="metaHasImportSelection" pButton label="Import selected" icon="pi pi-download"
                        class="p-button-sm" [loading]="metaImporting" (click)="importSelectedMetaForms()"></button>
              </div>
            </div>
            <p class="text-sm text-600 mt-0 mb-3">
              Import a form, map its fields, and its leads flow in. Leads that arrive for a
              not-imported, unmapped or disabled form are <strong>held safely</strong> — the count shows
              on the form, and <strong>Replay</strong> ingests them once you've fixed it.
            </p>

            <div *ngIf="metaFormsError" class="source-field-error"><i class="pi pi-times-circle"></i> {{ metaFormsError }}</div>

            <!-- ── Ad attribution health ──────────────────────────────────────────────────
                 Campaign / ad set / ad name only arrive when the Page's token can read ads and
                 we're within Meta's per-app quota. When either fails Meta returns the lead and
                 drops those fields with NO error, so the source looks fine while every lead
                 lands unattributed. Shown only once paid leads exist — "0 of 0" would be a
                 false alarm on a source that simply hasn't received anything yet. -->
            <div *ngIf="metaAdCoverage && metaAdCoverage.paidLeads > 0"
                 class="meta-cov" [class.is-bad]="!metaAdCoverage.healthy"
                 [class.is-partial]="metaAdCoverage.healthy && metaAdCoverage.coveragePct < 90">
              <i class="pi" [ngClass]="metaAdCoverage.healthy
                    ? (metaAdCoverage.coveragePct < 90 ? 'pi-exclamation-triangle' : 'pi-check-circle')
                    : 'pi-times-circle'"></i>
              <div class="meta-cov__body">
                <div class="meta-cov__head">
                  Campaign attribution:
                  <b>{{ metaAdCoverage.coveragePct }}%</b>
                  <span class="meta-cov__sub">
                    — {{ metaAdCoverage.withCampaign }} of {{ metaAdCoverage.paidLeads }}
                    paid leads in the last {{ metaAdCoverage.days }} days carry a campaign
                  </span>
                </div>
                <div class="meta-cov__hint" *ngIf="metaAdCoverage.hint">{{ metaAdCoverage.hint }}</div>
                <div class="meta-cov__hint" *ngIf="!metaAdCoverage.hint && metaAdCoverage.coveragePct < 90">
                  Some leads arrived without a campaign. That usually means Meta is rate-limiting
                  ad lookups — they fill in automatically as each ad is resolved and cached.
                </div>
                <!-- Repair the leads that already missed out. Sits inside the warning itself so
                     the fix is where the problem is, rather than hidden in a menu. Shown only
                     when there is genuinely something to repair. -->
                <div class="meta-cov__fix" *ngIf="metaBackfillPreview?.leadsPending > 0">
                  <button pButton type="button" class="p-button-sm" [outlined]="true"
                          icon="pi pi-history"
                          [label]="'Backfill ' + metaBackfillPreview.leadsPending + ' lead(s)'"
                          [loading]="metaBackfillRunning"
                          (click)="runMetaBackfill()"></button>
                  <span class="meta-cov__fixnote">
                    Resolves {{ metaBackfillPreview.adsFound }} ad(s) once and applies the campaign
                    to every lead they brought in. Safe to run more than once.
                  </span>
                </div>
              </div>
            </div>

            <!-- Select-all for import (only shown when there are un-imported forms) -->
            <div *ngIf="metaNotImportedForms.length" class="flex align-items-center gap-2 px-2 py-1 mb-1">
              <p-checkbox [ngModel]="metaAllSelected" (ngModelChange)="toggleAllMetaImport($event)" [binary]="true"
                          inputId="metaSelectAll"></p-checkbox>
              <label for="metaSelectAll" class="text-500 text-sm cursor-pointer m-0">
                Select all {{ metaNotImportedForms.length }} un-imported form(s)
              </label>
            </div>

            <div *ngFor="let form of metaForms"
                 style="border:1px solid var(--surface-border);border-radius:8px;margin-bottom:8px;"
                 [style.opacity]="form.imported && !form.enabled ? 0.6 : 1">
              <div class="flex align-items-center gap-2 p-2">
                <!-- Not imported: checkbox for bulk import. Imported: expand chevron. -->
                <p-checkbox *ngIf="!form.imported" [(ngModel)]="metaImportSelection[form.form_id]"
                            [binary]="true"></p-checkbox>
                <i *ngIf="form.imported" class="pi cursor-pointer"
                   [ngClass]="metaFormOpen?.form_id === form.form_id ? 'pi-chevron-down' : 'pi-chevron-right'"
                   (click)="openMetaForm(form)"></i>
                <span class="font-medium" [class.cursor-pointer]="form.imported"
                      (click)="form.imported && openMetaForm(form)">{{ form.name }}</span>
                <p-tag *ngIf="form.status" [value]="form.status"
                       [severity]="form.status === 'ACTIVE' ? 'success' : 'secondary'" [rounded]="true"></p-tag>
                <span class="sp" style="flex:1"></span>
                <p-tag *ngIf="form.source_name" severity="info" [rounded]="true"
                       [value]="'Source: ' + form.source_name"
                       pTooltip="Leads from this form show this as their source." tooltipPosition="top"></p-tag>
                <p-tag *ngIf="form.pending_leads > 0" severity="warn" [rounded]="true"
                       [value]="form.pending_leads + ' lead(s) waiting'"
                       pTooltip="These leads arrived while the form wasn't ready — map/enable it, then Replay."
                       tooltipPosition="top"></p-tag>
                <p-tag *ngIf="!form.imported" value="Not imported" severity="secondary" [rounded]="true"></p-tag>
                <p-tag *ngIf="form.imported && !form.mapped" value="Not mapped" severity="warn" [rounded]="true"></p-tag>
                <p-tag *ngIf="form.imported && form.mapped && form.enabled" value="Live" severity="success" [rounded]="true"></p-tag>
                <p-tag *ngIf="form.imported && !form.enabled" value="Disabled" severity="danger" [rounded]="true"></p-tag>
                <button *ngIf="form.pending_leads > 0" pButton type="button" label="Replay" icon="pi pi-play"
                        size="small" severity="warn" [outlined]="true"
                        [loading]="metaReplayingId === form.form_id" (click)="replayMetaForm(form)"></button>
                <!-- Backfill historical leads (imported forms only) -->
                <button *ngIf="form.imported" pButton type="button" icon="pi pi-history" size="small"
                        severity="secondary" [text]="true"
                        pTooltip="Import this form's past leads from Meta (submitted before the webhook connected)"
                        tooltipPosition="top" [loading]="metaImportPastId === form.form_id"
                        (click)="importPastMetaLeads(form)"></button>
                <!-- Enable/disable switch (imported forms only) -->
                <button *ngIf="form.imported" pButton type="button" size="small" [text]="true"
                        [icon]="form.enabled ? 'pi pi-pause-circle' : 'pi pi-play-circle'"
                        [severity]="form.enabled ? 'secondary' : 'success'"
                        [pTooltip]="form.enabled ? 'Disable — hold this form\'s leads' : 'Enable this form'"
                        tooltipPosition="top" (click)="toggleMetaFormEnabled(form)"></button>
                <i *ngIf="metaFormLoadingId === form.form_id" class="pi pi-spin pi-spinner"></i>
              </div>

              <!-- Expanded: field mapping + per-form journey -->
              <div *ngIf="metaFormOpen?.form_id === form.form_id" class="p-3"
                   style="border-top:1px solid var(--surface-border);">
                <span class="p-input-icon-left w-full" style="display:block;max-width:340px;margin-bottom:8px;">
                  <i class="pi pi-search"></i>
                  <input pInputText [(ngModel)]="metaFieldSearch" class="w-full source-input"
                         placeholder="Search fields…" />
                </span>
                <table class="gh-table" style="width:100%;border-collapse:collapse;">
                  <tr>
                    <th style="text-align:left;padding:4px 8px;" class="text-500 text-sm">Form question</th>
                    <th style="text-align:left;padding:4px 8px;" class="text-500 text-sm">Maps to</th>
                  </tr>
                  <tr *ngFor="let field of filteredMetaFormFields">
                    <td style="padding:4px 8px;vertical-align:top;max-width:340px;">
                      <div>{{ field.label }}</div>
                      <small class="text-500">{{ field.key }} · {{ field.type }}</small>
                    </td>
                    <td style="padding:4px 8px;">
                      <p-select appendTo="body" [options]="metaFormMappingOptions"
                                [(ngModel)]="metaFormDraft[field.key]"
                                optionLabel="label" optionValue="value"
                                [filter]="true" filterBy="label,value"
                                placeholder="Don't map" styleClass="w-full"></p-select>
                      <small *ngIf="field.suggested && metaFormDraft[field.key] === field.suggested" class="text-500">
                        suggested from question type
                      </small>
                    </td>
                  </tr>
                </table>
                <div class="mt-3" style="max-width:420px;">
                  <label class="source-field-label">Source name for this form's leads</label>
                  <p-select appendTo="body" [options]="metaSourceNameOptions" [(ngModel)]="metaFormSourceNameDraft"
                            [editable]="true" [filter]="true" filterBy="label" optionLabel="label" optionValue="value" [showClear]="true"
                            placeholder="e.g. JNV Class 9 — search, pick or type" styleClass="w-full"></p-select>
                  <small class="text-500">Leads from this form show this as their source (and group under it in
                    reports). Search and pick, or type a new name. Blank = the page's source.</small>
                </div>
                <div class="mt-3" style="max-width:420px;">
                  <label class="source-field-label">Journey for this form's leads</label>
                  <p-select appendTo="body" [options]="operations" [(ngModel)]="metaFormOperationDraft"
                            optionLabel="name" optionValue="id" [showClear]="true"
                            [filter]="true" filterBy="name"
                            placeholder="Use the source's default journey" styleClass="w-full"></p-select>
                  <small class="text-500">Each form can enter its own journey — e.g. one per course or product.</small>
                </div>
                <div class="flex justify-content-end gap-2 mt-2">
                  <button pButton type="button" label="Cancel" severity="secondary" [text]="true"
                          (click)="metaFormOpen = null"></button>
                  <button pButton type="button" label="Save mapping" icon="pi pi-check"
                          [loading]="metaFormSaving" (click)="saveMetaFormMapping()"></button>
                </div>
              </div>
            </div>

            <div *ngIf="!metaFormsLoading && !metaForms.length && !metaFormsError" class="text-500 text-sm">
              Click "Load forms from Meta" to list this Page's lead forms.
            </div>
          </div>
        </ng-container>
      </ng-container>

      <!-- ══ GOOGLE ADS PANEL — discovered forms (no import; Google has no list API) ══ -->
      <ng-template #notMeta>
      <ng-container *ngIf="sourceTypeFilter === 'google_ads'; else genericSources">
        <div class="flex align-items-center gap-2 mb-3" style="flex-wrap:wrap;">
          <button *ngFor="let s of googleSources" pButton type="button" class="p-button-sm"
                  [outlined]="metaPanelSource?.id !== s.id"
                  [label]="getCampaignName(s) || s.sourceId" icon="pi pi-google"
                  (click)="selectGoogleSource(s)"></button>
          <button pButton type="button" class="p-button-sm" severity="secondary"
                  label="Add Google source" icon="pi pi-plus" (click)="startAddGoogleSource()"></button>
        </div>

        <!-- ADD SOURCE: inline connection card (mirrors Meta's Add Page) -->
        <div *ngIf="!metaPanelSource" class="settings-card" style="max-width:560px;">
          <h5 class="m-0 font-bold text-900 mb-1">Add a Google Ads source</h5>
          <p class="text-sm text-600 mt-0 mb-3">Name it and set a webhook key — we generate the Webhook URL to
            paste into Google Ads. Forms then appear here as leads arrive, ready to map.</p>
          <div style="display:grid;gap:0.75rem;">
            <div>
              <label class="source-field-label">Name</label>
              <input pInputText [(ngModel)]="googleConn.name" class="w-full source-input"
                     placeholder="e.g. Veda Google Leads — shown as the SOURCE across the app" />
            </div>
            <div>
              <label class="source-field-label">Webhook Key</label>
              <div class="flex gap-2">
                <input pInputText [(ngModel)]="googleConn.key" class="w-full source-input"
                       placeholder="any secret string — must match Google Ads" />
                <button pButton icon="pi pi-refresh" size="small" severity="secondary" [outlined]="true"
                        pTooltip="Generate a new key" (click)="genGoogleKey()"></button>
              </div>
              <small class="text-500">You'll paste this exact key into the Google Ads webhook config.</small>
            </div>
            <div *ngIf="googleConnError" class="source-field-error"><i class="pi pi-times-circle"></i> {{ googleConnError }}</div>
            <div>
              <button pButton label="Create source" icon="pi pi-check"
                      [loading]="googleConnSaving" (click)="saveGoogleConnection()"></button>
            </div>
          </div>
        </div>

        <ng-container *ngIf="metaPanelSource as sel">
          <div class="flex justify-content-end mb-2">
            <button pButton type="button" label="Reprocess leads → cases" icon="pi pi-arrow-right-arrow-left"
                    size="small" severity="secondary" [outlined]="true" [loading]="reprocessingCases"
                    pTooltip="Leads stored while in test mode show in Lead Analysis but not on the board — this creates their cases."
                    tooltipPosition="bottom" (click)="reprocessToCases()"></button>
          </div>
          <!-- Connection: the two values to paste into Google Ads → Lead form → Data integration -->
          <div class="settings-card mb-3">
            <h5 class="m-0 font-bold text-900 mb-1">Connect in Google Ads</h5>
            <p class="text-sm text-600 mt-0 mb-3">Paste these into your Google Ads Lead Form asset →
              <strong>Data integration → Webhook</strong>. No verification or review needed.</p>
            <div *ngIf="googleCredsLoading" class="text-500 text-sm">Loading connection…</div>
            <div *ngIf="googleCreds" style="display:grid;gap:0.6rem;max-width:640px;">
              <div>
                <label class="source-field-label">Webhook URL</label>
                <div class="flex gap-2">
                  <input pInputText [value]="googleCreds.webhookUrl" readonly class="w-full source-input" />
                  <button pButton icon="pi pi-copy" size="small" severity="secondary" [outlined]="true"
                          (click)="copyText(googleCreds.webhookUrl)"></button>
                </div>
              </div>
              <div>
                <label class="source-field-label">Webhook Key</label>
                <div class="flex gap-2">
                  <input pInputText [value]="googleCreds.googleKey" readonly class="w-full source-input" />
                  <button pButton icon="pi pi-copy" size="small" severity="secondary" [outlined]="true"
                          (click)="copyText(googleCreds.googleKey)"></button>
                </div>
                <small class="text-500">This must match the "Key" you set on the Google Ads webhook.</small>
              </div>
            </div>
          </div>

          <!-- Discovered forms -->
          <div class="settings-card">
            <div class="card-header">
              <h5 class="m-0 font-bold text-900">Lead Forms</h5>
              <button pButton [label]="metaForms.length ? 'Refresh' : 'Load forms'" icon="pi pi-refresh"
                      class="p-button-sm" severity="secondary" [outlined]="true"
                      [loading]="metaFormsLoading" (click)="loadMetaForms()"></button>
            </div>
            <p class="text-sm text-600 mt-0 mb-3">
              Google forms appear here <strong>after their first lead arrives</strong> (Google has no form list).
              Map a form's columns, name its source, pick its journey — leads that came before it was ready are
              <strong>held</strong> and released by <strong>Replay</strong>.
            </p>
            <div *ngIf="metaFormsError" class="source-field-error"><i class="pi pi-times-circle"></i> {{ metaFormsError }}</div>

            <div *ngFor="let form of metaForms"
                 style="border:1px solid var(--surface-border);border-radius:8px;margin-bottom:8px;"
                 [style.opacity]="!form.enabled ? 0.6 : 1">
              <div class="flex align-items-center gap-2 p-2">
                <i class="pi cursor-pointer"
                   [ngClass]="metaFormOpen?.form_id === form.form_id ? 'pi-chevron-down' : 'pi-chevron-right'"
                   (click)="openMetaForm(form)"></i>
                <span class="font-medium cursor-pointer" (click)="openMetaForm(form)">{{ form.name }}</span>
                <span class="sp" style="flex:1"></span>
                <p-tag *ngIf="form.source_name" severity="info" [rounded]="true" [value]="'Source: ' + form.source_name"></p-tag>
                <p-tag *ngIf="form.pending_leads > 0" severity="warn" [rounded]="true"
                       [value]="form.pending_leads + ' lead(s) waiting'"></p-tag>
                <p-tag *ngIf="!form.mapped" value="Not mapped" severity="warn" [rounded]="true"></p-tag>
                <p-tag *ngIf="form.mapped && form.enabled" value="Live" severity="success" [rounded]="true"></p-tag>
                <p-tag *ngIf="!form.enabled" value="Disabled" severity="danger" [rounded]="true"></p-tag>
                <button *ngIf="form.pending_leads > 0" pButton type="button" label="Replay" icon="pi pi-play"
                        size="small" severity="warn" [outlined]="true"
                        [loading]="metaReplayingId === form.form_id" (click)="replayMetaForm(form)"></button>
                <button pButton type="button" size="small" [text]="true"
                        [icon]="form.enabled ? 'pi pi-pause-circle' : 'pi pi-play-circle'"
                        [severity]="form.enabled ? 'secondary' : 'success'"
                        [pTooltip]="form.enabled ? 'Disable — hold this form\'s leads' : 'Enable this form'"
                        tooltipPosition="top" (click)="toggleMetaFormEnabled(form)"></button>
                <i *ngIf="metaFormLoadingId === form.form_id" class="pi pi-spin pi-spinner"></i>
              </div>

              <div *ngIf="metaFormOpen?.form_id === form.form_id" class="p-3" style="border-top:1px solid var(--surface-border);">
                <span class="p-input-icon-left w-full" style="display:block;max-width:340px;margin-bottom:8px;">
                  <i class="pi pi-search"></i>
                  <input pInputText [(ngModel)]="metaFieldSearch" class="w-full source-input" placeholder="Search columns…" />
                </span>
                <table class="gh-table" style="width:100%;border-collapse:collapse;">
                  <tr>
                    <th style="text-align:left;padding:4px 8px;" class="text-500 text-sm">Form column</th>
                    <th style="text-align:left;padding:4px 8px;" class="text-500 text-sm">Maps to</th>
                  </tr>
                  <tr *ngFor="let field of filteredMetaFormFields">
                    <td style="padding:4px 8px;vertical-align:top;max-width:340px;">
                      <div>{{ field.label }}</div>
                      <small class="text-500">{{ field.key }}</small>
                    </td>
                    <td style="padding:4px 8px;">
                      <p-select appendTo="body" [options]="metaFormMappingOptions" [(ngModel)]="metaFormDraft[field.key]"
                                optionLabel="label" optionValue="value" [filter]="true" filterBy="label,value"
                                placeholder="Don't map" styleClass="w-full"></p-select>
                    </td>
                  </tr>
                </table>
                <div class="mt-3" style="max-width:420px;">
                  <label class="source-field-label">Source name for this form's leads</label>
                  <p-select appendTo="body" [options]="metaSourceNameOptions" [(ngModel)]="metaFormSourceNameDraft"
                            [editable]="true" [filter]="true" filterBy="label" optionLabel="label" optionValue="value" [showClear]="true"
                            placeholder="e.g. JNV Class 9 — search, pick or type" styleClass="w-full"></p-select>
                </div>
                <div class="mt-3" style="max-width:420px;">
                  <label class="source-field-label">Journey for this form's leads</label>
                  <p-select appendTo="body" [options]="operations" [(ngModel)]="metaFormOperationDraft"
                            optionLabel="name" optionValue="id" [showClear]="true" [filter]="true" filterBy="name"
                            placeholder="Use the source's default journey" styleClass="w-full"></p-select>
                </div>
                <div class="flex justify-content-end gap-2 mt-2">
                  <button pButton type="button" label="Cancel" severity="secondary" [text]="true" (click)="metaFormOpen = null"></button>
                  <button pButton type="button" label="Save mapping" icon="pi pi-check" [loading]="metaFormSaving" (click)="saveMetaFormMapping()"></button>
                </div>
              </div>
            </div>

            <div *ngIf="!metaFormsLoading && !metaForms.length && !metaFormsError" class="text-500 text-sm">
              No forms discovered yet — they appear here once the first lead from each Google form arrives.
            </div>
          </div>
        </ng-container>
      </ng-container>
      </ng-template>

      <!-- ══ GENERIC SOURCES (all other types, and the unfiltered view) ════════ -->
      <ng-template #genericSources>
      <div class="settings-card">
        <div class="card-header">
          <h5 class="m-0 font-bold text-900">{{ sourceTypeFilter ? (sourceTypeLabels[sourceTypeFilter] + ' Sources') : 'All Sources' }}</h5>
          <button pButton label="Add Source" icon="pi pi-plus" class="p-button-sm"
                  (click)="sourceTypeFilter ? addSourceOfType(sourceTypeFilter) : openAddSource()"></button>
        </div>
        <p-table [value]="filteredSources" [loading]="loadingSources" styleClass="p-datatable-sm">
          <ng-template pTemplate="header">
            <tr>
              <th>Campaign Name</th>
              <th>Type</th>
              <th>Source ID</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-src>
            <tr>
              <td>
                <i [class]="'pi ' + getSourceIcon(src.source)" style="margin-right:8px;color:#64748b;"></i>
                <strong>{{ getCampaignName(src) || '—' }}</strong>
              </td>
              <td><span class="text-600">{{ getSourceLabel(src.source) }}</span></td>
              <td><code class="source-row__id">{{ src.sourceId }}</code></td>
              <td>
                <p-tag [value]="src.isActive ? 'Active' : 'Disabled'"
                       [severity]="src.isActive ? 'success' : 'secondary'" [rounded]="true"></p-tag>
                <!-- Pull health — IndiaMART (and future pull providers). PENDING flips to
                     CONNECTED / INVALID_KEY on the connector's first poll after save. -->
                <p-tag *ngIf="src.source === 'indiamart'"
                       [value]="getPullLabel(src)" [severity]="getPullSeverity(src)" [rounded]="true"
                       [pTooltip]="getPullTooltip(src)" tooltipPosition="top"
                       style="margin-left:6px;"></p-tag>
              </td>
              <td style="white-space:nowrap;">
                <!-- Meta sources have no manual test — Meta pushes to our central webhook, so the
                     honest test is a lead from Meta's Lead Ads Testing tool, not a curl at intake. -->
                <button *ngIf="src.source !== 'meta_ads'"
                        pButton icon="pi pi-play" pTooltip="Run Test" tooltipPosition="top"
                        size="small" severity="info" [text]="true" (click)="openTestDialog(src)"></button>
                <button pButton icon="pi pi-pencil" pTooltip="Edit" tooltipPosition="top"
                        size="small" severity="secondary" [text]="true" (click)="openEditSource(src)"></button>
                <button pButton pTooltip="Regenerate Token" tooltipPosition="top"
                        [icon]="regeneratingId === src.id ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'"
                        size="small" severity="warn" [text]="true"
                        [disabled]="regeneratingId === src.id" (click)="doRegenerateToken(src)"></button>
                <button pButton
                        [icon]="togglingId === src.id ? 'pi pi-spin pi-spinner' : (src.isActive ? 'pi pi-pause-circle' : 'pi pi-play-circle')"
                        [pTooltip]="src.isActive ? 'Disable' : 'Enable'" tooltipPosition="top"
                        size="small" [severity]="src.isActive ? 'secondary' : 'success'" [text]="true"
                        [disabled]="togglingId === src.id" (click)="toggleSource(src)"></button>
                <button *ngIf="src.isActive" pButton icon="pi pi-trash" pTooltip="Delete" tooltipPosition="top"
                        size="small" severity="danger" [text]="true"
                        [disabled]="deletingId === src.id" (click)="deleteSource(src)"></button>
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="5">
              <div class="empty-config">
                <i class="pi pi-link"></i>
                <span>No sources connected yet. Click <strong>Add Source</strong> to set up your first lead integration.</span>
              </div>
            </td></tr>
          </ng-template>
        </p-table>
      </div>
      </ng-template><!-- /genericSources -->

    </div><!-- /sources panel -->

    <!-- ══════════════ EVENTS SETUP ══════════════ -->
    <div *ngIf="activeTab === 'events'" class="settings-panel fadein">

      <!-- Header -->
      <div class="panel-header">
        <div>
          <h2 class="panel-title">Events Setup</h2>
          <p class="panel-sub">Send behavioural and transactional events to enrich lead timelines and trigger automations.</p>
        </div>
      </div>

      <!-- Section A: Intro banner -->
      <div class="event-banner">
        <div class="event-banner__icon"><i class="pi pi-bolt"></i></div>
        <div>
          <div class="event-banner__title">Track every customer touchpoint in real time</div>
          <div class="event-banner__sub">Send a single POST request when anything meaningful happens — a cart abandoned, a form submitted, an appointment booked. Syntero enriches the lead timeline and triggers automation rules automatically.</div>
        </div>
      </div>

      <!-- Section B: Supported Event Types -->
      <div class="settings-card mb-4">
        <div class="settings-card__title">Supported Event Types</div>
        <div class="event-groups">
          <div *ngFor="let grp of eventTypeGroups" class="event-group">
            <div class="event-group__label">{{ grp.label }}</div>
            <div class="event-group__chips">
              <span *ngFor="let ev of grp.items" class="event-chip">{{ ev.label }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Section C: Test Event Simulator -->
      <div class="settings-card mb-4">
        <div class="settings-card__title">Event Simulator</div>
        <div class="settings-card__sub">Build and fire a test event to verify your integration without touching live data.</div>

        <div class="event-sim-grid">

          <!-- Mode toggle -->
          <div class="event-sim-row">
            <label class="source-field-label">Mode</label>
            <div class="event-mode-toggle">
              <button class="event-mode-btn" [class.active]="eventMode === 'test'" (click)="eventMode = 'test'">
                <i class="pi pi-flask"></i> Test
              </button>
              <button class="event-mode-btn" disabled title="Live mode coming soon">
                <i class="pi pi-circle-fill"></i> Live
              </button>
            </div>
            <span *ngIf="eventMode === 'test'" class="event-mode-badge test">No data will be persisted</span>
            <span *ngIf="eventMode === 'live'" class="event-mode-badge live">Real event — will trigger automations</span>
          </div>

          <!-- Phone -->
          <div class="event-sim-row">
            <label class="source-field-label">Lead Phone <span class="req-star">*</span></label>
            <input pInputText class="source-input" placeholder="+919000000001" [(ngModel)]="eventPhone" />
          </div>

          <!-- Event Type -->
          <div class="event-sim-row">
            <label class="source-field-label">Event Type <span class="req-star">*</span></label>
            <p-select appendTo="body" [filter]="true" filterPlaceholder="Search"
              [options]="eventTypeGroups"
              [(ngModel)]="selectedEventType"
              optionLabel="label"
              optionValue="value"
              [group]="true"
              groupLabel="label"
              groupItems="items"
              placeholder="Select an event type"
              class="w-full"
              (onChange)="onEventTypeChange()">
            </p-select>
          </div>

          <!-- Dynamic fields -->
          <ng-container *ngFor="let f of currentEventFields">
            <div class="event-sim-row">
              <label class="source-field-label">{{ f.label }}</label>
              <input pInputText class="source-input" [placeholder]="f.placeholder"
                     [(ngModel)]="eventDynamicValues[f.key]" />
            </div>
          </ng-container>

          <!-- Idempotency key -->
          <div class="event-sim-row">
            <label class="source-field-label">Idempotency Key <span class="source-field-hint">(optional)</span></label>
            <input pInputText class="source-input" placeholder="Leave blank to auto-generate" [(ngModel)]="idempotencyKey" />
          </div>

          <!-- Send button -->
          <div class="event-sim-row">
            <label class="source-field-label"></label>
            <button pButton
              [label]="sendingEvent ? 'Sending…' : 'Send Event'"
              icon="pi pi-send"
              [loading]="sendingEvent"
              [disabled]="!selectedEventType || !eventPhone || sendingEvent"
              (click)="sendEvent()">
            </button>
          </div>

          <!-- Result -->
          <div *ngIf="eventSendResult" class="event-sim-row">
            <label class="source-field-label"></label>
            <div class="event-result" [class.success]="eventSendResult.success" [class.error]="!eventSendResult.success">
              <i [class]="'pi ' + (eventSendResult.success ? 'pi-check-circle' : 'pi-times-circle')"></i>
              {{ eventSendResult.message }}
              <pre *ngIf="eventSendResult.data" class="event-result-json">{{ eventSendResult.data | json }}</pre>
            </div>
          </div>

        </div>
      </div>

      <!-- Section D: Quick Test Buttons -->
      <div class="settings-card mb-4">
        <div class="settings-card__title">Quick Tests</div>
        <div class="settings-card__sub">Fire pre-filled common events instantly to verify your setup end-to-end.</div>
        <div class="quick-test-row">
          <button pButton severity="secondary" label="Cart Abandoned" icon="pi pi-shopping-cart"
            (click)="quickTestEvent('cart_abandoned', { cart_value: 2499, product_name: 'Pro Plan', currency: 'INR' })">
          </button>
          <button pButton severity="secondary" label="Page Visited" icon="pi pi-eye"
            (click)="quickTestEvent('page_visited', { page_url: 'https://yoursite.com/pricing', page_title: 'Pricing' })">
          </button>
          <button pButton severity="secondary" label="Purchase Completed" icon="pi pi-check"
            (click)="quickTestEvent('purchase_completed', { order_id: 'ORD-DEMO-001', amount: 4999, currency: 'INR' })">
          </button>
          <button pButton severity="secondary" label="Appointment Booked" icon="pi pi-calendar"
            (click)="quickTestEvent('appointment_booked', { appointment_date: '2025-06-15', appointment_time: '10:00 AM', service_name: 'Demo Call' })">
          </button>
        </div>
      </div>

      <!-- Section E: API Integration Block -->
      <div class="settings-card mb-4">
        <div class="settings-card__title">API Integration</div>
        <div class="settings-card__sub">Use this endpoint in your backend, CRM, or Zapier workflow.</div>

        <!-- Endpoint -->
        <div class="event-api-row">
          <label class="source-field-label">Endpoint</label>
          <div class="cred-field">
            <code class="cred-field__value">{{ eventApiEndpoint }}</code>
            <button pButton icon="pi pi-copy" [text]="true" severity="secondary" pTooltip="Copy URL"
              (click)="copyEventField(eventApiEndpoint, 'endpoint')">
            </button>
            <span *ngIf="copiedEventField === 'endpoint'" class="copy-flash">Copied!</span>
          </div>
        </div>

        <!-- Required headers -->
        <div class="event-api-row mt-3">
          <label class="source-field-label">Required Headers</label>
          <div class="event-header-list">
            <div class="event-header-item"><code>Authorization</code><span>Bearer &lt;your_auth_token&gt;</span></div>
            <div class="event-header-item"><code>Content-Type</code><span>application/json</span></div>
            <div class="event-header-item"><code>Idempotency-Key</code><span>Unique string to prevent duplicate events</span></div>
          </div>
        </div>

        <!-- cURL -->
        <div class="event-api-row mt-3">
          <label class="source-field-label">cURL Example</label>
          <div class="event-curl-wrap">
            <pre class="event-curl">{{ eventCurlSnippet }}</pre>
            <button pButton icon="pi pi-copy" [text]="true" severity="secondary" pTooltip="Copy cURL"
              class="event-curl-copy" (click)="copyEventField(eventCurlSnippet, 'curl')">
            </button>
            <span *ngIf="copiedEventField === 'curl'" class="copy-flash curl-flash">Copied!</span>
          </div>
        </div>

      </div>

      <!-- Section F: How It Works -->
      <div class="settings-card">
        <div class="settings-card__title">How It Works</div>
        <div class="event-steps">
          <div class="event-step">
            <div class="event-step__num">1</div>
            <div>
              <div class="event-step__title">Identify the lead</div>
              <div class="event-step__desc">Pass the lead's phone number. Syntero automatically matches it to an existing lead in your workspace.</div>
            </div>
          </div>
          <div class="event-step">
            <div class="event-step__num">2</div>
            <div>
              <div class="event-step__title">Pick an event type</div>
              <div class="event-step__desc">Choose from 16 standard event types covering lead lifecycle, commerce, communication, and appointment actions.</div>
            </div>
          </div>
          <div class="event-step">
            <div class="event-step__num">3</div>
            <div>
              <div class="event-step__title">Include event data</div>
              <div class="event-step__desc">Add any relevant metadata in the <code>event_data</code> object — cart value, order ID, page URL, or custom fields.</div>
            </div>
          </div>
          <div class="event-step">
            <div class="event-step__num">4</div>
            <div>
              <div class="event-step__title">Timeline updates instantly</div>
              <div class="event-step__desc">The event is appended to the lead's journey timeline and any matching automation rules are triggered in real time.</div>
            </div>
          </div>
        </div>
      </div>

    </div><!-- /events panel -->

    <!-- ══════════════ MASTER DATA ══════════════ -->
    <div *ngIf="activeTab === 'master-data'" class="settings-panel fadein">

      <div class="panel-header" style="display:flex;align-items:flex-start;justify-content:space-between;gap:1rem;flex-wrap:wrap;">
        <div>
          <h2 class="panel-title">Master Data</h2>
          <p class="panel-sub">Manage the lookup lists that drive dropdowns across the platform.</p>
        </div>
        <div class="flex gap-2">
          <button *ngIf="masterDataTab === 'SOURCE_NAME'" pButton label="Import from leads" icon="pi pi-download"
                  severity="secondary" [outlined]="true" [loading]="importingSourceNames"
                  pTooltip="Create Source Names from the distinct sources already on your leads"
                  tooltipPosition="bottom" (click)="importSourceNamesFromLeads()"></button>
          <button pButton label="Add Entry" icon="pi pi-plus" (click)="openAddMasterData()"></button>
        </div>
      </div>

      <!-- Category sub-tabs (pill style) -->
      <div class="md-subtabs">
        <button *ngFor="let cat of masterDataCategories"
                type="button"
                class="md-subtab"
                [class.md-subtab--active]="masterDataTab === cat.key"
                (click)="selectMasterDataTab(cat.key)">
          <i class="pi" [ngClass]="cat.icon.replace('pi pi-','pi-')"></i>
          <span class="md-subtab__label">{{ cat.label }}</span>
          <span class="md-subtab__count">{{ masterDataRows[cat.key]?.length || 0 }}</span>
        </button>
      </div>

      <!-- Category hint banner -->
      <div class="md-hint-banner">
        <i class="pi pi-info-circle"></i>
        <div>
          <div class="md-hint-banner__title">{{ currentMasterDataCategoryLabel }}</div>
          <ng-container *ngFor="let cat of masterDataCategories">
            <div *ngIf="masterDataTab === cat.key" class="md-hint-banner__sub">{{ cat.hint }}</div>
          </ng-container>
        </div>
      </div>

      <!-- Loading -->
      <div *ngIf="currentMasterDataLoading" class="settings-loading">
        <p-progressSpinner strokeWidth="4" styleClass="settings-spinner"></p-progressSpinner>
        <span>Loading entries…</span>
      </div>

      <!-- Empty state -->
      <div *ngIf="!currentMasterDataLoading && currentMasterDataRows.length === 0" class="md-empty">
        <div class="md-empty__icon"><i class="pi pi-database"></i></div>
        <div class="md-empty__title">No entries yet</div>
        <div class="md-empty__sub">
          Click <strong>Add Entry</strong> to create your first {{ currentMasterDataCategoryLabel | lowercase }} value.
        </div>
      </div>

      <!-- Table -->
      <div *ngIf="!currentMasterDataLoading && currentMasterDataRows.length > 0" class="md-table-card">
        <p-table [value]="currentMasterDataRows" styleClass="p-datatable-sm md-table">
          <ng-template pTemplate="header">
            <tr>
              <th style="width:72px;">Order</th>
              <th style="width:200px;">Code</th>
              <th>Label</th>
              <th>Description</th>
              <th style="width:120px;">Status</th>
              <th style="width:110px;">Source</th>
              <th style="width:150px;text-align:right;">Actions</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td><span class="md-order-pill">{{ row.displayOrder }}</span></td>
              <td><code class="md-code">{{ row.code }}</code></td>
              <td>
                <span class="md-label-text">{{ row.label }}</span>
                <span *ngIf="masterDataTab === 'SYSTEM_FIELD'" class="md-type-chip">{{ systemFieldTypeBadge(row) }}</span>
              </td>
              <td class="md-desc">{{ row.description || '—' }}</td>
              <td>
                <span class="md-status-chip" [class.md-status-chip--active]="row.isActive">
                  <i class="pi" [ngClass]="row.isActive ? 'pi-circle-fill' : 'pi-circle'"></i>
                  {{ row.isActive ? 'Active' : 'Disabled' }}
                </span>
              </td>
              <td>
                <span class="md-source-chip" [class.md-source-chip--system]="row.isSystem">
                  {{ row.isSystem ? 'System' : 'Custom' }}
                </span>
              </td>
              <td class="md-actions">
                <button pButton icon="pi pi-pencil" pTooltip="Edit" tooltipPosition="top"
                        size="small" severity="secondary" [text]="true" [rounded]="true"
                        (click)="openEditMasterData(row)"></button>
                <button pButton
                        [icon]="row.isActive ? 'pi pi-pause-circle' : 'pi pi-play-circle'"
                        [pTooltip]="row.isActive ? 'Disable' : 'Enable'" tooltipPosition="top"
                        size="small" [severity]="row.isActive ? 'secondary' : 'success'"
                        [text]="true" [rounded]="true"
                        (click)="toggleMasterDataActive(row)"></button>
                <button *ngIf="!row.isSystem" pButton icon="pi pi-trash" pTooltip="Delete" tooltipPosition="top"
                        size="small" severity="danger" [text]="true" [rounded]="true"
                        [disabled]="deletingMasterDataId === row.id"
                        (click)="deleteMasterData(row)"></button>
              </td>
            </tr>
          </ng-template>
        </p-table>
      </div>

    </div><!-- /master-data panel -->

    <!-- ══════════════ BOOKING CONFIG ══════════════ -->
    <div *ngIf="activeTab === 'booking'" class="settings-panel fadein">
      <app-booking-config></app-booking-config>
    </div><!-- /booking panel -->

    <div *ngIf="activeTab === 'activity-types'" class="settings-panel fadein">
      <app-activity-types></app-activity-types>
    </div><!-- /activity-types panel -->

    <div *ngIf="activeTab === 'proposals'" class="settings-panel fadein">
      <app-proposal-templates></app-proposal-templates>
    </div><!-- /proposal templates panel -->

    <div *ngIf="activeTab === 'numbering'" class="settings-panel fadein">
      <app-number-rules></app-number-rules>
    </div><!-- /numbering panel -->

    <div *ngIf="activeTab === 'automation'" class="settings-panel fadein">
      <app-pro-automation></app-pro-automation>
    </div><!-- /pro automation panel (Pro mode) -->

    <!-- ═══════ FOLLOW-UP REMINDERS TAB (Pro mode) ═══════
         Per-status reminder cadence: how often the user is nudged to follow up
         with a lead while its case sits on a status, and when to stop. -->
    <div *ngIf="activeTab === 'reminders'" class="settings-panel fadein">
      <app-reminder-cadence></app-reminder-cadence>
    </div><!-- /follow-up reminders panel (Pro mode) -->

    <!-- ═══════ STATUSES TAB (Pro mode) ═══════
         Pro companies have a single hidden Default stage, so its statuses get
         this top-level tab instead of the per-phase Workflow-Stages dialog. Reuses
         the exact same inline editor (phaseConfigStage + phaseStatuses). -->
    <div *ngIf="activeTab === 'statuses'" class="settings-panel fadein">
      <div class="js-phase-config-dialog">
      <div class="pc-pane">
        <div class="js-dialog">
          <div class="pc-pane__title">
            <span>Stages</span>
            <i class="pi pi-info-circle pc-info"
               pTooltip="Dropdown values you can pick in a case to label its current business state — e.g. Negotiating, Quote Sent, Closed Won. Terminal statuses let the case close."
               tooltipPosition="right"></i>
          </div>

          <div *ngIf="loadingPhaseStatuses" class="pc-inline-loading">
            <p-progressSpinner strokeWidth="4" styleClass="settings-spinner"></p-progressSpinner>
            <span>Loading statuses…</span>
          </div>

          <div *ngIf="!loadingPhaseStatuses && !phaseConfigStage" class="pc-empty">
            No default stage found yet. Turn on Pro mode for this company (Super Admin → Companies) and reload.
          </div>

          <div *ngIf="!loadingPhaseStatuses && phaseConfigStage">
            <div *ngIf="!phaseStatuses.length" class="pc-empty">
              No stages yet. Add the first one below.
            </div>

            <div *ngIf="phaseStatuses.length" class="pc-statuses">
              <div *ngFor="let s of phaseStatuses" class="pc-status" [style.borderLeftColor]="s.color">
                <div class="pc-status__main">
                  <span class="pc-status__dot" [style.background]="s.color"></span>
                  <span class="pc-status__name">{{ s.name }}</span>
                  <span class="pc-status__flags">
                    <span *ngIf="s.isDefault" class="pc-status__flag pc-status__flag--default"
                          pTooltip="Default status for new cases">
                      <i class="pi pi-star-fill"></i> Default
                    </span>
                    <!-- Read-only mirrors — see the Pro-mode list above. -->
                    <span *ngIf="s.isTerminal" class="pc-status__flag pc-status__flag--terminal"
                          pTooltip="Setting this stage advances the case to the next phase. Configured in the journey.">
                      <i class="pi pi-forward"></i> Auto-advance
                    </span>
                    <span *ngIf="s.agentOutcome" class="pc-status__flag pc-status__flag--step"
                          pTooltip="Credits the agent for their step. Configured in the journey.">
                      <i class="pi pi-user"></i> Step {{ s.agentOutcome === 'WON' ? 'won' : 'lost' }}
                    </span>
                    <span *ngIf="s.caseOutcome" class="pc-status__flag"
                          [class.pc-status__flag--won]="s.caseOutcome === 'WON'"
                          [class.pc-status__flag--lost]="s.caseOutcome === 'LOST'"
                          pTooltip="Closes the deal — drives forecast and pipeline. Configured in the journey.">
                      <i class="pi" [class.pi-trophy]="s.caseOutcome === 'WON'"
                         [class.pi-times-circle]="s.caseOutcome === 'LOST'"></i>
                      Deal {{ s.caseOutcome === 'WON' ? 'won' : 'lost' }}
                    </span>
                  </span>
                </div>
                <div class="pc-status__actions">
                  <button pButton icon="pi pi-pencil" class="p-button-text p-button-sm"
                          (click)="editPhaseStatus(s)" pTooltip="Edit"></button>
                  <button pButton icon="pi pi-trash" class="p-button-text p-button-sm p-button-danger"
                          [loading]="deletingPhaseStatusId === s.id"
                          [disabled]="deletingPhaseStatusId === s.id"
                          (click)="deletePhaseStatus(s)" pTooltip="Delete"></button>
                </div>
              </div>
            </div>

            <!-- Inline add/edit form -->
            <div class="pc-status-form">
              <div class="pc-status-form__title">
                {{ phaseStatusForm.id ? 'Edit stage' : 'Add stage' }}
                <button *ngIf="phaseStatusForm.id" pButton icon="pi pi-times"
                        label="New" class="p-button-text p-button-sm"
                        (click)="resetPhaseStatusForm()"></button>
              </div>
              <div class="formgrid grid">
                <div class="field col-12 md:col-5">
                  <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest">Name <span class="req">*</span></label>
                  <input pInputText [(ngModel)]="phaseStatusForm.name" class="w-full"
                         placeholder="e.g. Negotiating" />
                </div>
                <div class="field col-6 md:col-3">
                  <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest">Color</label>
                  <input type="color" [(ngModel)]="phaseStatusForm.color" class="pc-color-picker" />
                </div>
                <div class="field col-6 md:col-2">
                  <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest">Order</label>
                  <p-inputNumber [(ngModel)]="phaseStatusForm.displayOrder"
                                 [showButtons]="true" [min]="1"
                                 styleClass="w-full"></p-inputNumber>
                </div>
                <div class="field col-12 md:col-2 pc-status-form__flag-col">
                  <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest">&nbsp;</label>
                  <button pButton label="Save"
                          icon="pi pi-check"
                          class="w-full"
                          [loading]="savingPhaseStatus"
                          [disabled]="savingPhaseStatus || !phaseStatusForm.name?.trim()"
                          (click)="submitPhaseStatusForm()"></button>
                </div>

                <div class="field col-12 pc-status-form__flags">
                  <label class="pc-status-form__check">
                    <p-checkbox [(ngModel)]="phaseStatusForm.isDefault" [binary]="true"></p-checkbox>
                    <span>Default for new cases</span>
                  </label>
                  <span class="pc-status-form__hint">
                    <i class="pi pi-info-circle"></i>
                    What a stage <em>does</em> — auto-advance, agent win/loss, deal win/loss, win % —
                    is set per journey in <strong>Operations → Phase flow → Outcomes</strong>.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div><!-- /statuses panel (Pro) -->

  </div><!-- /settings-content -->
</div><!-- /settings-page -->

<!-- ══════════════ MASTER DATA DIALOG ══════════════ -->
<p-dialog [(visible)]="showMasterDataDialog" [modal]="true" [draggable]="false" [resizable]="false"
          [style]="{ width: '560px' }" appendTo="body"
          [closable]="!savingMasterData" [closeOnEscape]="!savingMasterData"
          styleClass="master-data-dialog">

  <ng-template pTemplate="header">
    <div class="md-dialog-header">
      <div class="md-dialog-header__icon">
        <i class="pi" [ngClass]="masterDataTab === 'LEAD_STATUS' ? 'pi-flag' : 'pi-tags'"></i>
      </div>
      <div>
        <div class="md-dialog-header__title">
          {{ editingMasterData ? 'Edit' : 'Add' }} {{ currentMasterDataCategoryLabel }}
        </div>
        <div class="md-dialog-header__sub">
          {{ editingMasterData
              ? 'Update the values used across the platform for this entry.'
              : 'Define a new value for the ' + (currentMasterDataCategoryLabel | lowercase) + ' lookup list.' }}
        </div>
      </div>
    </div>
  </ng-template>

  <div class="md-form-body">

    <div class="md-field">
      <label class="md-label">Code <span class="req">*</span></label>
      <div class="md-input-row" [class.is-invalid]="masterDataFormError && !masterDataForm.code?.trim()">
        <i class="pi pi-hashtag"></i>
        <input pInputText
               [ngModel]="masterDataForm.code"
               (ngModelChange)="onMasterDataCodeInput($event)"
               [disabled]="!!editingMasterData || savingMasterData"
               [placeholder]="masterDataTab === 'LEAD_STATUS' ? 'NEW' : 'phone'" />
      </div>
      <small class="md-hint">
        <i class="pi pi-info-circle"></i>
        Unique identifier used by integrations. Cannot be changed after creation.
      </small>
    </div>

    <div class="md-field">
      <label class="md-label">Label <span class="req">*</span></label>
      <div class="md-input-row" [class.is-invalid]="masterDataFormError && !masterDataForm.label?.trim()">
        <i class="pi pi-pencil"></i>
        <input pInputText [(ngModel)]="masterDataForm.label"
               [disabled]="savingMasterData"
               [placeholder]="masterDataTab === 'LEAD_STATUS' ? 'New' : 'Phone'" />
      </div>
      <small class="md-hint">Display text shown in dropdowns.</small>
    </div>

    <div class="md-field">
      <label class="md-label">Description</label>
      <textarea pInputTextarea [(ngModel)]="masterDataForm.description"
                [disabled]="savingMasterData"
                rows="2"
                class="md-textarea"
                placeholder="Optional context for your team."></textarea>
    </div>

    <!-- System Field only: how this field stores its value -->
    <div class="md-field" *ngIf="masterDataTab === 'SYSTEM_FIELD'">
      <label class="md-label">Field Type</label>
      <p-select [filter]="true" filterPlaceholder="Search" appendTo="body" [options]="systemFieldTypeOptions" [(ngModel)]="masterDataForm.fieldType"
                optionLabel="label" optionValue="value"
                [disabled]="savingMasterData" placeholder="Text" styleClass="w-full"></p-select>
      <small class="md-hint">
        <i class="pi pi-info-circle"></i>
        Choose how this field stores its value — plain text, a true/false toggle, key–value options, or a single / multiple file upload.
      </small>
    </div>

    <!-- Boolean default value -->
    <div class="md-field md-field--toggle"
         *ngIf="masterDataTab === 'SYSTEM_FIELD' && masterDataForm.fieldType === 'boolean'">
      <label class="md-label">Default Value</label>
      <div class="md-toggle-row">
        <p-checkbox [(ngModel)]="masterDataForm.boolValue" [binary]="true"
                    [disabled]="savingMasterData" inputId="mdBoolValue"></p-checkbox>
        <label for="mdBoolValue" class="md-toggle-label">
          <span class="md-toggle-title">{{ masterDataForm.boolValue ? 'True' : 'False' }}</span>
          <span class="md-toggle-sub">Default on/off value for this field.</span>
        </label>
      </div>
    </div>

    <!-- Key–Value pairs editor -->
    <div class="md-field"
         *ngIf="masterDataTab === 'SYSTEM_FIELD' && masterDataForm.fieldType === 'keyvalue'">
      <label class="md-label">Key – Value Pairs</label>
      <div class="md-kv-list">
        <div class="md-kv-row" *ngFor="let pair of masterDataForm.kvPairs; let i = index">
          <input pInputText class="md-kv-key" [(ngModel)]="pair.key"
                 [disabled]="savingMasterData" placeholder="Key (e.g. yes)" />
          <input pInputText class="md-kv-value" [(ngModel)]="pair.value"
                 [disabled]="savingMasterData" placeholder="Value (e.g. Yes, proceed)" />
          <button type="button" class="md-kv-remove" (click)="removeKvPair(i)"
                  [disabled]="savingMasterData" title="Remove pair">
            <i class="pi pi-times"></i>
          </button>
        </div>
        <div *ngIf="!masterDataForm.kvPairs.length" class="md-kv-empty">
          No pairs yet — add the options this field can hold.
        </div>
      </div>
      <button type="button" class="md-kv-add" (click)="addKvPair()" [disabled]="savingMasterData">
        <i class="pi pi-plus"></i> Add pair
      </button>
      <small class="md-hint">
        <i class="pi pi-info-circle"></i>
        Each pair maps a stored key to a human-friendly label.
      </small>

      <!-- Allow an "Other" choice that reveals a free-text box at capture time. -->
      <div class="md-toggle-row" style="margin-top:12px;">
        <p-checkbox [(ngModel)]="masterDataForm.allowOther" [binary]="true"
                    [disabled]="savingMasterData" inputId="mdAllowOther"></p-checkbox>
        <label for="mdAllowOther" class="md-toggle-label">
          <span class="md-toggle-title">Allow “Other”</span>
          <span class="md-toggle-sub">Adds an <b>Other…</b> choice to the dropdown; picking it shows a box where the agent types a custom value.</span>
        </label>
      </div>
    </div>

    <!-- System Field only: expose this field as a performance metric (how many times an agent fills it). -->
    <div class="md-field md-field--toggle" *ngIf="masterDataTab === 'SYSTEM_FIELD'">
      <label class="md-label">Performance metric</label>
      <div class="md-toggle-row">
        <p-checkbox [(ngModel)]="masterDataForm.trackPerformance" [binary]="true"
                    [disabled]="savingMasterData" inputId="mdTrackPerf"></p-checkbox>
        <label for="mdTrackPerf" class="md-toggle-label">
          <span class="md-toggle-title">Track as performance metric</span>
          <span class="md-toggle-sub">Show in performance as a count of how many times each agent fills this field.</span>
        </label>
      </div>
    </div>

    <!-- System Field only: curate the customer-flow Captured-data panel. In Pro this is the only
         place to set it (fields sync to the single Default phase). -->
    <div class="md-field md-field--toggle" *ngIf="masterDataTab === 'SYSTEM_FIELD'">
      <label class="md-label">Show in capture</label>
      <div class="md-toggle-row">
        <p-checkbox [(ngModel)]="masterDataForm.showInCapture" [binary]="true"
                    [disabled]="savingMasterData" inputId="mdShowInCapture"></p-checkbox>
        <label for="mdShowInCapture" class="md-toggle-label">
          <span class="md-toggle-title">Show in Captured-data panel</span>
          <span class="md-toggle-sub">Shows on the case screen's compact Captured-data panel. Unticked fields still save — they appear under Details. If no field is ticked, the panel shows everything.</span>
        </label>
      </div>
    </div>

    <div class="md-field-row">
      <div class="md-field md-field--narrow">
        <label class="md-label">Display Order</label>
        <div class="md-input-row">
          <i class="pi pi-sort-numeric-up"></i>
          <input pInputText type="number" min="0"
                 [(ngModel)]="masterDataForm.displayOrder"
                 [disabled]="savingMasterData" />
        </div>
      </div>
      <div class="md-field md-field--toggle">
        <label class="md-label">Status</label>
        <div class="md-toggle-row">
          <p-checkbox [(ngModel)]="masterDataForm.isActive" [binary]="true"
                      [disabled]="savingMasterData" inputId="mdActive"></p-checkbox>
          <label for="mdActive" class="md-toggle-label">
            <span class="md-toggle-title">Active</span>
            <span class="md-toggle-sub">Show this entry in dropdowns.</span>
          </label>
        </div>
      </div>
    </div>

    <div *ngIf="masterDataFormError" class="md-error">
      <i class="pi pi-exclamation-circle"></i> {{ masterDataFormError }}
    </div>
  </div>

  <ng-template pTemplate="footer">
    <button pButton label="Cancel" class="p-button-text p-button-secondary"
            (click)="showMasterDataDialog = false" [disabled]="savingMasterData"></button>
    <button pButton
            [icon]="savingMasterData ? 'pi pi-spin pi-spinner' : (editingMasterData ? 'pi pi-check' : 'pi pi-plus')"
            [label]="savingMasterData ? 'Saving…' : (editingMasterData ? 'Update Entry' : 'Create Entry')"
            [disabled]="savingMasterData"
            (click)="submitMasterData()"></button>
  </ng-template>
</p-dialog>

<!-- ══════════════ LEAD SOURCES DIALOGS ══════════════ -->

<!-- Dialog 1: Choose Source Type -->
<p-dialog header="Choose Source Type" [(visible)]="showChooseDialog" [modal]="true"
          [style]="{width:'640px'}" [closable]="true" [draggable]="false">
  <div class="source-type-grid">
    <div *ngFor="let st of sourceTypeOptions"
         class="source-type-card"
         [class.selected]="pickedSourceType === st.type"
         (click)="pickedSourceType = st.type">
      <i [class]="'pi ' + st.icon + ' source-type-card__icon'"></i>
      <div class="source-type-card__name">{{ st.label }}</div>
      <div class="source-type-card__desc">{{ st.desc }}</div>
    </div>
  </div>
  <ng-template pTemplate="footer">
    <button pButton label="Cancel" severity="secondary" [text]="true" (click)="showChooseDialog = false"></button>
    <button pButton label="Continue" icon="pi pi-arrow-right" iconPos="right"
            [disabled]="!pickedSourceType" (click)="proceedToConfigure()"></button>
  </ng-template>
</p-dialog>

<!-- Dialog 2: Configure Source -->
<p-dialog [header]="editingSource ? 'Edit Source — ' + getSourceLabel(pickedSourceType) : 'Configure ' + getSourceLabel(pickedSourceType)"
          [(visible)]="showConfigDialog" [modal]="true"
          [style]="{width:'700px'}" [contentStyle]="{'overflow-y':'auto','max-height':'80vh','padding':'1.5rem'}"
          [closable]="true" [draggable]="false">

  <!-- ⓪ Campaign Name — the source's display identity everywhere (list, board SOURCE
       column, per-source quotas, CSV Lead Import matching). Always required + unique. -->
  <div class="source-config-section">
    <label class="source-config-label">Lead Source <span class="req-star">*</span></label>
    <p-select appendTo="body" [options]="leadSourceNameOptions"
              [(ngModel)]="sourceForm.campaign_name"
              optionLabel="label" optionValue="value"
              [editable]="true" [filter]="true" filterBy="label,value"
              placeholder="Pick a Source Name or type a new one" styleClass="w-full"></p-select>
    <div class="source-field-hint">
      <i class="pi pi-info-circle"></i>
      Pick from your <strong>Source Names</strong> (Settings → Master Data), or type a new one. This name identifies the source across the app — it must be unique. Lead Import matches a row's Contact Source to this.
    </div>
  </div>

  <!-- ① Campaign / Form Name -->
  <div class="source-config-section">
    <label class="source-config-label">Campaign / Form Name <span class="req-star">*</span></label>
    <input pInputText class="w-full source-input"
           [ngModel]="sourceForm.source_id"
           (ngModelChange)="onSourceIdInput($event)"
           placeholder="e.g. meta_summer_2025" />
    <div class="source-field-hint">
      <i class="pi pi-info-circle"></i>
      Lowercase only · use underscores instead of spaces · no special characters
    </div>
    <div *ngIf="sourceIdHasError" class="source-field-error">
      <i class="pi pi-times-circle"></i> Use lowercase letters and underscores only (e.g. summer_campaign_2025)
    </div>
  </div>

  <!-- ② Webhook URL Preview (live) -->
  <div *ngIf="sourceForm.source_id" class="source-config-section">
    <label class="source-config-label">Webhook URL Preview</label>
    <div class="webhook-preview">
      <i class="pi pi-link webhook-preview__icon"></i>
      <code class="webhook-preview__url">{{ webhookUrlPreview }}</code>
      <button pButton
              [icon]="copiedField === 'wh-preview' ? 'pi pi-check' : 'pi pi-copy'"
              [severity]="copiedField === 'wh-preview' ? 'success' : 'secondary'"
              [text]="true" size="small" style="flex-shrink:0;"
              pTooltip="Copy URL" tooltipPosition="top"
              (click)="copyCredField(webhookUrlPreview, 'wh-preview')"></button>
    </div>
    <div class="source-field-hint">Updates live as you type the name above.</div>
  </div>

  <!-- ③ Required Fields -->
  <div class="source-config-section">
    <label class="source-config-label">Required Fields <span class="req-star">*</span></label>
    <div class="required-field-chips">
      <div *ngFor="let f of ['phone','name','email']"
           class="required-field-chip"
           [class.active]="isRequiredField(f)"
           (click)="toggleRequiredField(f)">
        <i [class]="'pi ' + (isRequiredField(f) ? 'pi-check-circle' : 'pi-circle')"></i>
        {{ f | titlecase }}
      </div>
    </div>
    <div *ngIf="!isRequiredField('phone') && !isRequiredField('email')" class="source-field-error" style="margin-top:0.5rem;">
      <i class="pi pi-exclamation-triangle"></i> Select at least Phone or Email
    </div>
  </div>

  <!-- ④ Field Mapping -->
  <div class="source-config-section">
    <div class="source-config-label" style="display:flex;align-items:center;justify-content:space-between;">
      <span>Field Mapping</span>
      <button pButton label="+ Add field mapping" [text]="true" size="small" (click)="addMappingRow()"></button>
    </div>
    <table class="field-mapping-table gh-table">
      <thead>
        <tr>
          <th>Incoming Field (from source)</th>
          <th>System Field</th>
          <th style="width:36px;"></th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let row of fieldMappingRows; let i = index">
          <td>
            <input pInputText [(ngModel)]="row.incoming" class="w-full source-input"
                   placeholder="e.g. full_name" />
          </td>
          <td>
            <p-select appendTo="body" [options]="systemFieldOptions" [(ngModel)]="row.system"
                      optionLabel="label" optionValue="value"
                      [filter]="true" filterBy="label,value"
                      placeholder="Select field" class="w-full"></p-select>
          </td>
          <td>
            <button pButton icon="pi pi-trash" [text]="true" size="small" severity="danger"
                    [disabled]="fieldMappingRows.length === 1"
                    [pTooltip]="isRequiredField(row.system) ? 'This maps a required field — deleting may break validation' : 'Remove row'"
                    tooltipPosition="left"
                    (click)="removeMappingRow(i)"></button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Mapping status per required field -->
    <div class="mapping-status-wrap">
      <div *ngFor="let f of sourceForm.required_fields" class="mapping-status-row">
        <i [class]="getMappingStatus(f) === 'mapped'
              ? 'pi pi-check-circle mapping-ok'
              : 'pi pi-exclamation-triangle mapping-warn'"></i>
        <span *ngIf="getMappingStatus(f) === 'mapped'" class="mapping-ok">{{ f | titlecase }} is mapped</span>
        <span *ngIf="getMappingStatus(f) === 'missing'" class="mapping-warn">{{ f | titlecase }} is required but not mapped</span>
      </div>
    </div>
  </div>

  <!-- ⑤ Google Key (google_ads only, required) -->
  <div *ngIf="pickedSourceType === 'google_ads'" class="source-config-section">
    <label class="source-config-label">Google Conversion Key <span class="req-star">*</span></label>
    <input pInputText [(ngModel)]="sourceForm.google_key" class="w-full source-input"
           placeholder="AW-XXXXXXXXXX/XXXXXXXXXXXXXXXXXX" />
    <div *ngIf="!sourceForm.google_key" class="source-field-error" style="margin-top:4px;">
      <i class="pi pi-times-circle"></i> Google Key is required for Google Ads sources
    </div>
  </div>

  <!-- ⑤a IndiaMART CRM key (indiamart only, required). Pull-based: no webhook to paste
       anywhere — the pull-connector polls IndiaMART's crmListing API with this key every
       ~5 minutes and lands enquiries through the same intake as every other source. -->
  <div *ngIf="pickedSourceType === 'indiamart'" class="source-config-section">
    <label class="source-config-label">
      IndiaMART CRM Key <span class="req-star">*</span>
      <span *ngIf="editingSource && sourceForm.indiamart_configured" class="metadata-optional">
        — saved; leave blank to keep
      </span>
    </label>
    <input pInputText type="password" [(ngModel)]="sourceForm.indiamart_crm_key" class="w-full source-input"
           autocomplete="new-password"
           [placeholder]="sourceForm.indiamart_configured ? '•••••••• (unchanged)' : 'paste the glusr_crm_key from your IndiaMART seller panel'" />
    <small class="text-500">
      Seller panel → Lead Manager → Settings → CRM Integration (the key is emailed to the
      registered address). Stored encrypted; leads auto-pull every 5 minutes — new enquiries
      only, from the moment this source is saved.
    </small>
    <div *ngIf="!indiamartKeyValid" class="source-field-error" style="margin-top:4px;">
      <i class="pi pi-times-circle"></i> IndiaMART CRM Key is required
    </div>
    <!-- IndiaMART serves history only ~24h after a key is generated — set expectations
         so an empty first day doesn't read as a broken integration. -->
    <div class="js-map-preview" style="margin-top:10px;">
      <i class="pi pi-info-circle"></i>
      <div>
        <div class="js-map-preview__title">Freshly generated key?</div>
        <div class="js-map-preview__sub">
          IndiaMART activates a new key within ~24 hours. New enquiries sync right away;
          if the status stays <strong>Pending</strong> with no leads on day one, that's
          IndiaMART's activation window — not an error.
        </div>
      </div>
    </div>
  </div>

  <!-- ⑤a2 Shopify connection (shopify only) — custom app per store. The merchant creates a custom
       app in their Shopify admin, grants read scopes, and pastes these three here. -->
  <div *ngIf="pickedSourceType === 'shopify'" class="source-config-section">
    <label class="source-config-label">
      Shopify store connection <span class="req-star">*</span>
      <span class="metadata-optional">from a custom app in your Shopify admin</span>
    </label>

    <div style="display:grid;gap:0.75rem;">
      <div>
        <label class="source-field-label">Store domain</label>
        <input pInputText [(ngModel)]="sourceForm.shopify_store_domain" class="w-full source-input"
               placeholder="yourstore.myshopify.com" />
        <small class="text-500">The store's <strong>.myshopify.com</strong> domain — the webhook routing key.</small>
      </div>

      <div>
        <label class="source-field-label">
          Admin API access token
          <span *ngIf="editingSource && sourceForm.shopify_configured" class="metadata-optional">— saved; leave blank to keep</span>
        </label>
        <input pInputText type="password" [(ngModel)]="sourceForm.shopify_access_token" class="w-full source-input"
               autocomplete="new-password"
               [placeholder]="sourceForm.shopify_configured ? '•••••••• (unchanged)' : 'shpat_… from the custom app API credentials'" />
        <small class="text-500">Settings → Apps → Develop apps → your app → API credentials. Stored encrypted.</small>
      </div>

      <div>
        <label class="source-field-label">
          Webhook signing secret
          <span *ngIf="editingSource && sourceForm.shopify_configured" class="metadata-optional">— saved; leave blank to keep</span>
        </label>
        <input pInputText type="password" [(ngModel)]="sourceForm.shopify_webhook_secret" class="w-full source-input"
               autocomplete="new-password"
               [placeholder]="sourceForm.shopify_configured ? '•••••••• (unchanged)' : 'the custom app API secret key'" />
        <small class="text-500">The app's <strong>API secret key</strong> — verifies Shopify's signed webhooks. Stored encrypted.</small>
      </div>
    </div>

    <div *ngIf="shopifyMissingFields.length" class="source-field-error" style="margin-top:6px;">
      <i class="pi pi-times-circle"></i> Required for Shopify: {{ shopifyMissingFields.join(', ') }}
    </div>

    <div *ngIf="!editingSource" class="js-map-preview" style="margin-top:10px;">
      <i class="pi pi-info-circle"></i>
      <div>
        <div class="js-map-preview__title">After saving</div>
        <div class="js-map-preview__sub">
          Re-open this source to choose which events become leads (abandoned checkout / order /
          customer) and to copy the webhook URL to add in your Shopify admin.
        </div>
      </div>
    </div>
  </div>

  <!-- ⑤b Meta connection (meta_ads only — covers Facebook AND Instagram lead forms).
       Central-app model: OUR Meta app receives every tenant's leads on one webhook and routes
       them by Page id, so a source needs only the customer's Page id + a Page token that can
       read its leads (generated from the system user after the partner share). -->
  <div *ngIf="pickedSourceType === 'meta_ads'" class="source-config-section">
    <label class="source-config-label">
      Meta Lead Ads connection <span class="req-star">*</span>
      <span class="metadata-optional">Facebook &amp; Instagram lead forms use the same setup</span>
    </label>

    <div style="display:grid;gap:0.75rem;">
      <div>
        <label class="source-field-label">Facebook Page ID</label>
        <input pInputText [(ngModel)]="sourceForm.meta_page_id" class="w-full source-input"
               placeholder="e.g. 102938475610293" />
        <small class="text-500">Incoming leads are matched to this source by Page id — get it from the
          customer's Page (About → Page transparency) or Business Manager.</small>
      </div>

      <div>
        <label class="source-field-label">
          Page Access Token
          <span *ngIf="editingSource && sourceForm.meta_configured" class="metadata-optional">
            — saved; leave blank to keep
          </span>
        </label>
        <input pInputText type="password" [(ngModel)]="sourceForm.meta_page_access_token" class="w-full source-input"
               autocomplete="new-password"
               [placeholder]="sourceForm.meta_configured ? '•••••••• (unchanged)' : 'Page token with leads_retrieval (from the system user)'" />
        <small class="text-500">Meta's webhook carries no lead data — we fetch it with this.</small>
      </div>

      <!-- Meta gives no reliable test flag (its testing-tool leads look like ordinary organic
           leads), so the admin says so explicitly while wiring the integration up. -->
      <div class="field-checkbox flex align-items-start gap-2" style="margin-top:2px;">
        <p-checkbox [(ngModel)]="sourceForm.meta_test_mode" [binary]="true" inputId="metaTestMode"></p-checkbox>
        <label for="metaTestMode" class="m-0 cursor-pointer">
          Test mode — save leads but don't message anyone
          <small class="block text-500">
            Turn on while you set this up: leads still appear, so you can confirm it works, but no
            WhatsApp is sent. Turn it off to go live.
          </small>
        </label>
      </div>
    </div>

    <div *ngIf="metaMissingFields.length" class="source-field-error" style="margin-top:6px;">
      <i class="pi pi-times-circle"></i> Required for Meta Ads: {{ metaMissingFields.join(', ') }}
    </div>

    <!-- Shared callback URL — configured ONCE in our Meta app, shown here for reference. -->
    <div *ngIf="editingSource && sourceForm.meta_webhook_url" class="js-map-preview" style="margin-top:10px;">
      <i class="pi pi-link"></i>
      <div>
        <div class="js-map-preview__title">Webhook (already set on our Meta app)</div>
        <div class="js-map-preview__sub" style="word-break:break-all;">
          <strong>{{ sourceForm.meta_webhook_url }}</strong>
        </div>
        <div class="js-map-preview__sub">
          One URL serves all customers — leads are routed to this source by its Page ID.
          Just make sure the customer's Page is subscribed to <strong>leadgen</strong>.
        </div>
      </div>
    </div>
  </div>

  <!-- ⑤c Meta Lead Forms — per-form field mapping (edit mode only: needs a saved Page token).
       A form with NO mapping PARKS its leads in the DLQ until it's mapped here, so the
       "Not mapped" badge is an operational alarm, not decoration. -->
  <div *ngIf="pickedSourceType === 'meta_ads' && editingSource" class="source-config-section">
    <label class="source-config-label">
      Lead Forms
      <span class="metadata-optional">each form must be mapped before its leads flow in</span>
    </label>

    <div class="flex align-items-center gap-2 mb-2">
      <button pButton type="button" [label]="metaForms.length ? 'Refresh forms' : 'Load forms from Meta'"
              icon="pi pi-refresh" size="small" severity="secondary" [outlined]="true"
              [loading]="metaFormsLoading" (click)="loadMetaForms()"></button>
      <small *ngIf="metaForms.length" class="text-500">
        {{ metaForms.length }} form(s) on this Page — pulled live, new forms appear automatically.
      </small>
    </div>

    <div *ngIf="metaFormsError" class="source-field-error"><i class="pi pi-times-circle"></i> {{ metaFormsError }}</div>

    <div *ngFor="let form of metaForms" style="border:1px solid var(--surface-border);border-radius:8px;margin-bottom:8px;">
      <!-- Form row — click to expand its fields -->
      <div class="flex align-items-center gap-2 p-2 cursor-pointer" (click)="openMetaForm(form)">
        <i class="pi" [ngClass]="metaFormOpen?.form_id === form.form_id ? 'pi-chevron-down' : 'pi-chevron-right'"></i>
        <span class="font-medium">{{ form.name }}</span>
        <p-tag [value]="form.status" [severity]="form.status === 'ACTIVE' ? 'success' : 'secondary'" [rounded]="true"></p-tag>
        <span class="sp" style="flex:1"></span>
        <p-tag *ngIf="form.mapped" value="Mapped" severity="success" [rounded]="true"
               [pTooltip]="form.mapped_field_count + ' field(s) mapped'" tooltipPosition="top"></p-tag>
        <p-tag *ngIf="!form.mapped" value="Not mapped — leads waiting" severity="warn" [rounded]="true"></p-tag>
        <i *ngIf="metaFormLoadingId === form.form_id" class="pi pi-spin pi-spinner"></i>
      </div>

      <!-- Expanded: the form's questions with mapping dropdowns -->
      <div *ngIf="metaFormOpen?.form_id === form.form_id" class="p-3" style="border-top:1px solid var(--surface-border);">
        <table class="gh-table" style="width:100%;border-collapse:collapse;">
          <tr>
            <th style="text-align:left;padding:4px 8px;" class="text-500 text-sm">Form question</th>
            <th style="text-align:left;padding:4px 8px;" class="text-500 text-sm">Maps to</th>
          </tr>
          <tr *ngFor="let field of metaFormOpen!.fields">
            <td style="padding:4px 8px;vertical-align:top;max-width:340px;">
              <div>{{ field.label }}</div>
              <small class="text-500">{{ field.key }} · {{ field.type }}</small>
            </td>
            <td style="padding:4px 8px;">
              <p-select [filter]="true" filterPlaceholder="Search" appendTo="body" [options]="metaFormMappingOptions"
                        [(ngModel)]="metaFormDraft[field.key]"
                        optionLabel="label" optionValue="value"
                        placeholder="Don't map" styleClass="w-full"></p-select>
              <small *ngIf="field.suggested && metaFormDraft[field.key] === field.suggested" class="text-500">
                suggested from question type
              </small>
            </td>
          </tr>
        </table>
        <div class="flex justify-content-end gap-2 mt-2">
          <button pButton type="button" label="Cancel" severity="secondary" [text]="true"
                  (click)="metaFormOpen = null"></button>
          <button pButton type="button" label="Save mapping" icon="pi pi-check"
                  [loading]="metaFormSaving" (click)="saveMetaFormMapping()"></button>
        </div>
      </div>
    </div>

    <div *ngIf="!metaFormsLoading && !metaForms.length && !metaFormsError" class="text-500 text-sm">
      Click "Load forms from Meta" to list this Page's lead forms and map their fields.
    </div>
  </div>

  <!-- ⑤e Shopify events — per-event enable + journey + mapping (edit mode only). A disabled or
       unmapped event acknowledges its webhooks but ingests nothing. -->
  <div *ngIf="pickedSourceType === 'shopify' && editingSource" class="source-config-section">
    <label class="source-config-label">
      Events → leads
      <span class="metadata-optional">choose which Shopify events become leads</span>
    </label>

    <!-- Connect: one-click auto-register of the store's webhooks via the Shopify Admin API. Uses the
         saved store domain + access token — no hand-creating webhooks in Shopify. -->
    <div class="shopify-connect" style="border:1px solid var(--surface-border);border-radius:8px;padding:12px;margin-bottom:12px;background:var(--surface-50);">
      <div class="flex align-items-center gap-2 flex-wrap">
        <button pButton type="button" label="Connect &amp; auto-register webhooks" icon="pi pi-bolt"
                [loading]="shopifyRegistering" (click)="connectShopify()"></button>
        <small class="text-500">One click — we register the order, customer &amp; checkout webhooks in your store for you.</small>
      </div>
      <div *ngIf="shopifyRegisterResult" style="margin-top:10px;">
        <div class="mb-1 font-medium">
          {{ shopifyRegisterResult.registered }}/{{ shopifyRegisterResult.total }} webhooks registered
        </div>
        <div *ngFor="let r of shopifyRegisterResult.results" class="flex align-items-center gap-2" style="font-size:0.85rem;padding:2px 0;">
          <i class="pi" [ngClass]="r.ok ? 'pi-check-circle text-green-500' : 'pi-times-circle text-red-500'"></i>
          <span class="font-medium">{{ r.label }}</span>
          <small class="text-500" *ngIf="!r.ok">— {{ r.error }}</small>
        </div>
      </div>
    </div>

    <div class="flex align-items-center gap-2 mb-2">
      <button pButton type="button" label="Refresh" icon="pi pi-refresh" size="small"
              severity="secondary" [outlined]="true" [loading]="shopifyEventsLoading"
              (click)="loadShopifyEvents()"></button>
      <small class="text-500">Only enabled + mapped events create leads.</small>
    </div>

    <div *ngIf="shopifyEventsError" class="source-field-error"><i class="pi pi-times-circle"></i> {{ shopifyEventsError }}</div>

    <div *ngFor="let ev of shopifyEvents" style="border:1px solid var(--surface-border);border-radius:8px;margin-bottom:8px;">
      <div class="flex align-items-center gap-2 p-2">
        <i class="pi cursor-pointer" [ngClass]="shopifyEventOpen?.slug === ev.slug ? 'pi-chevron-down' : 'pi-chevron-right'"
           (click)="openShopifyEvent(ev)"></i>
        <span class="font-medium cursor-pointer" (click)="openShopifyEvent(ev)">{{ ev.label }}</span>
        <p-tag [value]="ev.enabled ? 'On' : 'Off'" [severity]="ev.enabled ? 'success' : 'secondary'" [rounded]="true"></p-tag>
        <p-tag *ngIf="ev.registered" value="Registered" severity="info" [rounded]="true"></p-tag>
        <span class="sp" style="flex:1"></span>
        <p-tag *ngIf="ev.enabled && !shopifyEventMapped(ev)" value="Not mapped" severity="warn" [rounded]="true"></p-tag>
        <button pButton type="button" size="small" [text]="true"
                [label]="ev.enabled ? 'Disable' : 'Enable'"
                [severity]="ev.enabled ? 'danger' : 'success'"
                [loading]="shopifyEventTogglingSlug === ev.slug"
                (click)="toggleShopifyEvent(ev)"></button>
      </div>

      <div *ngIf="shopifyEventOpen?.slug === ev.slug" class="p-3" style="border-top:1px solid var(--surface-border);">
        <table class="gh-table" style="width:100%;border-collapse:collapse;">
          <tr>
            <th style="text-align:left;padding:4px 8px;" class="text-500 text-sm">Shopify field</th>
            <th style="text-align:left;padding:4px 8px;" class="text-500 text-sm">Maps to</th>
            <th style="width:2rem"></th>
          </tr>
          <tr *ngFor="let row of shopifyEventDraftRows; let i = index">
            <td style="padding:4px 8px;">
              <input pInputText [(ngModel)]="row.incoming" class="w-full" placeholder="e.g. email, phone, total_price" />
            </td>
            <td style="padding:4px 8px;">
              <p-select [filter]="true" filterPlaceholder="Search" appendTo="body" [options]="metaFormMappingOptions" [(ngModel)]="row.system"
                        optionLabel="label" optionValue="value" placeholder="Don't map" styleClass="w-full"></p-select>
            </td>
            <td style="padding:4px 8px;">
              <button pButton type="button" icon="pi pi-times" [text]="true" severity="secondary"
                      (click)="removeShopifyMappingRow(i)"></button>
            </td>
          </tr>
        </table>
        <button pButton type="button" label="Add field" icon="pi pi-plus" size="small" [text]="true"
                (click)="addShopifyMappingRow()"></button>

        <div style="display:grid;gap:0.75rem;margin-top:10px;">
          <div>
            <label class="source-field-label">Journey for this event</label>
            <p-select [filter]="true" filterPlaceholder="Search" appendTo="body" [options]="operations" [(ngModel)]="shopifyEventDraftOperationId"
                      optionLabel="name" optionValue="id" placeholder="Use the source default"
                      [showClear]="true" styleClass="w-full"></p-select>
          </div>
          <div>
            <label class="source-field-label">Punch this activity on the case</label>
            <p-select appendTo="body" [options]="shopifyActivityOptions" [(ngModel)]="shopifyEventDraftActivityTypeId"
                      optionLabel="label" optionValue="value" placeholder="Pick an activity (drives the automation)"
                      [showClear]="true" [filter]="true" styleClass="w-full"></p-select>
            <small class="text-500">When this event fires, we log this activity — your phase automation then changes the stage &amp; sends the follow-up. No extra setup in Event Types.</small>
          </div>
          <div>
            <label class="source-field-label">Source name (optional)</label>
            <input pInputText [(ngModel)]="shopifyEventDraftSourceName" class="w-full source-input"
                   placeholder="e.g. Shopify — abandoned cart" />
          </div>
        </div>

        <div class="flex justify-content-end gap-2 mt-2">
          <button pButton type="button" label="Cancel" severity="secondary" [text]="true"
                  (click)="shopifyEventOpen = null"></button>
          <button pButton type="button" label="Save event" icon="pi pi-check"
                  [loading]="shopifyEventSaving" (click)="saveShopifyEvent()"></button>
        </div>
      </div>
    </div>

    <div *ngIf="shopifyEventsLoading && !shopifyEvents.length" class="text-500 text-sm">Loading events…</div>
  </div>

  <!-- ⑥ Metadata -->
  <div class="source-config-section">
    <label class="source-config-label">
      Metadata
      <span class="metadata-optional">Optional — used for tagging and filtering</span>
    </label>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:0.75rem;">
      <div>
        <label class="source-field-label">Campaign Type</label>
        <p-select [filter]="true" filterPlaceholder="Search" appendTo="body" [options]="campaignTypeOptions" [(ngModel)]="sourceForm.campaign_type"
                  optionLabel="label" optionValue="value"
                  placeholder="Select type" class="w-full"></p-select>
      </div>
      <div>
        <label class="source-field-label">Region</label>
        <p-select [filter]="true" filterPlaceholder="Search" appendTo="body" [options]="regionOptions" [(ngModel)]="sourceForm.region"
                  optionLabel="label" optionValue="value"
                  placeholder="Select region" class="w-full"></p-select>
      </div>
    </div>
  </div>

  <!-- ⑦ Operation (preferred) -->
  <div class="source-config-section">
    <label class="source-config-label">Operation <span class="req-star">*</span></label>
    <p-select appendTo="body" [options]="operations" [(ngModel)]="sourceForm.operationId"
              optionLabel="name" optionValue="id"
              placeholder="Select operation"
              [loading]="loadingOperations" [showClear]="true" [filter]="true" filterBy="name"
              class="w-full"></p-select>
    <div class="source-field-hint">
      <i class="pi pi-info-circle"></i>
      Leads from this source enter this operation — its Phase&nbsp;1 opening is sent automatically.
    </div>
  </div>

  <!-- Legacy Workflow selector removed — sources bind to an Operation (journey) above. -->

  <!-- ⑧ Pre-submit Checklist -->
  <div class="source-checklist">
    <div class="source-checklist__title">Configuration Check</div>
    <div *ngFor="let check of configChecklist" class="source-checklist__item">
      <i [class]="check.valid ? 'pi pi-check-circle check-ok' : 'pi pi-times-circle check-fail'"></i>
      <span [class]="check.valid ? 'check-ok' : 'check-fail'">{{ check.label }}</span>
    </div>
    <div *ngIf="isSourceFormReady" class="source-checklist__item source-checklist__ready">
      <i class="pi pi-check-circle check-ok"></i>
      <span class="check-ok">Ready to receive leads</span>
    </div>
    <div *ngIf="!isSourceFormReady" class="source-checklist__warn">
      <i class="pi pi-exclamation-triangle"></i> Fix the issues above before continuing
    </div>
  </div>

  <!-- ⑧ Duplicate / API error -->
  <div *ngIf="sourceFormError" class="source-form-error" style="margin-top:0.75rem;">
    <div><i class="pi pi-exclamation-triangle"></i> <strong>{{ sourceFormError }}</strong></div>
    <div *ngIf="sourceFormError.includes('already exists')" class="source-dup-suggestions">
      Try a different name, e.g.&nbsp;
      <code>{{ sourceForm.source_id }}_v2</code>&nbsp;or&nbsp;
      <code>{{ sourceForm.source_id }}_{{ currentYear }}</code>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <button pButton label="Cancel" severity="secondary" [text]="true"
            (click)="showConfigDialog = false; sourceFormError = ''"></button>
    <!-- Meta sources hand out no credentials (Meta pushes to our central webhook), so the
         button promises only what actually happens. -->
    <button pButton [label]="editingSource ? 'Save Changes'
                : (pickedSourceType === 'meta_ads' ? 'Create Source' : 'Create & Get Credentials')"
            icon="pi pi-check" [loading]="savingSource"
            [disabled]="!isSourceFormReady || savingSource"
            (click)="submitSourceConfig()"></button>
  </ng-template>
</p-dialog>

<!-- Dialog 3: Credentials + Test -->
<p-dialog [header]="credentialsMode === 'test' ? 'Test Source — ' + currentCredentials?.sourceId : 'Credentials — ' + currentCredentials?.sourceId"
          [(visible)]="showCredentialsDialog" [modal]="true" [style]="{width:'680px'}"
          [closable]="true" [draggable]="false">

  <ng-container *ngIf="currentCredentials">

    <!-- Token warning (new / regenerate only) -->
    <div *ngIf="credentialsMode !== 'test'" class="cred-warning">
      <i class="pi pi-exclamation-triangle"></i>
      <span>Copy your <strong>Auth Token</strong> now — it will <strong>not</strong> be shown again after you close this dialog.</span>
    </div>

    <!-- Credential fields (new / regenerate only) -->
    <ng-container *ngIf="credentialsMode !== 'test'">
      <div class="cred-field">
        <div class="cred-field__label">Webhook URL</div>
        <div class="cred-field__row">
          <div class="cred-field__value">{{ currentCredentials.webhookUrl }}</div>
          <button pButton [icon]="copiedField === 'url' ? 'pi pi-check' : 'pi pi-copy'"
                  [severity]="copiedField === 'url' ? 'success' : 'secondary'"
                  [text]="true" size="small" pTooltip="Copy URL"
                  (click)="copyCredField(currentCredentials.webhookUrl, 'url')"></button>
        </div>
      </div>
      <div class="cred-field">
        <div class="cred-field__label">Auth Token <span class="cred-once-badge">shown once</span></div>
        <div class="cred-field__row">
          <div class="cred-field__value">{{ currentCredentials.authToken }}</div>
          <button pButton [icon]="copiedField === 'token' ? 'pi pi-check' : 'pi pi-copy'"
                  [severity]="copiedField === 'token' ? 'success' : 'secondary'"
                  [text]="true" size="small" pTooltip="Copy Token"
                  (click)="copyCredField(currentCredentials.authToken, 'token')"></button>
        </div>
      </div>
      <div class="cred-field">
        <div class="cred-field__label">Auth Header</div>
        <div class="cred-field__row">
          <div class="cred-field__value">{{ currentCredentials.authHeader }}</div>
          <button pButton [icon]="copiedField === 'header' ? 'pi pi-check' : 'pi pi-copy'"
                  [severity]="copiedField === 'header' ? 'success' : 'secondary'"
                  [text]="true" size="small" pTooltip="Copy Header"
                  (click)="copyCredField(currentCredentials.authHeader, 'header')"></button>
        </div>
      </div>
      <div class="cred-field">
        <div class="cred-field__label">cURL Example
          <button pButton [icon]="copiedField === 'curl' ? 'pi pi-check' : 'pi pi-copy'"
                  [severity]="copiedField === 'curl' ? 'success' : 'secondary'"
                  [text]="true" size="small" style="margin-left:0.5rem;"
                  (click)="copyCredField(currentCredentials.curlExample, 'curl')"></button>
        </div>
        <div class="cred-field__code">{{ currentCredentials.curlExample }}</div>
      </div>
      <hr style="border:none;border-top:1px solid #e2e8f0;margin:1.25rem 0;" />
    </ng-container>

    <!-- Test section -->
    <div class="source-config-label" style="margin-bottom:0.75rem;">Run a Test Lead</div>
    <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:0.75rem;margin-bottom:0.75rem;">
      <div>
        <label class="block text-xs text-500 mb-1">Name</label>
        <input pInputText [(ngModel)]="testBody.name" class="w-full" />
      </div>
      <div>
        <label class="block text-xs text-500 mb-1">Phone</label>
        <input pInputText [(ngModel)]="testBody.phone" class="w-full" />
      </div>
      <div>
        <label class="block text-xs text-500 mb-1">Email</label>
        <input pInputText [(ngModel)]="testBody.email" class="w-full" />
      </div>
    </div>
    <button pButton label="Run Test" icon="pi pi-play" [loading]="testLoading" (click)="runTest()"></button>

    <!-- Test result -->
    <div *ngIf="testResult" class="test-result"
         [ngClass]="testResult._success ? 'test-result--success' : 'test-result--error'">
      <ng-container *ngIf="testResult._success">
        <i class="pi pi-check-circle" style="margin-right:0.5rem;"></i>
        <strong>Connection verified.</strong> Lead accepted and stored.
        <span *ngIf="testResult.isDuplicate" style="margin-left:0.5rem;">(Duplicate detected)</span>
      </ng-container>
      <ng-container *ngIf="!testResult._success">
        <i class="pi pi-times-circle" style="margin-right:0.5rem;"></i>
        <strong>
          <ng-container [ngSwitch]="testResult._status">
            <span *ngSwitchCase="401">Invalid or expired token — regenerate token and try again.</span>
            <span *ngSwitchCase="400">Validation error — check that required fields are present.</span>
            <span *ngSwitchCase="429">Rate limit reached — wait a moment and retry.</span>
            <span *ngSwitchDefault>{{ testResult._message || 'Test failed.' }}</span>
          </ng-container>
        </strong>
      </ng-container>
    </div>

  </ng-container>

  <ng-template pTemplate="footer">
    <button pButton label="Close" severity="secondary" [text]="true" (click)="showCredentialsDialog = false; testResult = null"></button>
  </ng-template>
</p-dialog>

<!-- ── Disable Source Confirm ─────────────────────────── -->
<p-dialog header="Disable Source" [(visible)]="showDeleteConfirm" [modal]="true"
          [style]="{width:'420px'}" [closable]="true" [draggable]="false"
          styleClass="settings-dialog" (onHide)="sourceToDelete = null">
  <ng-template pTemplate="content">
    <div class="delete-confirm-body">
      <i class="pi pi-exclamation-triangle delete-confirm-icon"></i>
      <p>Are you sure you want to disable <strong>{{ sourceToDelete?.sourceId }}</strong>?</p>
      <p class="delete-confirm-sub">This source will stop receiving leads. You can re-enable it at any time.</p>
    </div>
  </ng-template>
  <ng-template pTemplate="footer">
    <button pButton label="Cancel" severity="secondary" [text]="true"
            (click)="showDeleteConfirm = false; sourceToDelete = null"></button>
    <button pButton label="Disable" severity="danger" icon="pi pi-pause-circle"
            (click)="confirmDeleteSource()"></button>
  </ng-template>
</p-dialog>

<!-- ── Map Stage to Team Dialog ──────────────────────── -->
<p-dialog header="Map Stage Responsibility" [(visible)]="showMapStatusDialog" [modal]="true" [style]="{ width: '520px' }" styleClass="p-fluid settings-dialog">
  <div class="field mt-2">
    <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest">Team</label>
    <input pInputText [value]="selectedTeamForStatus?.name || ''" class="w-full" readonly />
  </div>
  <div class="field mt-3">
    <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest">Select Stage</label>
    <p class="text-xs text-500 mt-0 mb-2">Choose the one workflow stage this team is responsible for. Click a selected stage again to deselect.</p>
    <div *ngIf="statuses.length === 0" class="empty-config">
      <i class="pi pi-list-check"></i>
      <span>No stages found. Create them in Workflow Stages.</span>
    </div>
    <div class="status-checkbox-list">
      <div *ngFor="let s of statuses" class="status-checkbox-row"
        [class.selected]="selectedStatusId === s.id"
        (click)="selectedStatusId = (selectedStatusId === s.id ? null : s.id)">
        <i class="pi" [class.pi-check-circle]="selectedStatusId === s.id" [class.pi-circle]="selectedStatusId !== s.id"></i>
        <span>{{ s.name }}</span>
        <span *ngIf="originalStatusId === s.id && selectedStatusId !== s.id" class="text-xs text-400 ml-auto">(current)</span>
      </div>
    </div>
  </div>
  <ng-template pTemplate="footer">
    <button pButton label="Cancel" class="p-button-text" (click)="showMapStatusDialog = false"></button>
    <button pButton label="Save Mapping" icon="pi pi-check"
      [loading]="savingTeamStatuses"
      [disabled]="savingTeamStatuses || selectedStatusId === originalStatusId"
      (click)="saveTeamStatuses()"></button>
  </ng-template>
</p-dialog>

<!-- ── View Team Stage Responsibility Dialog ───────────── -->
<p-dialog [header]="'Stage Responsibility — ' + (viewingTeamForStatus?.name || '')" [(visible)]="showTeamStatusesDialog" [modal]="true" [style]="{ width: '520px' }" styleClass="p-fluid settings-dialog">
  <div class="info-banner mb-3" style="padding:10px 14px;">
    <div class="info-banner__icon" style="width:28px;height:28px;"><i class="pi pi-info-circle" style="font-size:13px"></i></div>
    <div class="info-banner__body">
      <div class="info-banner__text" style="margin-bottom:0">The workflow stage this team is responsible for handling.</div>
    </div>
  </div>
  <div *ngIf="loadingTeamStatuses" class="flex align-items-center gap-2 text-500 text-sm py-4">
    <p-progressSpinner strokeWidth="4" styleClass="settings-spinner"></p-progressSpinner>
    <span>Loading stage...</span>
  </div>
  <div *ngIf="!loadingTeamStatuses">
    <div *ngIf="teamStatusMappings.length === 0" class="empty-config">
      <i class="pi pi-list-check"></i>
      <span>No stage mapped to this team yet.</span>
    </div>
    <div *ngIf="teamStatusMappings.length > 0" class="member-list">
      <div *ngFor="let m of teamStatusMappings" class="member-row">
        <div class="member-avatar" style="background:#dcfce7;color:#166534;">
          <i class="pi pi-list-check" style="font-size:14px"></i>
        </div>
        <div class="member-info">
          <div class="member-name">{{ m.resolvedName }}</div>
        </div>
      </div>
    </div>
  </div>
  <ng-template pTemplate="footer">
    <button pButton label="Close" class="p-button-text" (click)="showTeamStatusesDialog = false"></button>
    <button pButton label="Edit Mapping" icon="pi pi-pencil" class="p-button-outlined"
      (click)="showTeamStatusesDialog = false; openMapStatus(viewingTeamForStatus)"></button>
  </ng-template>
</p-dialog>

<!-- ── Invite User Dialog ────────────────────────────── -->
<p-dialog [(visible)]="showAddUserDialog" [modal]="true" [draggable]="false" [resizable]="false"
  [style]="{ width: '720px' }" appendTo="body" styleClass="invite-dialog">

  <ng-template pTemplate="header">
    <div class="invite-header">
      <div class="invite-header__icon"><i class="pi pi-user-plus"></i></div>
      <div>
        <div class="invite-header__title">{{ editingUserId ? 'Edit Team Member' : 'Invite Team Member' }}</div>
        <div class="invite-header__sub">{{ editingUserId ? 'Update this member’s details' : 'Add a new member to your workspace' }}</div>
      </div>
    </div>
  </ng-template>

  <form [formGroup]="userInviteForm" (ngSubmit)="addEmployee()" class="invite-form-body">

    <!-- Row 1: Full Name · Work Email -->
    <div class="invite-grid-2">
      <div class="invite-field">
        <label class="invite-label">Full Name</label>
        <div class="invite-input-row" [class.is-invalid]="isUserFormInvalid('name')">
          <i class="pi pi-user"></i>
          <input pInputText formControlName="name" placeholder="e.g. Jane Smith" />
        </div>
        <small class="p-error" *ngIf="isUserFormInvalid('name')">Full name is required.</small>
      </div>

      <div class="invite-field">
        <label class="invite-label">Work Email</label>
        <div class="invite-input-row" [class.is-invalid]="isUserFormInvalid('email')">
          <i class="pi pi-envelope"></i>
          <input pInputText formControlName="email" placeholder="name@company.com" />
        </div>
        <small class="p-error" *ngIf="isUserFormInvalid('email')">A valid work email is required.</small>
      </div>
    </div>

    <!-- Row 2: Phone · Employee Code -->
    <div class="invite-grid-2">
      <div class="invite-field">
        <label class="invite-label">Phone <span style="color:#94a3b8;font-weight:500;">(optional)</span></label>
        <div class="invite-input-row">
          <i class="pi pi-phone"></i>
          <input pInputText formControlName="phone" placeholder="e.g. 9876543210" autocomplete="off" />
        </div>
      </div>

      <div class="invite-field">
        <label class="invite-label">Employee Code <span style="color:#94a3b8;font-weight:500;">(optional)</span></label>
        <div class="invite-input-row">
          <i class="pi pi-id-card"></i>
          <input pInputText formControlName="employeeCode" placeholder="Auto-generated if left blank" autocomplete="off" />
        </div>
      </div>
    </div>

    <!-- Row 3: Department · Roles -->
    <div class="invite-grid-2">
      <div class="invite-field">
        <label class="invite-label">Departments <span style="color:#94a3b8;font-weight:500;">(optional)</span></label>
        <p-multiSelect appendTo="body" [options]="availableDepartments" formControlName="departmentIds"
                  optionLabel="name" optionValue="id" placeholder="Select one or more departments"
                  [filter]="availableDepartments.length > 6" filterBy="name" display="chip"
                  styleClass="w-full" [emptyMessage]="'No departments yet — create one under Teams'"></p-multiSelect>
      </div>

      <div class="invite-field">
        <label class="invite-label">Assign Roles</label>
        <p-multiSelect filterPlaceHolder="Search" appendTo="body" [options]="roleOptions" formControlName="roles" optionLabel="label" optionValue="value"
          placeholder="Select one or more roles" styleClass="w-full"
          [ngClass]="{'ng-invalid ng-dirty': isUserFormInvalid('roles')}">
        </p-multiSelect>
        <small class="p-error" *ngIf="isUserFormInvalid('roles')">At least one role is required.</small>
      </div>
    </div>

    <!-- Row 4: Access Template · Temporary Password (password field only when adding) -->
    <div class="invite-grid-2">
      <div class="invite-field">
        <label class="invite-label">Access Template</label>
        <p-select [filter]="true" filterPlaceholder="Search" appendTo="body" [options]="permTemplateOptions" formControlName="permissionTemplateId"
                  optionLabel="label" optionValue="value" placeholder="Role default" styleClass="w-full"
                  [disabled]="editingUserIsAdmin"
                  pTooltip="Which permission template governs this user's access. Admins always have full access."
                  tooltipPosition="top"></p-select>
        <small *ngIf="editingUserIsAdmin" style="display:block; margin-top:4px; color:#94a3b8; font-size:0.78rem;">
          Company Admins always have full access — templates don't apply.
        </small>
      </div>

      <div class="invite-field" *ngIf="!editingUserId">
        <label class="invite-label">Temporary Password</label>
        <div style="display:flex; gap:8px; align-items:stretch;">
          <div class="invite-input-row" style="flex:1" [class.is-invalid]="isUserFormInvalid('password')">
            <i class="pi pi-key"></i>
            <input pInputText formControlName="password" [type]="userPasswordVisible ? 'text' : 'password'"
                   placeholder="At least 6 characters" autocomplete="off" />
            <button pButton type="button" class="p-button-text p-button-sm invite-input-btn"
                    [icon]="userPasswordVisible ? 'pi pi-eye-slash' : 'pi pi-eye'"
                    [pTooltip]="userPasswordVisible ? 'Hide password' : 'Show password'" tooltipPosition="top"
                    (click)="userPasswordVisible = !userPasswordVisible"></button>
          </div>
          <button pButton type="button" label="Generate" icon="pi pi-refresh" class="p-button-outlined p-button-sm" (click)="generateUserPassword()"></button>
        </div>
        <small class="p-error" *ngIf="isUserFormInvalid('password')">Password must be at least 6 characters.</small>
        <small *ngIf="!isUserFormInvalid('password')" style="display:block; margin-top:4px; color:#94a3b8; font-size:0.78rem;">
          Click the eye to reveal what you typed — share it with the new user; they'll set their own at first sign-in.
        </small>
      </div>
    </div>

    <!-- Role legend, full-width under the grid -->
    <div class="invite-role-guide">
      <div class="invite-role-row">
        <span class="invite-role-badge invite-role-badge--agent">Agent</span>
        <span class="invite-role-desc">Handles cases assigned to their team</span>
      </div>
      <div class="invite-role-row">
        <span class="invite-role-badge invite-role-badge--manager">Manager</span>
        <span class="invite-role-desc">Oversees agents &amp; monitors workflow</span>
      </div>
      <div class="invite-role-row">
        <span class="invite-role-badge invite-role-badge--admin">Admin</span>
        <span class="invite-role-desc">Full access to settings &amp; configurations</span>
      </div>
    </div>

  </form>

  <ng-template pTemplate="footer">
    <button pButton label="Cancel" class="p-button-text p-button-secondary"
      (click)="showAddUserDialog = false"></button>
    <button pButton [label]="editingUserId ? 'Update User' : 'Send Invitation'"
      [icon]="editingUserId ? 'pi pi-check' : 'pi pi-send'"
      [loading]="savingUser" (click)="addEmployee()"></button>
  </ng-template>
</p-dialog>

<!-- ── Add Team Member Dialog ───────────────────────── -->
<p-dialog header="Add Members" [(visible)]="showAddMemberDialog" [modal]="true"
          [style]="{ width: '820px', maxWidth: '96vw' }" styleClass="p-fluid settings-dialog">
  <div class="field mt-2">
    <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest">Team</label>
    <input pInputText [value]="selectedTeamForMember?.name || ''" class="w-full" readonly />
  </div>
  <div class="field mt-3">
    <label class="font-medium mb-2 block text-sm text-500 uppercase tracking-widest">
      Select Employees
      <span class="text-400 normal-case tracking-normal font-normal">— pick as many as you like</span>
    </label>
    <div *ngIf="loadingEmployees" class="flex align-items-center gap-2 text-500 text-sm py-2">
      <p-progressSpinner strokeWidth="4" styleClass="settings-spinner"></p-progressSpinner>
      <span>Loading employees...</span>
    </div>
    <!-- appendTo="body" is required: inside a p-dialog the overlay is otherwise clipped by the
         dialog's own bounds, which is why the list showed as a cramped, cut-off box.
         Multi-select: a department is normally staffed in one sitting, so the picker takes the
         whole intake at once instead of one person per dialog open. `selectAll` covers the
         common "everyone in this list" case; already-added people are filtered out upstream by
         availableEmployees, so nothing can be double-added. -->
    <p-multiSelect *ngIf="!loadingEmployees"
      [(ngModel)]="memberForm.selectedEmployees"
      [options]="availableEmployees"
      [optionLabel]="'name'"
      placeholder="Choose employees"
      [filter]="true"
      filterBy="name,email,phone,employeeCode"
      filterPlaceHolder="Search by name, email or phone"
      appendTo="body"
      [showClear]="true"
      [showToggleAll]="true"
      [display]="'chip'"
      [maxSelectedLabels]="12"
      selectedItemsLabel="{0} employees selected"
      [scrollHeight]="'340px'"
      emptyMessage="No employees found"
      emptyFilterMessage="No employee matches that search"
      class="w-full"
      styleClass="w-full">
      <ng-template pTemplate="item" let-emp>
        <div class="flex flex-column gap-1 py-1">
          <span class="font-semibold text-sm">{{ employeeLabel(emp) }}</span>
          <!-- Only when it adds something: people with no name fall back to their email as the
               label, and the email was then printed a second time underneath it. -->
          <span class="text-500 text-xs" *ngIf="employeeSubLabel(emp)">{{ employeeSubLabel(emp) }}</span>
        </div>
      </ng-template>
    </p-multiSelect>
    <small class="text-500 block mt-2" *ngIf="memberForm.selectedEmployees?.length">
      {{ memberForm.selectedEmployees.length }}
      {{ memberForm.selectedEmployees.length === 1 ? 'person' : 'people' }} will be added to
      <b>{{ selectedTeamForMember?.name }}</b>.
    </small>
  </div>

  <!-- V016 — Manager toggle. When ticked, this member sees manager-only
       screens (SLA breach inbox, team review, escalations) for this team. -->
  <div class="field mt-4 manager-toggle-row" [class.is-on]="memberForm.isManager">
    <div class="manager-toggle-text">
      <div class="manager-toggle-title">
        <i class="pi pi-shield"></i>
        <span>Make {{ (memberForm.selectedEmployees?.length || 0) > 1 ? 'these people managers' : 'this person a manager' }}</span>
      </div>
      <div class="manager-toggle-sub">
        Managers see the team review screen, SLA breach inbox and escalations for this team.
        <ng-container *ngIf="(memberForm.selectedEmployees?.length || 0) > 1">
          This applies to <b>everyone</b> selected above.
        </ng-container>
      </div>
    </div>
    <p-checkbox [(ngModel)]="memberForm.isManager" [binary]="true" inputId="memberMgrToggle"></p-checkbox>
  </div>

  <ng-template pTemplate="footer">
    <button pButton label="Cancel" class="p-button-text" (click)="showAddMemberDialog = false"></button>
    <button pButton icon="pi pi-user-plus"
      [label]="(memberForm.selectedEmployees?.length || 0) > 1
                 ? ('Add ' + memberForm.selectedEmployees.length + ' Members') : 'Add Member'"
      [loading]="savingMember"
      [disabled]="savingMember || !(memberForm.selectedEmployees?.length)"
      (click)="addTeamMember()"></button>
  </ng-template>
</p-dialog>

<!-- ── View Team Members Dialog ──────────────────────── -->
<!-- ══════════════ TEAM MEMBERS DIALOG ══════════════ -->
<!-- Two tabs: "People" (flat searchable list of every member) and "Groups"
     (each group is a collapsible section showing its members underneath, plus
     an "Ungrouped" bucket). Wider than the old dialog so rows don't wrap. -->
<p-dialog [header]="'Members — ' + (viewingTeam?.name || '')" [(visible)]="showMembersDialog"
          [modal]="true" [draggable]="false" [resizable]="false"
          [style]="{ width: '1180px', maxWidth: '96vw' }"
          contentStyleClass="mm-dlg-body" styleClass="settings-dialog mm-dlg">
  <div *ngIf="loadingTeamMembers" class="flex align-items-center gap-2 text-500 text-sm py-4">
    <p-progressSpinner strokeWidth="4" styleClass="settings-spinner"></p-progressSpinner>
    <span>Loading members...</span>
  </div>

  <div *ngIf="!loadingTeamMembers" class="mm">

    <!-- Summary header — quick counts + search + tab switch. -->
    <div class="mm__top">
      <div class="mm__counts">
        <span class="mm__count-pill"><i class="pi pi-users"></i> {{ teamMembers.length }} {{ teamMembers.length === 1 ? 'member' : 'members' }}</span>
        <span class="mm__count-pill mm__count-pill--muted"><i class="pi pi-sitemap"></i> {{ teamGroups.length }} {{ teamGroups.length === 1 ? 'group' : 'groups' }}</span>
        <span class="mm__count-pill mm__count-pill--muted" *ngIf="membersManagerCount > 0"><i class="pi pi-shield"></i> {{ membersManagerCount }} {{ membersManagerCount === 1 ? 'manager' : 'managers' }}</span>
      </div>
      <div class="mm__search">
        <i class="pi pi-search"></i>
        <input pInputText type="text" [(ngModel)]="memberSearch" placeholder="Search by name, email or code"/>
        <button *ngIf="memberSearch" class="mm__search-x" type="button" (click)="memberSearch = ''" title="Clear">
          <i class="pi pi-times"></i>
        </button>
      </div>
    </div>

    <div class="mm__tabs">
      <button type="button" class="mm-tab" [class.is-active]="membersTab === 'people'" (click)="membersTab = 'people'">
        <i class="pi pi-user"></i> People
      </button>
      <button type="button" class="mm-tab" [class.is-active]="membersTab === 'groups'" (click)="membersTab = 'groups'">
        <i class="pi pi-sitemap"></i> Groups
      </button>
    </div>

    <!-- ── PEOPLE tab — flat list, filtered by search ────────────── -->
    <div *ngIf="membersTab === 'people'" class="mm__pane">
      <div *ngIf="teamMembers.length === 0" class="mm__empty">
        <i class="pi pi-users"></i>
        <div class="mm__empty-title">No members yet</div>
        <div class="mm__empty-sub">Click "Add member" to invite someone to this team.</div>
      </div>
      <ng-container *ngIf="teamMembers.length > 0">
        <div *ngIf="filteredMembers.length === 0" class="mm__empty mm__empty--sm">
          <i class="pi pi-search"></i>
          <div class="mm__empty-sub">No members match "{{ memberSearch }}".</div>
        </div>
        <div class="mm__list" *ngIf="filteredMembers.length > 0">
          <ng-container *ngFor="let m of filteredMembers">
            <ng-container *ngTemplateOutlet="memberRow; context: { $implicit: m }"></ng-container>
          </ng-container>
        </div>
      </ng-container>
    </div>

    <!-- ── GROUPS tab — each group its own section w/ members inside ── -->
    <div *ngIf="membersTab === 'groups'" class="mm__pane">
      <!-- Compact "add group" bar at the top of the Groups tab. -->
      <div class="mm__addgroup">
        <div class="mm__addgroup-input">
          <i class="pi pi-sitemap"></i>
          <input pInputText [(ngModel)]="newGroupName" placeholder="New group name (e.g. Noida)"
                 (keydown.enter)="createGroup()"/>
        </div>
        <button pButton label="Add group" icon="pi pi-plus" class="p-button-sm"
                [loading]="savingGroup" [disabled]="!newGroupName.trim()" (click)="createGroup()"></button>
      </div>

      <div *ngIf="teamGroups.length === 0 && teamMembers.length === 0" class="mm__empty">
        <i class="pi pi-sitemap"></i>
        <div class="mm__empty-title">No groups yet</div>
        <div class="mm__empty-sub">Groups let you route work to a subset of the team (e.g. by city).</div>
      </div>

      <!-- One section per group + one for members with no group. -->
      <div class="mm__groups">
        <div *ngFor="let g of teamGroups" class="mm-group">
          <div class="mm-group__head">
            <div class="mm-group__title">
              <i class="pi pi-sitemap"></i>
              <!-- Renamed in place: the row is just a name + a count, so a dialog would be more
                   ceremony than the edit deserves. Enter saves, Escape restores. -->
              <span *ngIf="editingGroupId !== g.id">{{ g.name }}</span>
              <input *ngIf="editingGroupId === g.id" pInputText class="mm-group__rename"
                     [(ngModel)]="editingGroupName" [disabled]="savingGroupRename"
                     (keydown.enter)="saveGroupRename(g)" (keydown.escape)="cancelGroupRename()"
                     placeholder="Group name" autofocus />
              <span class="mm-group__count">{{ (membersByGroup[g.id] || []).length }} {{ (membersByGroup[g.id] || []).length === 1 ? 'member' : 'members' }}</span>
            </div>
            <div class="mm-group__acts">
              <button type="button" class="mm-group__del" *ngIf="editingGroupId !== g.id"
                      (click)="startGroupRename(g)" title="Rename group">
                <i class="pi pi-pencil"></i>
              </button>
              <button type="button" class="mm-group__del" *ngIf="editingGroupId === g.id"
                      [disabled]="!editingGroupName.trim() || savingGroupRename"
                      (click)="saveGroupRename(g)" title="Save name">
                <i class="pi" [ngClass]="savingGroupRename ? 'pi-spin pi-spinner' : 'pi-check'"></i>
              </button>
              <button type="button" class="mm-group__del" *ngIf="editingGroupId === g.id"
                      (click)="cancelGroupRename()" title="Cancel">
                <i class="pi pi-times"></i>
              </button>
              <button type="button" class="mm-group__del" *ngIf="editingGroupId !== g.id"
                      (click)="deleteGroup(g)" title="Delete group">
                <i class="pi pi-trash"></i>
              </button>
            </div>
          </div>
          <div class="mm-group__body">
            <div *ngIf="(membersByGroup[g.id] || []).length === 0" class="mm-group__empty">
              <span>No one in this group yet.</span>
              <span class="mm-group__hint">Use the group dropdown on a person to move them here.</span>
            </div>
            <div *ngIf="(membersByGroup[g.id] || []).length > 0" class="mm__list mm__list--sub">
              <ng-container *ngFor="let m of membersByGroup[g.id]">
                <ng-container *ngTemplateOutlet="memberRow; context: { $implicit: m }"></ng-container>
              </ng-container>
            </div>
          </div>
        </div>

        <!-- Ungrouped bucket — always shown when there are groups, so admins
             can spot people who still need one. -->
        <div class="mm-group mm-group--ungrouped" *ngIf="teamGroups.length > 0">
          <div class="mm-group__head">
            <div class="mm-group__title">
              <i class="pi pi-inbox"></i>
              <span>Ungrouped</span>
              <span class="mm-group__count">{{ (membersByGroup['__none'] || []).length }} {{ (membersByGroup['__none'] || []).length === 1 ? 'member' : 'members' }}</span>
            </div>
          </div>
          <div class="mm-group__body">
            <div *ngIf="(membersByGroup['__none'] || []).length === 0" class="mm-group__empty">
              <span>Everyone has a group. Nice.</span>
            </div>
            <div *ngIf="(membersByGroup['__none'] || []).length > 0" class="mm__list mm__list--sub">
              <ng-container *ngFor="let m of membersByGroup['__none']">
                <ng-container *ngTemplateOutlet="memberRow; context: { $implicit: m }"></ng-container>
              </ng-container>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Reusable member row (same markup in both tabs). -->
  <ng-template #memberRow let-m>
    <div class="mm-row" [class.is-manager]="m.isManager">
      <div class="mm-row__avatar">{{ (m.name || getMemberName(m.userId) || '?').charAt(0).toUpperCase() }}</div>
      <div class="mm-row__info">
        <div class="mm-row__name">
          {{ m.name || getMemberName(m.userId) }}
          <span *ngIf="m.isManager" class="mm-row__badge" title="Manager"><i class="pi pi-shield"></i> Manager</span>
        </div>
        <div class="mm-row__meta">
          <ng-container *ngIf="m.employeeCode || m.email || m.phone; else noMeta">
            <span *ngIf="m.employeeCode"><i class="pi pi-id-card"></i> {{ m.employeeCode }}</span>
            <span *ngIf="m.email"><i class="pi pi-envelope"></i> {{ m.email }}</span>
            <span *ngIf="m.phone"><i class="pi pi-phone"></i> {{ m.phone }}</span>
          </ng-container>
          <ng-template #noMeta><span class="text-500">{{ m.userId }}</span></ng-template>
        </div>
      </div>
      <div class="mm-row__actions">
        <!-- Group picker — hidden when the team has no groups (nothing to pick). -->
        <p-select [filter]="true" filterPlaceholder="Search" *ngIf="teamGroups.length" appendTo="body" [options]="teamGroups" [ngModel]="m.groupId"
                  optionLabel="name" optionValue="id" [showClear]="true"
                  placeholder="No group" styleClass="mm-row__group"
                  (onChange)="setMemberGroup(m, $event.value)"></p-select>
        <button pButton class="p-button-text p-button-sm mm-row__manager" [class.is-on]="m.isManager"
                [loading]="togglingManagerId === m.id" [disabled]="togglingManagerId === m.id"
                [title]="m.isManager ? 'Remove manager role' : 'Make manager'"
                (click)="toggleMemberManager(m)">
          <i class="pi pi-shield"></i>
          <span class="ml-1">{{ m.isManager ? 'Manager' : 'Make manager' }}</span>
        </button>
        <button pButton class="p-button-text p-button-sm p-button-danger mm-row__remove"
                [loading]="removingMemberId === m.id" [disabled]="removingMemberId === m.id"
                title="Remove from team" (click)="removeMember(m)">
          <i class="pi pi-user-minus"></i>
        </button>
      </div>
    </div>
  </ng-template>

  <ng-template pTemplate="footer">
    <button pButton label="Close" class="p-button-text" (click)="showMembersDialog = false"></button>
    <button pButton label="Add member" icon="pi pi-user-plus"
      (click)="showMembersDialog = false; openAddMember(viewingTeam)"></button>
  </ng-template>
</p-dialog>

<!-- ── SOP Tasks Dialog ──────────────────────────────── -->
<p-dialog [(visible)]="showSopDialog" [modal]="true" [draggable]="false" [resizable]="false"
  [style]="{ width: '640px' }" appendTo="body" styleClass="sop-dialog">

  <ng-template pTemplate="header">
    <div class="sop-dialog-header">
      <div class="sop-dialog-header__icon"><i class="pi pi-list-check"></i></div>
      <div>
        <div class="sop-dialog-header__title">SOP Tasks</div>
        <div class="sop-dialog-header__sub">
          Standard operating procedures for <strong>{{ selectedStageForSop?.name }}</strong>
        </div>
      </div>
    </div>
  </ng-template>

  <!-- Add SOP form -->
  <div class="sop-add-form">
    <div class="sop-add-form__title"><i class="pi pi-plus-circle"></i> Add New Task</div>
    <div class="sop-add-form__row">
      <div class="sop-add-form__field sop-add-form__field--grow">
        <label>Task Label</label>
        <input pInputText [(ngModel)]="sopForm.label" placeholder="e.g. Site Visit, Call Client…" class="w-full" />
      </div>
      <div class="sop-add-form__field sop-add-form__field--order">
        <label>Order No.</label>
        <input pInputText type="number" [(ngModel)]="sopForm.orderNo" min="1" class="w-full" />
      </div>
      <div class="sop-add-form__field sop-add-form__field--mandatory">
        <label>Mandatory</label>
        <div class="sop-mandatory-toggle" [class.active]="sopForm.isMandatory" (click)="sopForm.isMandatory = !sopForm.isMandatory">
          <i class="pi" [ngClass]="sopForm.isMandatory ? 'pi-check-circle' : 'pi-circle'"></i>
          <span>{{ sopForm.isMandatory ? 'Yes' : 'No' }}</span>
        </div>
      </div>
    </div>
    <div class="sop-add-form__actions">
      <button pButton label="Add Task" icon="pi pi-plus" class="p-button-sm"
        [loading]="savingSop" [disabled]="savingSop || !sopForm.label.trim()"
        (click)="createSopTask()"></button>
    </div>
  </div>

  <!-- Task list -->
  <div class="sop-task-list">
    <div class="sop-task-list__header">
      <span class="sop-task-list__title">
        <i class="pi pi-sort-alt"></i> All Tasks
        <span class="sop-task-count">{{ sopTasks.length }}</span>
      </span>
      <span class="sop-task-list__hint">Drag rows to reorder</span>
    </div>

    <div *ngIf="loadingSop" class="sop-loading">
      <p-progressSpinner strokeWidth="4" styleClass="settings-spinner"></p-progressSpinner>
      <span>Loading tasks…</span>
    </div>

    <div *ngIf="!loadingSop && sopTasks.length === 0" class="sop-empty">
      <i class="pi pi-inbox"></i>
      <span>No SOP tasks yet. Add the first one above.</span>
    </div>

    <div *ngIf="!loadingSop && sopTasks.length > 0"
      cdkDropList
      (cdkDropListDropped)="onSopDrop($event)"
      class="sop-drag-list">

      <div *ngFor="let task of sopTasks; let i = index"
        cdkDrag
        class="sop-task-row">

        <!-- drag handle -->
        <div cdkDragHandle class="sop-task-row__handle" pTooltip="Drag to reorder">
          <i class="pi pi-bars"></i>
        </div>

        <!-- order badge -->
        <div class="sop-task-row__order">{{ task.orderNo }}</div>

        <!-- label -->
        <div class="sop-task-row__label">{{ task.label }}</div>

        <!-- mandatory badge -->
        <span class="sop-task-row__mandatory" [class.sop-mandatory--yes]="task.isMandatory" [class.sop-mandatory--no]="!task.isMandatory">
          <i class="pi" [ngClass]="task.isMandatory ? 'pi-shield' : 'pi-minus-circle'"></i>
          {{ task.isMandatory ? 'Mandatory' : 'Optional' }}
        </span>

        <!-- delete -->
        <button pButton icon="pi pi-trash" class="p-button-text p-button-danger p-button-sm sop-task-row__delete"
          pTooltip="Remove task"
          [loading]="deletingSopId === task.id"
          [disabled]="!!deletingSopId"
          (click)="deleteSopTask(task)"></button>

        <!-- drag placeholder -->
        <div *cdkDragPlaceholder class="sop-drag-placeholder"></div>
      </div>

    </div>
  </div>

  <ng-template pTemplate="footer">
    <button pButton label="Close" class="p-button-text p-button-secondary" (click)="showSopDialog = false"></button>
  </ng-template>
</p-dialog>

<!-- ── Dialogs (unchanged) ───────────────────────────── -->
<p-dialog header="Upgrade to Pro" [(visible)]="showUpgradeDialog" [modal]="true" [style]="{ width: '520px' }" styleClass="p-fluid settings-dialog">
  <div class="text-sm text-slate-600">You are currently on the Free plan. Upgrade to Pro to unlock premium features.</div>
  <ng-template pTemplate="footer">
    <button pButton label="Cancel" class="p-button-text" (click)="showUpgradeDialog = false"></button>
    <button pButton label="Confirm Upgrade" icon="pi pi-check" class="p-button-warning" [loading]="upgradingPlan" [disabled]="upgradingPlan" (click)="confirmUpgrade()"></button>
  </ng-template>
</p-dialog>

<p-dialog header="Edit Linked Account" [(visible)]="showTokenDialog" [modal]="true" [style]="{ width: '580px' }" styleClass="p-fluid campaign-dialog">
  <div class="campaign-form-body">
    <div class="mb-4">
      <label class="block font-medium mb-1">Friendly Name</label>
      <input pInputText placeholder="e.g., Marketing Account" [(ngModel)]="tokenForm.accountName" class="w-full" />
    </div>
    <!-- Collapsed by default. Normal onboarding never needs a token: the client shares their
         WhatsApp Account with us and our system user sends on it. This stays for the one case that
         cannot work that way — a WABA that was never shared with us. -->
    <a *ngIf="!showAdvancedToken" class="text-sm text-primary cursor-pointer"
       (click)="showAdvancedToken = true">Use a client-supplied access token instead</a>
    <div *ngIf="showAdvancedToken" class="mb-4 access-token-field">
      <label class="block font-medium mb-1">Access Token / System User Token</label>
      <textarea pTextarea rows="6" [(ngModel)]="tokenForm.token" placeholder="EAAB..." class="w-full access-token-textarea" spellcheck="false" autocomplete="off"></textarea>
      <!-- The list endpoint (ChannelAccountResponse) does not return credentials, so this box
           always opens EMPTY — there is no stored value to "leave unchanged". Blank means the
           backend leaves the stored token exactly as it is. -->
      <small class="access-token-hint">Only needed if this client's WhatsApp Account is not shared with GrowHigh. Leave blank to change the name only — the stored token is never shown here and stays as it is.</small>
    </div>
  </div>
  <ng-template pTemplate="footer">
    <div class="campaign-dialog-footer">
      <button pButton label="Save Changes" icon="pi pi-check" (click)="saveAccessToken()"></button>
    </div>
  </ng-template>
</p-dialog>

<!-- ── Remove Linked Account ──────────────────────────────────────────────────
     Deleting a channel account cascades to every company_channel_configs row
     (phone number) mapped to it, so this dialog reads the server's impact report
     and names each number BEFORE the confirm button is enabled. The Veda/Koshish
     number mix-up had to be repaired in raw SQL precisely because this screen had
     no delete affordance and no way to see what a delete would take with it. -->
<p-dialog header="Remove WhatsApp Account" [(visible)]="showDeleteAccountDialog" [modal]="true"
          [style]="{ width: '580px' }" [contentStyle]="{'overflow-y':'auto','max-height':'70vh'}"
          [closable]="true" [draggable]="false" [resizable]="false"
          styleClass="settings-dialog" (onHide)="accountUsage = null">
  <ng-template pTemplate="content">
    <div *ngIf="loadingAccountUsage" class="settings-loading">
      <p-progressSpinner strokeWidth="4" styleClass="settings-spinner"></p-progressSpinner>
      <span>Checking what this account is used for...</span>
    </div>

    <!-- Report failed to load — fail closed: no numbers named, no delete offered. -->
    <div *ngIf="!loadingAccountUsage && !accountUsage" class="delete-confirm-body">
      <i class="pi pi-exclamation-triangle delete-confirm-icon"></i>
      <p>Could not check what this account is used for.</p>
      <p class="delete-confirm-sub">Nothing has been removed. Close this dialog and try again.</p>
    </div>

    <div *ngIf="!loadingAccountUsage && accountUsage" class="delete-confirm-body">
      <i class="pi pi-exclamation-triangle delete-confirm-icon"></i>
      <p>Remove <strong>{{ accountUsage.accountName || channelAccount?.accountName }}</strong> from GrowHigh?</p>
      <p class="delete-confirm-sub">
        This removes the account <strong>and every WhatsApp number mapped to it</strong> from
        GrowHigh only. Nothing is deleted at Meta — the numbers stay in your Meta Business
        account and can be registered again later.
      </p>

      <div class="w-full text-left mt-2">
        <div class="integration-label">Numbers that will be removed from GrowHigh</div>
        <div *ngFor="let n of accountUsage.phoneNumbers" class="integration-item mb-2">
          <div class="text-sm font-bold text-900">
            {{ n.phoneNumber }}
            <span *ngIf="n.displayName" class="text-500 font-normal">— {{ n.displayName }}</span>
            <p-tag *ngIf="n.isDefault" severity="info" value="Default" styleClass="ml-2"></p-tag>
          </div>
          <div class="delete-confirm-sub">Phone Number ID {{ n.senderIdentifier }} · {{ n.status }}</div>
        </div>
        <div *ngIf="!accountUsage.phoneNumbers?.length" class="delete-confirm-sub">
          No numbers are currently mapped to this account.
        </div>
      </div>

      <div class="w-full text-left mt-2">
        <div class="integration-label">What is attached to those numbers</div>
        <div class="delete-confirm-sub">
          {{ accountUsage.counts?.messages || 0 }} message(s) ·
          {{ accountUsage.counts?.webhookEvents || 0 }} webhook event(s) ·
          {{ accountUsage.counts?.templates || 0 }} template(s) ·
          {{ accountUsage.counts?.leadConfigs || 0 }} lead config(s) ·
          {{ accountUsage.counts?.phaseConfigs || 0 }} phase config(s)
        </div>
        <div class="delete-confirm-sub">
          Chat history on these numbers goes with them. Webhook records are kept but
          are no longer linked to a number.
        </div>
      </div>

      <div *ngIf="!canRemoveAccount" class="w-full text-left mt-2">
        <div class="integration-label text-red-500">This account cannot be removed yet</div>
        <ul class="delete-confirm-sub m-0 pl-3">
          <li *ngFor="let b of accountUsageBlockers">{{ b }}</li>
        </ul>
        <div *ngIf="!accountUsageBlockers.length" class="delete-confirm-sub">
          Remove or re-point whatever still uses these numbers, then try again.
        </div>
      </div>
    </div>
  </ng-template>
  <ng-template pTemplate="footer">
    <button pButton label="Cancel" severity="secondary" [text]="true" [disabled]="deletingAccount"
            (click)="showDeleteAccountDialog = false"></button>
    <!-- Disabled, not just warned: never let an operator fire a delete the server will refuse. -->
    <button pButton label="Remove Account" severity="danger" icon="pi pi-trash"
            [loading]="deletingAccount" [disabled]="!canRemoveAccount || deletingAccount"
            (click)="confirmRemoveAccount()"></button>
  </ng-template>
</p-dialog>

<p-dialog header="WhatsApp Configuration" [(visible)]="showAccountDialog" [modal]="true" [style]="{ width: '580px' }" styleClass="p-fluid campaign-dialog">
  <div class="campaign-form-body">
    <div class="mb-4">
      <label class="block font-medium mb-1">Friendly Name</label>
      <input pInputText placeholder="e.g., Marketing Account" [(ngModel)]="accountForm.accountName" class="w-full" />
    </div>
    <!-- No token box. GrowHigh's own system-user token covers every WABA shared to us, so a
         per-client token is not needed (and would only be used if the platform token were missing).
         The account row still exists because a number config must belong to one. -->
    <div class="mb-4">
      <small class="access-token-hint">
        No access token is needed. Once the client shares their WhatsApp Account with GrowHigh,
        our system user can send on it — you only add the number and press <strong>Subscribe</strong>.
      </small>
    </div>
  </div>
  <ng-template pTemplate="footer">
    <div class="campaign-dialog-footer">
      <button pButton label="Connect Account" icon="pi pi-check" (click)="saveAccount()"></button>
    </div>
  </ng-template>
</p-dialog>

<!-- Channel type is not asked for: every WhatsApp sender registered here is CUSTOMER support.
     The model still sends that value, so the payload and the backend are unchanged. -->
<p-dialog [header]="editingConfig ? 'Edit Number' : 'Register New Number'" [(visible)]="showNumberDialog" [modal]="true" [style]="{ width: '560px' }" styleClass="p-fluid settings-dialog" contentStyleClass="vendor-dialog" (onHide)="editingConfig = null">
  <div class="vendor-form-body">
    <div class="mb-4">
      <label class="block font-medium mb-1">Phone Number (with code)</label>
      <input pInputText placeholder="+1234567890" [(ngModel)]="numberForm.phoneNumber" class="w-full" />
    </div>
    <div class="mb-4">
      <label class="block font-medium mb-1">Phone Number ID</label>
      <input pInputText placeholder="123456789012345" [(ngModel)]="numberForm.senderIdentifier" class="w-full" />
    </div>
    <div class="mb-4">
      <label class="block font-medium mb-1">WABA ID</label>
      <input pInputText placeholder="e.g. 102938475610293" [(ngModel)]="numberForm.wabaId" class="w-full" />
    </div>
    <div class="mb-4">
      <label class="block font-medium mb-1">Sender Label</label>
      <input pInputText placeholder="Support Dept" [(ngModel)]="numberForm.displayName" class="w-full" />
    </div>
    <div class="mb-4">
      <label class="block font-medium mb-1">Dedicated Journey</label>
      <p-select [filter]="true" filterPlaceholder="Search" appendTo="body" [options]="operations" optionLabel="name" optionValue="id"
                [(ngModel)]="numberForm.operationId" [showClear]="true" [loading]="loadingOperations"
                placeholder="Any journey (shared number)" class="w-full"></p-select>
      <small class="text-muted-color">Leave empty to share this number across all journeys.</small>
    </div>
    <div class="field-checkbox mb-2">
      <p-checkbox [(ngModel)]="numberForm.isDefault" binary="true" id="def"></p-checkbox>
      <label for="def" class="ml-2">Set as primary sending number</label>
    </div>
  </div>
  <ng-template pTemplate="footer">
    <div class="vendor-dialog-footer">
      <button pButton [label]="editingConfig ? 'Save Changes' : 'Register Number'" [icon]="editingConfig ? 'pi pi-check' : 'pi pi-plus'" (click)="saveNumber()"></button>
    </div>
  </ng-template>
</p-dialog>
